import * as PIXI from 'pixi.js';
import gsap from 'gsap';
import {
    COLORS,
    STARTING_GOLD,
    PROTECTION_SCROLLS,
    MAX_LEVEL,
    getWeaponByLevel,
    getEnhanceCost,
    getSellPrice,
    getEnhanceRate,
    getProtectionTierForLevel,
    weaponName,
    scrollName,
} from './constants';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { adService } from './ads';
import { initStorage, storage } from './persist';
import { initSafeArea, getSafeAreaInsets } from './utils/safeArea';
import {
    initI18n,
    t,
    getLocale,
    setLocale,
    getSupportedLocales,
    type Locale,
} from './i18n/index.js';
import {
    type Materials,
    type DropResult,
    MATERIAL_DEFS,
    emptyMaterials,
    calcDestroyDrop,
    applyDrop,
} from './materials.js';
import {
    type KeepInventoryState,
    type KeptWeapon,
    emptyKeepInventory,
    keepWeapon,
    retrieveWeapon,
    sellKeptWeapon,
    expandSlot,
    nextSlotCost,
    canKeep,
} from './keepInventory.js';
import {
    FORGE_RECIPES,
    canCraft,
    deductCost,
    getMissingMaterials,
    type ForgeCategory,
} from './forge.js';
import {
    type SacrificeState,
    emptySacrifice,
    sacrificeCap,
    sacrificeBonus,
    sacrificeBonusPer,
    sacrificeTargetIndices,
    addSacrifice,
    applyAdExtra,
    resetSacrifice,
    SACRIFICE_ABSOLUTE_CAP,
    SACRIFICE_AD_EXTRA,
} from './sacrifice.js';
import {
    type BuffItemsState,
    type BuffItemKey,
    emptyBuffItems,
    addBuffItem,
    useLuckCharm,
    useGuarantee,
    useDestroyGuard,
    consumeBuffOnEnhance,
    applyLuckCharmBonus,
    applyGuarantee,
} from './buffItems.js';

// === OTA Update (@capgo/capacitor-updater) ===
if (Capacitor.isNativePlatform()) {
    CapacitorUpdater.addListener('updateAvailable', () => {
        const overlay = document.createElement('div');
        overlay.id = 'ota-loading';
        overlay.innerHTML = '<div class="ota-spinner"></div>';
        document.body.appendChild(overlay);
    });
    CapacitorUpdater.addListener('downloadFailed', () => {
        document.getElementById('ota-loading')?.remove();
    });
}
CapacitorUpdater.notifyAppReady();

// 세이브 저장 레이어 초기화 — 네이티브는 Preferences 적재/마이그레이션이 끝나야 loadGame이 올바른 값을 읽음
await initStorage();

initI18n();

const haptic = (type: 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error') => {
    if (navigator.vibrate) {
        const duration = type === 'light' ? 10 : type === 'medium' ? 20 : type === 'success' ? 30 : 40;
        navigator.vibrate(duration);
    }
};

// Safe Area 초기화
initSafeArea();

// AdService 초기화
adService.initialize().catch((e) => {
    console.error('[AdService] Init failed:', e);
});

// --- BGM 관리 (Web Audio API) ---
let audioContext: AudioContext | null = null;
let audioBuffer: AudioBuffer | null = null;
let sourceNode: AudioBufferSourceNode | null = null;
let gainNode: GainNode | null = null;
let bgmLoaded = false;
let bgmPlaying = false;
let bgmShouldPlay = storage.get('swordEnhance_bgmEnabled', true);
let sfxEnabled = storage.get('swordEnhance_sfxEnabled', true);
let bgmStartTime = 0;
let bgmPauseTime = 0;

async function initBGM() {
    if (bgmLoaded) return;

    try {
        // AudioContext 생성 (사용자 상호작용 후에 resume 필요할 수 있음)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) {
            console.error('[BGM] Web Audio API not supported');
            return;
        }

        audioContext = new AudioContextClass();
        gainNode = audioContext.createGain();
        gainNode.gain.value = 0.5; // 50% 볼륨
        gainNode.connect(audioContext.destination);

        console.log('[BGM] AudioContext created, state:', audioContext.state);

        // 오디오 파일 로드
        const response = await fetch('./bgm.mp3');
        if (!response.ok) {
            throw new Error(`Failed to fetch bgm.mp3: ${response.status}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        bgmLoaded = true;
        console.log('[BGM] Audio loaded and decoded, duration:', audioBuffer.duration);
    } catch (e) {
        console.error('[BGM] Init error:', e);
    }
}

async function playBGM() {
    if (!bgmShouldPlay) return;

    // 초기화가 안 됐으면 초기화
    if (!bgmLoaded) {
        await initBGM();
    }

    if (!audioContext || !audioBuffer || !gainNode) {
        console.log('[BGM] Not ready to play');
        return;
    }

    // 이미 재생 중이면 무시
    if (bgmPlaying) return;

    try {
        // suspended 상태면 resume
        if (audioContext.state === 'suspended') {
            await audioContext.resume();
            console.log('[BGM] AudioContext resumed');
        }

        // 기존 소스 정리
        if (sourceNode) {
            try {
                sourceNode.stop();
            } catch { /* ignore */ }
            sourceNode.disconnect();
        }

        // 새 소스 노드 생성
        sourceNode = audioContext.createBufferSource();
        sourceNode.buffer = audioBuffer;
        sourceNode.loop = true;
        sourceNode.connect(gainNode);

        // 일시정지 위치에서 재개
        const offset = bgmPauseTime % audioBuffer.duration;
        sourceNode.start(0, offset);
        bgmStartTime = audioContext.currentTime - offset;
        bgmPlaying = true;

        console.log('[BGM] Playing from offset:', offset);
    } catch (e) {
        console.error('[BGM] Play error:', e);
    }
}

function pauseBGM() {
    if (!bgmPlaying || !sourceNode || !audioContext) return;

    try {
        // 현재 위치 저장
        bgmPauseTime = audioContext.currentTime - bgmStartTime;
        sourceNode.stop();
        sourceNode.disconnect();
        sourceNode = null;
        bgmPlaying = false;
        console.log('[BGM] Paused at:', bgmPauseTime);
    } catch (e) {
        console.error('[BGM] Pause error:', e);
    }
}

// 앱 백그라운드/포그라운드 전환 시 BGM 제어
document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
        pauseBGM();
    } else if (bgmShouldPlay) {
        playBGM();
    }
});

// 페이지 언로드 시 정리
window.addEventListener('pagehide', () => {
    pauseBGM();
});

// 사용자 상호작용 시 BGM 시작 (Web Audio API는 사용자 제스처 필요)
async function startBGMOnInteraction() {
    console.log('[BGM] User interaction detected');

    // AudioContext 초기화 및 resume
    if (!audioContext) {
        await initBGM();
    }

    if (audioContext && audioContext.state === 'suspended') {
        try {
            await audioContext.resume();
            console.log('[BGM] AudioContext resumed on interaction');
        } catch (e) {
            console.error('[BGM] Resume failed:', e);
        }
    }

    playBGM();
}

// 다양한 상호작용 이벤트에 리스너 등록
['touchstart', 'touchend', 'click', 'pointerdown'].forEach(eventType => {
    document.addEventListener(eventType, startBGMOnInteraction, { once: true, passive: true });
});

// 초기 로드 시작 (사용자 상호작용 전에 미리 로드)
initBGM();

// --- 효과음 시스템 (Web Audio API) ---
function getAudioContext(): AudioContext | null {
    if (!audioContext) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioContextClass) {
            audioContext = new AudioContextClass();
        }
    }
    if (audioContext?.state === 'suspended') {
        audioContext.resume();
    }
    return audioContext;
}

// 망치로 철 내려찍는 소리
function playHammerSound() {
    if (!sfxEnabled) return;
    if (hammerSound) {
        hammerSound.currentTime = 0;
        hammerSound.play().catch(() => {});
    }
}

// 강화 성공 사운드 (상승하는 차임)
function playSuccessSound() {
    if (!sfxEnabled) return;
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const frequencies = [523, 659, 784, 1047]; // C5, E5, G5, C6

    frequencies.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, now + i * 0.08);
        gain.gain.linearRampToValueAtTime(0.2, now + i * 0.08 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + 0.3);

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now + i * 0.08);
        osc.stop(now + i * 0.08 + 0.35);
    });
}

// 파괴 사운드
function playDestroySound() {
    if (!sfxEnabled) return;
    if (destroySound) {
        destroySound.currentTime = 0;
        destroySound.play().catch(() => {});
    }
}

// 보호 성공 사운드 (방패 소리)
function playProtectSound() {
    if (!sfxEnabled) return;
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(800, now);
    osc.frequency.exponentialRampToValueAtTime(400, now + 0.15);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.2);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.25);
}

// 효과음 (mp3 파일)
let cheerSound: HTMLAudioElement | null = null;
let disappointSound: HTMLAudioElement | null = null;
let hammerSound: HTMLAudioElement | null = null;
let destroySound: HTMLAudioElement | null = null;

function initSoundEffects() {
    cheerSound = new Audio('./cheer.mp3');
    disappointSound = new Audio('./disappoint.mp3');
    hammerSound = new Audio('./hammer.mp3');
    destroySound = new Audio('./destroy.mp3');
    cheerSound.volume = 0.7;
    disappointSound.volume = 0.7;
    hammerSound.volume = 0.8;
    destroySound.volume = 0.8;
}

function playCheerSound() {
    if (!sfxEnabled) return;
    if (cheerSound) {
        cheerSound.currentTime = 0;
        cheerSound.play().catch(() => {});
    }
}

function playDisappointSound() {
    if (!sfxEnabled) return;
    if (disappointSound) {
        disappointSound.currentTime = 0;
        disappointSound.play().catch(() => {});
    }
}

// 사운드 초기화
initSoundEffects();

// --- App 초기화 ---
const app = new PIXI.Application({
    width: window.innerWidth,
    height: window.innerHeight,
    backgroundColor: COLORS.background,
    resolution: 2,
    autoDensity: true,
});

const canvas = app.view as HTMLCanvasElement;
canvas.style.position = 'absolute';
canvas.style.top = '0';
canvas.style.left = '0';
canvas.style.width = '100%';
canvas.style.height = '100%';

const container = document.getElementById('game-container');
if (container) {
    container.appendChild(canvas);
}

// Resize 핸들러
function handleResize() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    app.renderer.resize(width, height);
}
window.addEventListener('resize', handleResize);
window.addEventListener('orientationchange', () => setTimeout(handleResize, 100));

// --- 게임 상수 ---
const SCREEN_WIDTH = window.innerWidth;
const SCREEN_HEIGHT = window.innerHeight;
const safeArea = getSafeAreaInsets();
const TOP_OFFSET = Math.max(safeArea.top, 20) + 10; // safe area + 여유 공간

// 상단 우측 유틸 배지 레이아웃 (보관/재료/설정) — 화면 폭에 따라 간격을 펼치는 반응형. 좁은 화면(375)에선 골드와 충돌 방지.
// 패널 우측 경계는 SW-15 (topPanel 박스). 기어/배지는 반드시 이 안쪽에 배치한다.
const TOPR_BADGE_W = 70;
const TOPR_GAP = Math.max(4, Math.min(34, Math.floor((SCREEN_WIDTH - 370) / 2)));
const TOPR_GEAR_CX = SCREEN_WIDTH - 40;                              // 설정 기어 중심 — 패널 우측 경계(SW-15) 안쪽 ~11px
const TOPR_MAT_X = (TOPR_GEAR_CX - 14) - TOPR_GAP - TOPR_BADGE_W;    // 재료 배지 좌측 x
const TOPR_KEEP_X = TOPR_MAT_X - TOPR_GAP - TOPR_BADGE_W;            // 보관 배지 좌측 x

// --- 리더보드 ---
interface LeaderboardEntry {
    name: string;
    rank: number;
    timestamp: number;
}

function getLeaderboard(): LeaderboardEntry[] {
    return storage.get('swordEnhance_leaderboard', []);
}

function addToLeaderboard(name: string): number {
    const leaderboard = getLeaderboard();
    const newRank = leaderboard.length + 1;
    leaderboard.push({
        name,
        rank: newRank,
        timestamp: Date.now(),
    });
    storage.set('swordEnhance_leaderboard', leaderboard);
    return newRank;
}

// --- 게임 상태 ---
let gold = STARTING_GOLD;
let protectionScrolls = [0, 0, 0, 0]; // [초급, 중급, 고급, 초고급] 방지권 개수
let weaponLevel = 1;
let useProtection = false;
let protectionTierUsed = -1; // 현재 사용 중인 방지권 티어 (해제 시 반환용, -1이면 미사용)
let adRestoreRemaining = 3; // 광고 복구 남은 횟수
let adBoostRemaining = 3; // 광고 확률 상승 남은 횟수
let adFreeEnhanceRemaining = 3; // 광고 무료 강화 남은 횟수
let freeEnhanceActive = false; // 무료 강화 활성화 여부
let rateBoostActive = false; // 광고 확률 상승 활성화 여부
let inventory: number[] = []; // 인벤토리 (저장된 무기 레벨들)
let shopTutorialShown = false; // 상점 튜토리얼 표시 여부

// Sprint 1: 재료 시스템
let materials: Materials = emptyMaterials();
// Sprint 1: 킵/무기 인벤토리
let keepInv: KeepInventoryState = emptyKeepInventory();
// Sprint 3: 제물 시스템
let sacrificeState: SacrificeState = emptySacrifice(1);
// Sprint 3: 버프 아이템
let buffItems: BuffItemsState = emptyBuffItems();
// Sprint 3: 인피니티 모드
let infinityActive = false;
let infinityEndTime = 0;
let infinityLastUsedTs = 0; // 마지막 사용 타임스탬프(ms), 0 = 미사용
const INFINITY_DURATION_MS = 10_000;
const INFINITY_COOLDOWN_MS = 120_000; // 2분 쿨타임

// 광고 확률 상승 계산
function getBoostedRate(baseRate: number): number {
    if (baseRate <= 0.30) {
        return Math.min(1, baseRate * 2); // 1-30%: 2배
    } else if (baseRate <= 0.50) {
        return Math.min(1, baseRate * 1.5); // 31-50%: 1.5배
    } else {
        return Math.min(1, baseRate * 1.3); // 51-99%: 1.3배
    }
}

// 저장/불러오기
function saveGame() {
    storage.set('swordEnhance_gold', gold);
    storage.set('swordEnhance_scrolls_v2', protectionScrolls); // v2: 배열 형태
    storage.set('swordEnhance_level', weaponLevel);
    storage.set('swordEnhance_inventory', inventory);
    // 광고 관련 상태 저장
    storage.set('swordEnhance_adRestore', adRestoreRemaining);
    storage.set('swordEnhance_adBoost', adBoostRemaining);
    storage.set('swordEnhance_adFreeEnhance', adFreeEnhanceRemaining);
    storage.set('swordEnhance_shopTutorial', shopTutorialShown);
    // Sprint 1: 재료 & 킵 인벤
    storage.set('swordEnhance_materials_v1', materials);
    storage.set('swordEnhance_keepInv_v1', keepInv);
    // Sprint 3: 제물/버프/인피니티
    storage.set('swordEnhance_sacrifice_v1', sacrificeState);
    storage.set('swordEnhance_buffItems_v1', buffItems);
    storage.set('swordEnhance_infinity_v2', { infinityLastUsedTs });
    // 장전 상태(방지권 활성/광고 보상) — 미저장 시 차감분만 남고 효과가 증발함
    storage.set('swordEnhance_pending_v1', {
        useProtection, protectionTierUsed, freeEnhanceActive, rateBoostActive,
    });
}

function loadGame() {
    gold = storage.get('swordEnhance_gold', STARTING_GOLD);
    // 새 배열 형태로 로드 (기본값: [0,0,0,0])
    const savedScrolls = storage.get('swordEnhance_scrolls_v2', [0, 0, 0, 0]);
    // 오염 세이브 방어: 길이 4의 숫자 배열로 정규화
    protectionScrolls = [0, 1, 2, 3].map((i) =>
        Array.isArray(savedScrolls) && typeof savedScrolls[i] === 'number' && savedScrolls[i] >= 0 ? savedScrolls[i] : 0
    );
    weaponLevel = storage.get('swordEnhance_level', 1);
    const savedInventory = storage.get<unknown[]>('swordEnhance_inventory', []);
    inventory = Array.isArray(savedInventory) ? savedInventory.filter((v): v is number => typeof v === 'number') : [];
    // 레벨 0은 더 이상 사용하지 않음 - 1로 업그레이드. 범위 밖 값은 클램프 (오염 시 PROTECTION_SCROLLS[-1] 크래시 방지)
    if (typeof weaponLevel !== 'number' || !Number.isFinite(weaponLevel) || weaponLevel < 1) weaponLevel = 1;
    if (weaponLevel > MAX_LEVEL) weaponLevel = MAX_LEVEL;
    // 광고 관련 상태 로드
    adRestoreRemaining = storage.get('swordEnhance_adRestore', 3);
    adBoostRemaining = storage.get('swordEnhance_adBoost', 3);
    adFreeEnhanceRemaining = storage.get('swordEnhance_adFreeEnhance', 3);
    shopTutorialShown = storage.get('swordEnhance_shopTutorial', false);
    // Sprint 1: 재료 — 기존 유저 세이브 호환 (필드 없으면 기본값)
    const savedMat = storage.get<Partial<Materials>>('swordEnhance_materials_v1', {});
    materials = {
        iron_dust: savedMat.iron_dust ?? 0,
        refining_core: savedMat.refining_core ?? 0,
        lava_essence: savedMat.lava_essence ?? 0,
        abyss_crystal: savedMat.abyss_crystal ?? 0,
    };
    // Sprint 1: 킵 인벤토리 — 기존 유저 세이브 호환
    const savedKeep = storage.get<Partial<KeepInventoryState>>('swordEnhance_keepInv_v1', {});
    keepInv = {
        slots: typeof savedKeep.slots === 'number' ? savedKeep.slots : 1,
        weapons: Array.isArray(savedKeep.weapons) ? (savedKeep.weapons as KeptWeapon[]) : [],
    };
    // Sprint 3: 제물 — 기존 유저 세이브 호환 (없으면 기본값)
    const savedSac = storage.get<Partial<SacrificeState>>('swordEnhance_sacrifice_v1', {});
    sacrificeState = {
        count: typeof savedSac.count === 'number' ? savedSac.count : 0,
        adExtra: typeof savedSac.adExtra === 'number' ? savedSac.adExtra : 0,
        forLevel: typeof savedSac.forLevel === 'number' ? savedSac.forLevel : weaponLevel,
    };
    // Sprint 3: 버프 아이템 — 기존 유저 세이브 호환
    const savedBuff = storage.get<Partial<BuffItemsState>>('swordEnhance_buffItems_v1', {});
    buffItems = {
        luck_charm: typeof savedBuff.luck_charm === 'number' ? savedBuff.luck_charm : 0,
        guarantee: typeof savedBuff.guarantee === 'number' ? savedBuff.guarantee : 0,
        destroy_guard: typeof savedBuff.destroy_guard === 'number' ? savedBuff.destroy_guard : 0,
        luckCharmActive: savedBuff.luckCharmActive === true,
        guaranteeActive: savedBuff.guaranteeActive === true,
        destroyGuardActive: savedBuff.destroyGuardActive === true,
    };
    // Sprint 3: 인피니티 모드 — v2(쿨타임 기반) 로드, v1 필드는 무시하고 안전 기본값 사용
    const savedInf = storage.get<{ infinityLastUsedTs?: number }>('swordEnhance_infinity_v2', {});
    infinityLastUsedTs = typeof savedInf.infinityLastUsedTs === 'number' ? savedInf.infinityLastUsedTs : 0;
    // 장전 상태 복원 (방지권 활성/광고 보상) — 티어 인덱스가 유효할 때만 방지권 복원
    const savedPending = storage.get<{
        useProtection?: boolean; protectionTierUsed?: number;
        freeEnhanceActive?: boolean; rateBoostActive?: boolean;
    }>('swordEnhance_pending_v1', {});
    const savedTier = typeof savedPending.protectionTierUsed === 'number' ? savedPending.protectionTierUsed : -1;
    if (savedPending.useProtection === true && savedTier >= 0 && savedTier < PROTECTION_SCROLLS.length) {
        useProtection = true;
        protectionTierUsed = savedTier;
    }
    freeEnhanceActive = savedPending.freeEnhanceActive === true;
    rateBoostActive = savedPending.rateBoostActive === true;
}
loadGame();

// --- 컨테이너 ---
const bgContainer = new PIXI.Container();
app.stage.addChild(bgContainer);

const gameContainer = new PIXI.Container();
app.stage.addChild(gameContainer);

const uiContainer = new PIXI.Container();
app.stage.addChild(uiContainer);

const effectContainer = new PIXI.Container();
app.stage.addChild(effectContainer);

// --- 배경 (대장간 화로 느낌) ---
const bgGradient = new PIXI.Graphics();
// 짙은 차콜 배경
bgGradient.beginFill(COLORS.background);
bgGradient.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
bgGradient.endFill();
// 하단부의 붉은 열기 (화로 느낌)
bgGradient.beginFill(COLORS.primary, 0.12);
bgGradient.drawEllipse(SCREEN_WIDTH / 2, SCREEN_HEIGHT, SCREEN_WIDTH * 0.9, 350);
bgGradient.endFill();
// 은은한 상단 글로우
bgGradient.beginFill(COLORS.panel, 0.3);
bgGradient.drawEllipse(SCREEN_WIDTH / 2, -100, SCREEN_WIDTH * 0.7, 300);
bgGradient.endFill();
bgContainer.addChild(bgGradient);

// 배경 파티클 (위로 튀는 불똥 효과)
const bgParticles: PIXI.Graphics[] = [];
for (let i = 0; i < 40; i++) {
    const particle = new PIXI.Graphics();
    // 불꽃 색상 (노랑 ~ 주황 랜덤)
    const color = Math.random() > 0.5 ? COLORS.gold : COLORS.primary;
    particle.beginFill(color, Math.random() * 0.5 + 0.3);
    particle.drawCircle(0, 0, Math.random() * 2.5 + 0.5);
    particle.endFill();
    particle.position.set(Math.random() * SCREEN_WIDTH, Math.random() * SCREEN_HEIGHT);
    bgContainer.addChild(particle);
    bgParticles.push(particle);
}

// --- UI 요소 ---

// 상단 패널 (골드, 방지권) - 대장간 금속 느낌
const topPanel = new PIXI.Graphics();
// 그림자
topPanel.beginFill(0x000000, 0.4);
topPanel.drawRoundedRect(17, TOP_OFFSET + 7, SCREEN_WIDTH - 30, 75, 16);
topPanel.endFill();
// 메인 패널
topPanel.beginFill(COLORS.panel, 0.9);
topPanel.drawRoundedRect(15, TOP_OFFSET + 5, SCREEN_WIDTH - 30, 75, 16);
topPanel.endFill();
// 금속 테두리
topPanel.lineStyle(2, COLORS.frameBorder, 0.6);
topPanel.drawRoundedRect(15, TOP_OFFSET + 5, SCREEN_WIDTH - 30, 75, 16);
uiContainer.addChild(topPanel);

// 골드 영역
const goldContainer = new PIXI.Container();
goldContainer.position.set(30, TOP_OFFSET + 20);
uiContainer.addChild(goldContainer);

const goldBg = new PIXI.Graphics();
goldBg.beginFill(0x1a1500, 0.7);
goldBg.drawRoundedRect(0, 0, 140, 50, 10);
goldBg.endFill();
goldBg.lineStyle(1, COLORS.gold, 0.4);
goldBg.drawRoundedRect(0, 0, 140, 50, 10);
goldContainer.addChild(goldBg);

const goldIcon = new PIXI.Text('💰', { fontSize: 24 });
goldIcon.position.set(12, 12);
goldContainer.addChild(goldIcon);

const goldText = new PIXI.Text('0', {
    fontFamily: 'Pretendard',
    fontSize: 20,
    fill: 0xffd700,
    fontWeight: 'bold',
});
goldText.position.set(45, 14);
goldContainer.addChild(goldText);

// 광고 골드 버튼 (상단 패널 아래에 배치)
const adGoldBtn = new PIXI.Container();
adGoldBtn.position.set(SCREEN_WIDTH / 2, TOP_OFFSET + 100);
adGoldBtn.eventMode = 'static';
adGoldBtn.cursor = 'pointer';
adGoldBtn.alpha = 0.7;
uiContainer.addChild(adGoldBtn);

const adGoldBtnGlow = new PIXI.Graphics();
adGoldBtn.addChild(adGoldBtnGlow);

const adGoldBtnBg = new PIXI.Graphics();
adGoldBtn.addChild(adGoldBtnBg);

const adGoldBtnText = new PIXI.Text(t('btn_free_enhance'), {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 100,
});
adGoldBtnText.anchor.set(0.5);
adGoldBtn.addChild(adGoldBtnText);

let isGoldAdAnimating = false;

function updateAdGoldButton() {
    adGoldBtnBg.clear();
    adGoldBtnGlow.clear();

    if (adFreeEnhanceRemaining <= 0) {
        // 소진
        adGoldBtnBg.beginFill(0x555555);
        adGoldBtnBg.drawRoundedRect(-55, -16, 110, 32, 16);
        adGoldBtnBg.endFill();
        adGoldBtnText.text = t('btn_exhausted');
        adGoldBtnText.style.fill = 0x888888;
        adGoldBtn.alpha = 0.4;
    } else {
        // 사용 가능
        adGoldBtnBg.beginFill(0x9b59b6);
        adGoldBtnBg.drawRoundedRect(-55, -16, 110, 32, 16);
        adGoldBtnBg.endFill();
        adGoldBtnBg.lineStyle(1, 0xaf7ac5, 0.8);
        adGoldBtnBg.drawRoundedRect(-55, -16, 110, 32, 16);
        adGoldBtnText.text = t('btn_free_enhance');
        adGoldBtnText.style.fill = 0xffffff;
        if (!isGoldAdAnimating) {
            adGoldBtn.alpha = 0.7;
        }
    }
}
updateAdGoldButton();

// 골드 부족 시 반짝임 애니메이션
function startGoldAdAttention() {
    if (adFreeEnhanceRemaining <= 0 || isGoldAdAnimating) return;

    isGoldAdAnimating = true;

    adGoldBtnGlow.beginFill(0x9b59b6, 0.6);
    adGoldBtnGlow.drawRoundedRect(-60, -21, 120, 42, 18);
    adGoldBtnGlow.endFill();

    gsap.to(adGoldBtn, {
        alpha: 1,
        duration: 0.3,
    });

    gsap.to(adGoldBtn, {
        y: TOP_OFFSET + 95,
        duration: 0.1,
        yoyo: true,
        repeat: 5,
        ease: 'power1.inOut',
        onComplete: () => {
            adGoldBtn.y = TOP_OFFSET + 100;
        }
    });

    gsap.to(adGoldBtnGlow, {
        alpha: 0,
        duration: 1.5,
        onComplete: () => {
            adGoldBtnGlow.clear();
            if (adFreeEnhanceRemaining > 0) {
                gsap.to(adGoldBtn, {
                    alpha: 0.7,
                    duration: 0.5,
                    onComplete: () => {
                        isGoldAdAnimating = false;
                    }
                });
            } else {
                isGoldAdAnimating = false;
            }
        }
    });
}

// --- 골드 광고 팝업 ---
const goldAdPopup = new PIXI.Container();
goldAdPopup.visible = false;
uiContainer.addChild(goldAdPopup);

const goldAdPopupBg = new PIXI.Graphics();
goldAdPopupBg.beginFill(0x000000, 0.85);
goldAdPopupBg.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
goldAdPopupBg.endFill();
goldAdPopupBg.eventMode = 'static';
goldAdPopup.addChild(goldAdPopupBg);

const goldAdPopupBox = new PIXI.Graphics();
goldAdPopupBox.beginFill(0x1a2a4a);
goldAdPopupBox.drawRoundedRect(-140, -140, 280, 280, 20);
goldAdPopupBox.endFill();
goldAdPopupBox.lineStyle(2, 0x4a6a9a, 1);
goldAdPopupBox.drawRoundedRect(-140, -140, 280, 280, 20);
goldAdPopupBox.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2);
goldAdPopup.addChild(goldAdPopupBox);

const goldAdPopupIcon = new PIXI.Text('⚔️', { fontSize: 40 });
goldAdPopupIcon.anchor.set(0.5);
goldAdPopupIcon.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 100);
goldAdPopup.addChild(goldAdPopupIcon);

const goldAdPopupTitle = new PIXI.Text(t('ad_gold_title'), {
    fontFamily: 'Pretendard',
    fontSize: 18,
    fill: 0xffffff,
    fontWeight: 'bold',
});
goldAdPopupTitle.anchor.set(0.5);
goldAdPopupTitle.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 50);
goldAdPopup.addChild(goldAdPopupTitle);

const goldAdPopupAmount = new PIXI.Text(t('ad_gold_desc'), {
    fontFamily: 'Pretendard',
    fontSize: 22,
    fill: 0x9b59b6,
    fontWeight: 'bold',
});
goldAdPopupAmount.anchor.set(0.5);
goldAdPopupAmount.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 15);
goldAdPopup.addChild(goldAdPopupAmount);

const goldAdPopupDesc = new PIXI.Text(t('ad_gold_note'), {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: 0xaaaaaa,
});
goldAdPopupDesc.anchor.set(0.5);
goldAdPopupDesc.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 15);
goldAdPopup.addChild(goldAdPopupDesc);

const goldAdRemainingText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: 0xaaaaaa,
});
goldAdRemainingText.anchor.set(0.5);
goldAdRemainingText.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 45);
goldAdPopup.addChild(goldAdRemainingText);

// 광고 시청 버튼
const goldAdConfirmBtn = new PIXI.Container();
goldAdConfirmBtn.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 90);
goldAdConfirmBtn.eventMode = 'static';
goldAdConfirmBtn.cursor = 'pointer';
goldAdPopup.addChild(goldAdConfirmBtn);

const goldAdConfirmBg = new PIXI.Graphics();
goldAdConfirmBg.beginFill(0x9b59b6);
goldAdConfirmBg.drawRoundedRect(-90, -20, 180, 40, 20);
goldAdConfirmBg.endFill();
goldAdConfirmBtn.addChild(goldAdConfirmBg);

const goldAdConfirmText = new PIXI.Text(t('ad_gold_confirm'), {
    fontFamily: 'Pretendard',
    fontSize: 15,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 170,
});
goldAdConfirmText.anchor.set(0.5);
goldAdConfirmBtn.addChild(goldAdConfirmText);

goldAdConfirmBtn.on('pointerdown', async () => {
    goldAdPopup.visible = false;
    const adResult = await adService.showRewardedAd();
    if (adResult) {
        adFreeEnhanceRemaining--;
        freeEnhanceActive = true;
        updateAdGoldButton();
        updateUI();
        haptic('success');
        showResult(t('ad_gold_free_next'), 0x9b59b6);
    }
});

// X 닫기 버튼
const goldAdCloseBtn = new PIXI.Container();
goldAdCloseBtn.position.set(SCREEN_WIDTH / 2 + 120, SCREEN_HEIGHT / 2 - 120);
goldAdCloseBtn.eventMode = 'static';
goldAdCloseBtn.cursor = 'pointer';
goldAdPopup.addChild(goldAdCloseBtn);

const goldAdCloseBg = new PIXI.Graphics();
goldAdCloseBg.beginFill(0x555555);
goldAdCloseBg.drawCircle(0, 0, 14);
goldAdCloseBg.endFill();
goldAdCloseBtn.addChild(goldAdCloseBg);

const goldAdCloseText = new PIXI.Text('✕', {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xffffff,
});
goldAdCloseText.anchor.set(0.5);
goldAdCloseBtn.addChild(goldAdCloseText);

goldAdCloseBtn.on('pointerdown', () => {
    goldAdPopup.visible = false;
});

function showGoldAdPopup() {
    goldAdRemainingText.text = t('ad_gold_remaining', { current: adFreeEnhanceRemaining });
    goldAdPopup.visible = true;
}

adGoldBtn.on('pointerdown', () => {
    if (freeEnhanceActive) {
        // 이미 장전된 상태에서 또 보면 횟수만 차감되고 효과는 중복되지 않음 — 헛소모 방지
        showResult(t('ad_gold_free_next'), 0x9b59b6);
        return;
    }
    if (adFreeEnhanceRemaining <= 0) {
        showResult(t('ad_gold_exhausted'), 0xe74c3c);
        return;
    }
    showGoldAdPopup();
});

// 설정 버튼 (톱니바퀴)
const settingsBtn = new PIXI.Container();
settingsBtn.position.set(TOPR_GEAR_CX, TOP_OFFSET + 37);
settingsBtn.eventMode = 'static';
settingsBtn.cursor = 'pointer';
settingsBtn.alpha = 0.5;
uiContainer.addChild(settingsBtn);

const settingsIcon = new PIXI.Text('⚙️', { fontSize: 20 });
settingsIcon.anchor.set(0.5);
settingsBtn.addChild(settingsIcon);

// 메뉴 팝업 (설정 + 인벤토리 탭)
const menuPopup = new PIXI.Container();
menuPopup.visible = false;
uiContainer.addChild(menuPopup);

const menuPopupBg = new PIXI.Graphics();
menuPopupBg.beginFill(0x000000, 0.8);
menuPopupBg.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
menuPopupBg.endFill();
menuPopupBg.eventMode = 'static';
menuPopup.addChild(menuPopupBg);

const menuBox = new PIXI.Graphics();
menuBox.beginFill(0x1a2a4a);
menuBox.drawRoundedRect(-150, -200, 300, 400, 16);
menuBox.endFill();
menuBox.lineStyle(2, 0x3a5a8a);
menuBox.drawRoundedRect(-150, -200, 300, 400, 16);
menuBox.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2);
menuPopup.addChild(menuBox);

// 탭 버튼들
let currentTab = 'settings'; // 'settings' | 'inventory'

const tabSettingsBtn = new PIXI.Container();
tabSettingsBtn.position.set(SCREEN_WIDTH / 2 - 70, SCREEN_HEIGHT / 2 - 170);
tabSettingsBtn.eventMode = 'static';
tabSettingsBtn.cursor = 'pointer';
menuPopup.addChild(tabSettingsBtn);

const tabSettingsBg = new PIXI.Graphics();
tabSettingsBtn.addChild(tabSettingsBg);

const tabSettingsText = new PIXI.Text(t('tab_settings'), {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 112,
});
tabSettingsText.anchor.set(0.5);
tabSettingsBtn.addChild(tabSettingsText);

const tabInventoryBtn = new PIXI.Container();
tabInventoryBtn.position.set(SCREEN_WIDTH / 2 + 70, SCREEN_HEIGHT / 2 - 170);
tabInventoryBtn.eventMode = 'static';
tabInventoryBtn.cursor = 'pointer';
menuPopup.addChild(tabInventoryBtn);

const tabInventoryBg = new PIXI.Graphics();
tabInventoryBtn.addChild(tabInventoryBg);

const tabInventoryText = new PIXI.Text(t('tab_inventory'), {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: 0xaaaaaa,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 112,
});
tabInventoryText.anchor.set(0.5);
tabInventoryBtn.addChild(tabInventoryText);

// 설정 탭 내용
const settingsContent = new PIXI.Container();
settingsContent.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2);
menuPopup.addChild(settingsContent);

const resetBtn = new PIXI.Container();
resetBtn.position.set(0, 130);
resetBtn.eventMode = 'static';
resetBtn.cursor = 'pointer';
settingsContent.addChild(resetBtn);

const resetBtnBg = new PIXI.Graphics();
resetBtnBg.beginFill(0xe74c3c);
resetBtnBg.drawRoundedRect(-110, -22, 220, 44, 12);
resetBtnBg.endFill();
resetBtn.addChild(resetBtnBg);

const resetBtnText = new PIXI.Text(t('reset_start_over'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xffffff,
    fontWeight: 'bold',
});
resetBtnText.anchor.set(0.5);
resetBtn.addChild(resetBtnText);

// BGM 토글 버튼
const bgmToggleBtn = new PIXI.Container();
bgmToggleBtn.position.set(0, -50);
bgmToggleBtn.eventMode = 'static';
bgmToggleBtn.cursor = 'pointer';
settingsContent.addChild(bgmToggleBtn);

const bgmToggleBg = new PIXI.Graphics();
bgmToggleBtn.addChild(bgmToggleBg);

const bgmToggleText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xffffff,
    fontWeight: 'bold',
});
bgmToggleText.anchor.set(0.5);
bgmToggleBtn.addChild(bgmToggleText);

function updateBgmToggle() {
    bgmToggleBg.clear();
    if (bgmShouldPlay) {
        bgmToggleBg.beginFill(0x27ae60);
        bgmToggleText.text = t('bgm_on');
    } else {
        bgmToggleBg.beginFill(0x555555);
        bgmToggleText.text = t('bgm_off');
    }
    bgmToggleBg.drawRoundedRect(-110, -18, 220, 36, 10);
    bgmToggleBg.endFill();
}
updateBgmToggle();

bgmToggleBtn.on('pointerdown', () => {
    bgmShouldPlay = !bgmShouldPlay;
    storage.set('swordEnhance_bgmEnabled', bgmShouldPlay);
    updateBgmToggle();
    if (bgmShouldPlay) {
        playBGM();
    } else {
        pauseBGM();
    }
    haptic('light');
});

// SFX 토글 버튼
const sfxToggleBtn = new PIXI.Container();
sfxToggleBtn.position.set(0, 10);
sfxToggleBtn.eventMode = 'static';
sfxToggleBtn.cursor = 'pointer';
settingsContent.addChild(sfxToggleBtn);

const sfxToggleBg = new PIXI.Graphics();
sfxToggleBtn.addChild(sfxToggleBg);

const sfxToggleText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xffffff,
    fontWeight: 'bold',
});
sfxToggleText.anchor.set(0.5);
sfxToggleBtn.addChild(sfxToggleText);

function updateSfxToggle() {
    sfxToggleBg.clear();
    if (sfxEnabled) {
        sfxToggleBg.beginFill(0x27ae60);
        sfxToggleText.text = t('sfx_on');
    } else {
        sfxToggleBg.beginFill(0x555555);
        sfxToggleText.text = t('sfx_off');
    }
    sfxToggleBg.drawRoundedRect(-110, -18, 220, 36, 10);
    sfxToggleBg.endFill();
}
updateSfxToggle();

sfxToggleBtn.on('pointerdown', () => {
    sfxEnabled = !sfxEnabled;
    storage.set('swordEnhance_sfxEnabled', sfxEnabled);
    updateSfxToggle();
    haptic('light');
});

// 언어 선택 버튼
const languageBtn = new PIXI.Container();
languageBtn.position.set(0, 70);
languageBtn.eventMode = 'static';
languageBtn.cursor = 'pointer';
settingsContent.addChild(languageBtn);

const languageBtnBg = new PIXI.Graphics();
languageBtnBg.beginFill(0x3498db);
languageBtnBg.drawRoundedRect(-110, -18, 220, 36, 10);
languageBtnBg.endFill();
languageBtn.addChild(languageBtnBg);

const languageBtnText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xffffff,
    fontWeight: 'bold',
});
languageBtnText.anchor.set(0.5);
languageBtn.addChild(languageBtnText);

function updateLanguageBtnText() {
    const current = getSupportedLocales().find((l) => l.code === getLocale());
    languageBtnText.text = `🌐 ${t('language')}: ${current?.name ?? 'English'}`;
}
updateLanguageBtnText();

// 언어 선택 팝업
const languagePopup = new PIXI.Container();
languagePopup.visible = false;
app.stage.addChild(languagePopup);

const langPopupOverlay = new PIXI.Graphics();
langPopupOverlay.beginFill(0x000000, 0.7);
langPopupOverlay.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
langPopupOverlay.endFill();
langPopupOverlay.eventMode = 'static';
languagePopup.addChild(langPopupOverlay);

const langPopupPanel = new PIXI.Graphics();
langPopupPanel.beginFill(0x2c3e50);
langPopupPanel.lineStyle(2, 0x4ecdc4);
langPopupPanel.drawRoundedRect(-160, -220, 320, 440, 15);
langPopupPanel.endFill();
langPopupPanel.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2);
languagePopup.addChild(langPopupPanel);

const langPopupTitle = new PIXI.Text(t('language'), {
    fontFamily: 'Pretendard',
    fontSize: 18,
    fill: 0xffffff,
    fontWeight: 'bold',
});
langPopupTitle.anchor.set(0.5);
langPopupTitle.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 190);
languagePopup.addChild(langPopupTitle);

getSupportedLocales().forEach((loc, idx) => {
    const col = idx % 2;
    const row = Math.floor(idx / 2);
    const localeBtn = new PIXI.Container();
    localeBtn.position.set(SCREEN_WIDTH / 2 - 70 + col * 140, SCREEN_HEIGHT / 2 - 140 + row * 60);
    localeBtn.eventMode = 'static';
    localeBtn.cursor = 'pointer';

    const localeBtnBg = new PIXI.Graphics();
    const isActive = loc.code === getLocale();
    localeBtnBg.beginFill(isActive ? 0x27ae60 : 0x34495e);
    localeBtnBg.lineStyle(2, isActive ? 0xf1c40f : 0x4ecdc4);
    localeBtnBg.drawRoundedRect(-60, -22, 120, 44, 10);
    localeBtnBg.endFill();
    localeBtn.addChild(localeBtnBg);

    const localeBtnText = new PIXI.Text(loc.name, {
        fontFamily: 'Pretendard',
        fontSize: 13,
        fill: 0xffffff,
        fontWeight: 'bold',
    });
    localeBtnText.anchor.set(0.5);
    localeBtn.addChild(localeBtnText);

    localeBtn.on('pointerdown', () => {
        if (loc.code !== getLocale()) {
            setLocale(loc.code as Locale);
            window.location.reload();
        } else {
            languagePopup.visible = false;
        }
    });
    languagePopup.addChild(localeBtn);
});

const langCloseBtn = new PIXI.Container();
langCloseBtn.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 180);
langCloseBtn.eventMode = 'static';
langCloseBtn.cursor = 'pointer';
languagePopup.addChild(langCloseBtn);

const langCloseBg = new PIXI.Graphics();
langCloseBg.beginFill(0x555555);
langCloseBg.drawRoundedRect(-60, -20, 120, 40, 10);
langCloseBg.endFill();
langCloseBtn.addChild(langCloseBg);

const langCloseText = new PIXI.Text(t('close'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xffffff,
    fontWeight: 'bold',
});
langCloseText.anchor.set(0.5);
langCloseBtn.addChild(langCloseText);

langCloseBtn.on('pointerdown', () => { languagePopup.visible = false; });
langPopupOverlay.on('pointerdown', () => { languagePopup.visible = false; });

languageBtn.on('pointerdown', () => {
    languagePopup.visible = true;
    haptic('light');
});

// 인벤토리 탭 내용
const inventoryContent = new PIXI.Container();
inventoryContent.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 100);
inventoryContent.visible = false;
menuPopup.addChild(inventoryContent);

// 방지권 보유 현황
const scrollsTitle = new PIXI.Text(t('inv_scrolls_title'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0x4ecdc4,
    fontWeight: 'bold',
});
scrollsTitle.anchor.set(0.5);
scrollsTitle.position.set(0, 0);
inventoryContent.addChild(scrollsTitle);

const scrollsInfoText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: 0xaaaaaa,
    align: 'center',
});
scrollsInfoText.anchor.set(0.5);
scrollsInfoText.position.set(0, 30);
inventoryContent.addChild(scrollsInfoText);

function updateScrollsInfo() {
    const line1 = t('inv_scrolls_line1', { c0: protectionScrolls[0], c1: protectionScrolls[1] });
    const line2 = t('inv_scrolls_line2', { c2: protectionScrolls[2], c3: protectionScrolls[3] });
    scrollsInfoText.text = `${line1}\n${line2}`;
}

// 무기 인벤토리 구분선
const invDivider = new PIXI.Graphics();
invDivider.lineStyle(1, 0x444444);
invDivider.moveTo(-130, 75);
invDivider.lineTo(130, 75);
inventoryContent.addChild(invDivider);

const weaponsTitle = new PIXI.Text(t('inventory_title'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: COLORS.gold,
    fontWeight: 'bold',
});
weaponsTitle.anchor.set(0.5);
weaponsTitle.position.set(0, 100);
inventoryContent.addChild(weaponsTitle);

const inventoryEmptyText = new PIXI.Text(t('inventory_empty'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0x888888,
});
inventoryEmptyText.anchor.set(0.5);
inventoryEmptyText.position.set(0, 140);
inventoryContent.addChild(inventoryEmptyText);

const inventoryItemsContainer = new PIXI.Container();
inventoryItemsContainer.position.set(0, 130);
inventoryContent.addChild(inventoryItemsContainer);

// 페이지네이션 — 4번째 항목부터 닫기 버튼/박스 밖으로 넘치던 오버플로 해결 (박스 안에 3개까지 안전)
const INV_PAGE_SIZE = 3;
let invPage = 0;

function makeInvPageBtn(dir: -1 | 1): PIXI.Container {
    const btn = new PIXI.Container();
    btn.position.set(dir * 110, 100);
    btn.eventMode = 'static';
    btn.cursor = 'pointer';
    const bg = new PIXI.Graphics();
    bg.beginFill(0x2a4a6a);
    bg.drawRoundedRect(-16, -14, 32, 28, 6);
    bg.endFill();
    btn.addChild(bg);
    const arrow = new PIXI.Text(dir < 0 ? '<' : '>', {
        fontFamily: 'Pretendard', fontSize: 15, fill: 0xffffff, fontWeight: 'bold',
    });
    arrow.anchor.set(0.5);
    btn.addChild(arrow);
    btn.on('pointerdown', () => {
        invPage += dir;
        updateInventoryUI();
        haptic('light');
    });
    inventoryContent.addChild(btn);
    return btn;
}
const invPrevBtn = makeInvPageBtn(-1);
const invNextBtn = makeInvPageBtn(1);

function updateInventoryUI() {
    // 방지권 정보 업데이트
    updateScrollsInfo();

    // 기존 아이템 버튼들 제거
    inventoryItemsContainer.removeChildren();

    const totalPages = Math.max(1, Math.ceil(inventory.length / INV_PAGE_SIZE));
    invPage = Math.min(Math.max(0, invPage), totalPages - 1);
    invPrevBtn.visible = invPage > 0;
    invNextBtn.visible = invPage < totalPages - 1;
    weaponsTitle.text = totalPages > 1
        ? `${t('inventory_title')} (${invPage + 1}/${totalPages})`
        : t('inventory_title');

    if (inventory.length === 0) {
        inventoryEmptyText.visible = true;
        return;
    }

    inventoryEmptyText.visible = false;

    const pageItems = inventory.slice(invPage * INV_PAGE_SIZE, invPage * INV_PAGE_SIZE + INV_PAGE_SIZE);
    pageItems.forEach((level, pageIndex) => {
        const index = invPage * INV_PAGE_SIZE + pageIndex; // 전역 인덱스 (splice용)
        const weapon = getWeaponByLevel(level);
        const itemBtn = new PIXI.Container();
        itemBtn.position.set(0, pageIndex * 50);
        itemBtn.eventMode = 'static';
        itemBtn.cursor = 'pointer';

        const itemBg = new PIXI.Graphics();
        itemBg.beginFill(0x2a4a6a);
        itemBg.drawRoundedRect(-130, -20, 260, 40, 8);
        itemBg.endFill();
        itemBtn.addChild(itemBg);

        const itemText = new PIXI.Text(`⚔️ ${t('inv_weapon_entry', { name: weaponName(weapon), level })}`, {
            fontFamily: 'Pretendard',
            fontSize: 14,
            fill: getWeaponColor(level),
            fontWeight: 'bold',
        });
        itemText.anchor.set(0, 0.5);
        itemText.position.set(-120, 0);
        itemBtn.addChild(itemText);

        const useText = new PIXI.Text(t('equip'), {
            fontFamily: 'Pretendard',
            fontSize: 12,
            fill: 0x2ecc71,
            fontWeight: 'bold',
        });
        useText.anchor.set(1, 0.5);
        useText.position.set(120, 0);
        itemBtn.addChild(useText);

        itemBtn.on('pointerdown', () => {
            // 현재 무기를 인벤토리에 저장 (1강은 저장 안함)
            if (weaponLevel > 1) {
                inventory.push(weaponLevel);
            }
            // 선택한 무기 장착 — 장전된 방지권 반환 + 제물 보너스 리셋 (레벨 이월 방지)
            releaseActiveProtection();
            weaponLevel = level;
            sacrificeState = resetSacrifice(level);
            updateSacrificeBonus();
            updateSacrificeBtn();
            // 인벤토리에서 제거
            inventory.splice(index, 1);

            updateInventoryUI();
            updateUI();
            saveGame();
            haptic('success');
            showResult(t('equip_msg', { name: weaponName(weapon) }), getWeaponColor(level));
        });

        inventoryItemsContainer.addChild(itemBtn);
    });
}

function updateTabUI() {
    tabSettingsBg.clear();
    tabInventoryBg.clear();

    if (currentTab === 'settings') {
        tabSettingsBg.beginFill(0x3a5a8a);
        tabSettingsBg.drawRoundedRect(-60, -18, 120, 36, 8);
        tabSettingsBg.endFill();
        tabSettingsText.style.fill = 0xffffff;

        tabInventoryBg.beginFill(0x1a2a3a);
        tabInventoryBg.drawRoundedRect(-60, -18, 120, 36, 8);
        tabInventoryBg.endFill();
        tabInventoryText.style.fill = 0x888888;

        settingsContent.visible = true;
        inventoryContent.visible = false;
    } else {
        tabSettingsBg.beginFill(0x1a2a3a);
        tabSettingsBg.drawRoundedRect(-60, -18, 120, 36, 8);
        tabSettingsBg.endFill();
        tabSettingsText.style.fill = 0x888888;

        tabInventoryBg.beginFill(0x3a5a8a);
        tabInventoryBg.drawRoundedRect(-60, -18, 120, 36, 8);
        tabInventoryBg.endFill();
        tabInventoryText.style.fill = 0xffffff;

        settingsContent.visible = false;
        inventoryContent.visible = true;
        updateInventoryUI();
    }
}
updateTabUI();

tabSettingsBtn.on('pointerdown', () => {
    currentTab = 'settings';
    updateTabUI();
});

tabInventoryBtn.on('pointerdown', () => {
    currentTab = 'inventory';
    updateTabUI();
});

// 초기화 확인 팝업 (uiContainer에 직접 추가)
const resetConfirmPopup = new PIXI.Container();
resetConfirmPopup.visible = false;
uiContainer.addChild(resetConfirmPopup);

const resetConfirmBg = new PIXI.Graphics();
resetConfirmBg.beginFill(0x000000, 0.9);
resetConfirmBg.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
resetConfirmBg.endFill();
resetConfirmBg.eventMode = 'static';
resetConfirmPopup.addChild(resetConfirmBg);

const resetConfirmBox = new PIXI.Graphics();
resetConfirmBox.beginFill(0x2a1a1a);
resetConfirmBox.drawRoundedRect(-130, -80, 260, 160, 16);
resetConfirmBox.endFill();
resetConfirmBox.lineStyle(2, 0xe74c3c);
resetConfirmBox.drawRoundedRect(-130, -80, 260, 160, 16);
resetConfirmBox.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2);
resetConfirmPopup.addChild(resetConfirmBox);

const resetConfirmTitle = new PIXI.Text(t('reset_confirm_title'), {
    fontFamily: 'Pretendard',
    fontSize: 16,
    fill: 0xe74c3c,
    fontWeight: 'bold',
});
resetConfirmTitle.anchor.set(0.5);
resetConfirmTitle.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 50);
resetConfirmPopup.addChild(resetConfirmTitle);

const resetConfirmDesc = new PIXI.Text(t('reset_confirm_desc'), {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: 0xaaaaaa,
});
resetConfirmDesc.anchor.set(0.5);
resetConfirmDesc.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 20);
resetConfirmPopup.addChild(resetConfirmDesc);

const resetConfirmYes = new PIXI.Container();
resetConfirmYes.position.set(SCREEN_WIDTH / 2 - 55, SCREEN_HEIGHT / 2 + 30);
resetConfirmYes.eventMode = 'static';
resetConfirmYes.cursor = 'pointer';
resetConfirmPopup.addChild(resetConfirmYes);

const resetConfirmYesBg = new PIXI.Graphics();
resetConfirmYesBg.beginFill(0xe74c3c);
resetConfirmYesBg.drawRoundedRect(-45, -18, 90, 36, 8);
resetConfirmYesBg.endFill();
resetConfirmYes.addChild(resetConfirmYesBg);

const resetConfirmYesText = new PIXI.Text(t('reset'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xffffff,
    fontWeight: 'bold',
});
resetConfirmYesText.anchor.set(0.5);
resetConfirmYes.addChild(resetConfirmYesText);

const resetConfirmNo = new PIXI.Container();
resetConfirmNo.position.set(SCREEN_WIDTH / 2 + 55, SCREEN_HEIGHT / 2 + 30);
resetConfirmNo.eventMode = 'static';
resetConfirmNo.cursor = 'pointer';
resetConfirmPopup.addChild(resetConfirmNo);

const resetConfirmNoBg = new PIXI.Graphics();
resetConfirmNoBg.beginFill(0x555555);
resetConfirmNoBg.drawRoundedRect(-45, -18, 90, 36, 8);
resetConfirmNoBg.endFill();
resetConfirmNo.addChild(resetConfirmNoBg);

const resetConfirmNoText = new PIXI.Text(t('cancel'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xffffff,
    fontWeight: 'bold',
});
resetConfirmNoText.anchor.set(0.5);
resetConfirmNo.addChild(resetConfirmNoText);

resetBtn.on('pointerdown', () => {
    resetConfirmPopup.visible = true;
});

resetConfirmYes.on('pointerdown', () => {
    gold = STARTING_GOLD;
    protectionScrolls = [0, 0, 0, 0];
    protectionTierUsed = -1;
    weaponLevel = 1;
    useProtection = false;
    adRestoreRemaining = 3;
    adBoostRemaining = 3;
    adFreeEnhanceRemaining = 3;
    freeEnhanceActive = false;
    rateBoostActive = false;
    currentLoadingPath = null;
    inventory = [];
    // Sprint 1 리셋
    materials = emptyMaterials();
    keepInv = emptyKeepInventory();
    // Sprint 3 리셋
    sacrificeState = emptySacrifice(1);
    buffItems = emptyBuffItems();
    infinityActive = false;
    infinityEndTime = 0;
    infinityLastUsedTs = 0;

    updateProtectionBtn();
    updateBoostButton();
    updateAdGoldButton();
    updateUI();
    saveGame();

    resetConfirmPopup.visible = false;
    menuPopup.visible = false;
    haptic('medium');

    // 타이틀 화면으로 돌아가기
    showTitleScreen();
});

resetConfirmNo.on('pointerdown', () => {
    resetConfirmPopup.visible = false;
});

// 닫기 버튼
const menuCloseBtn = new PIXI.Container();
menuCloseBtn.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 170);
menuCloseBtn.eventMode = 'static';
menuCloseBtn.cursor = 'pointer';
menuPopup.addChild(menuCloseBtn);

const menuCloseBtnText = new PIXI.Text(t('menu_close'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0x888888,
});
menuCloseBtnText.anchor.set(0.5);
menuCloseBtn.addChild(menuCloseBtnText);

menuCloseBtn.on('pointerdown', () => {
    menuPopup.visible = false;
});

menuPopupBg.on('pointerdown', () => {
    menuPopup.visible = false;
});

settingsBtn.on('pointerdown', () => {
    currentTab = 'settings';
    updateTabUI();
    menuPopup.visible = true;
});

// 방지권 확인 팝업
const protectionPopup = new PIXI.Container();
protectionPopup.visible = false;
uiContainer.addChild(protectionPopup);

const protectionPopupBg = new PIXI.Graphics();
protectionPopupBg.beginFill(0x000000, 0.8);
protectionPopupBg.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
protectionPopupBg.endFill();
protectionPopupBg.eventMode = 'static';
protectionPopup.addChild(protectionPopupBg);

const protectionBox = new PIXI.Graphics();
protectionBox.beginFill(0x1a2a4a);
protectionBox.drawRoundedRect(-140, -100, 280, 200, 16);
protectionBox.endFill();
protectionBox.lineStyle(2, 0x4ecdc4);
protectionBox.drawRoundedRect(-140, -100, 280, 200, 16);
protectionBox.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2);
protectionPopup.addChild(protectionBox);

const protectionPopupTitle = new PIXI.Text(t('protection_popup_title'), {
    fontFamily: 'Pretendard',
    fontSize: 18,
    fill: 0x4ecdc4,
    fontWeight: 'bold',
});
protectionPopupTitle.anchor.set(0.5);
protectionPopupTitle.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 70);
protectionPopup.addChild(protectionPopupTitle);

const protectionPopupInfo = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xffffff,
    align: 'center',
});
protectionPopupInfo.anchor.set(0.5);
protectionPopupInfo.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 20);
protectionPopup.addChild(protectionPopupInfo);

// 사용 버튼
const protectionUseBtn = new PIXI.Container();
protectionUseBtn.position.set(SCREEN_WIDTH / 2 - 60, SCREEN_HEIGHT / 2 + 50);
protectionUseBtn.eventMode = 'static';
protectionUseBtn.cursor = 'pointer';
protectionPopup.addChild(protectionUseBtn);

const protectionUseBtnBg = new PIXI.Graphics();
protectionUseBtnBg.beginFill(0x2ecc71);
protectionUseBtnBg.drawRoundedRect(-50, -18, 100, 36, 18);
protectionUseBtnBg.endFill();
protectionUseBtn.addChild(protectionUseBtnBg);

const protectionUseBtnText = new PIXI.Text(t('protection_use'), {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 92,
});
protectionUseBtnText.anchor.set(0.5);
protectionUseBtn.addChild(protectionUseBtnText);

// 취소 버튼
const protectionCancelBtn = new PIXI.Container();
protectionCancelBtn.position.set(SCREEN_WIDTH / 2 + 60, SCREEN_HEIGHT / 2 + 50);
protectionCancelBtn.eventMode = 'static';
protectionCancelBtn.cursor = 'pointer';
protectionPopup.addChild(protectionCancelBtn);

const protectionCancelBtnBg = new PIXI.Graphics();
protectionCancelBtnBg.beginFill(0x555555);
protectionCancelBtnBg.drawRoundedRect(-50, -18, 100, 36, 18);
protectionCancelBtnBg.endFill();
protectionCancelBtn.addChild(protectionCancelBtnBg);

const protectionCancelBtnText = new PIXI.Text(t('protection_cancel'), {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 92,
});
protectionCancelBtnText.anchor.set(0.5);
protectionCancelBtn.addChild(protectionCancelBtnText);

function showProtectionPopup() {
    const tier = getProtectionTierForLevel(weaponLevel);
    const scroll = PROTECTION_SCROLLS[tier];
    const hasEnough = protectionScrolls[tier] >= 1;

    protectionPopupInfo.text = t('protection_popup_info', { level: weaponLevel, name: scrollName(scroll), count: protectionScrolls[tier] });

    if (hasEnough) {
        protectionUseBtnBg.clear();
        protectionUseBtnBg.beginFill(0x2ecc71);
        protectionUseBtnBg.drawRoundedRect(-50, -18, 100, 36, 18);
        protectionUseBtnBg.endFill();
        protectionUseBtn.alpha = 1;
    } else {
        protectionUseBtnBg.clear();
        protectionUseBtnBg.beginFill(0x555555);
        protectionUseBtnBg.drawRoundedRect(-50, -18, 100, 36, 18);
        protectionUseBtnBg.endFill();
        protectionUseBtn.alpha = 0.5;
    }

    protectionPopup.visible = true;
}

protectionUseBtn.on('pointerdown', () => {
    const tier = getProtectionTierForLevel(weaponLevel);
    if (protectionScrolls[tier] >= 1) {
        protectionScrolls[tier] -= 1;
        protectionTierUsed = tier; // 사용한 티어 저장
        useProtection = true;
        updateProtectionBtn();
        updateUI();
        protectionPopup.visible = false;
        haptic('medium');
        const scroll = PROTECTION_SCROLLS[tier];
        showResult(t('protection_activated', { name: scrollName(scroll) }), 0x2ecc71);
    }
});

protectionCancelBtn.on('pointerdown', () => {
    protectionPopup.visible = false;
});

protectionPopupBg.on('pointerdown', () => {
    protectionPopup.visible = false;
});

// --- 중앙: 무기 표시 영역 ---
const weaponArea = new PIXI.Container();
weaponArea.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 40);
gameContainer.addChild(weaponArea);

// 무기 배경 프레임 (메탈릭 전시대)
const weaponFrame = new PIXI.Graphics();
// 1. 배경 글로우 (뒤에서 은은하게)
weaponFrame.beginFill(0x000000, 0.5);
weaponFrame.drawRoundedRect(-130, -160, 260, 320, 28);
weaponFrame.endFill();
// 2. 메인 금속 플레이트
weaponFrame.beginFill(COLORS.panel, 0.95);
weaponFrame.drawRoundedRect(-125, -155, 250, 310, 24);
weaponFrame.endFill();
// 3. 금속 테두리 (두껍게)
weaponFrame.lineStyle(3, COLORS.frameBorder, 0.8);
weaponFrame.drawRoundedRect(-125, -155, 250, 310, 24);
// 4. 내부 음각 라인 (디테일)
weaponFrame.lineStyle(1, 0x000000, 0.3);
weaponFrame.drawRoundedRect(-115, -145, 230, 290, 18);
// 5. 모서리 나사 장식 (4귀퉁이)
const screwOffset = 105;
weaponFrame.lineStyle(0);
weaponFrame.beginFill(COLORS.frameBorder);
weaponFrame.drawCircle(-screwOffset, -135, 5);
weaponFrame.drawCircle(screwOffset, -135, 5);
weaponFrame.drawCircle(-screwOffset, 135, 5);
weaponFrame.drawCircle(screwOffset, 135, 5);
weaponFrame.endFill();
// 나사 중심 (작은 십자)
weaponFrame.beginFill(0x000000, 0.5);
weaponFrame.drawCircle(-screwOffset, -135, 2);
weaponFrame.drawCircle(screwOffset, -135, 2);
weaponFrame.drawCircle(-screwOffset, 135, 2);
weaponFrame.drawCircle(screwOffset, 135, 2);
weaponFrame.endFill();
weaponArea.addChild(weaponFrame);

// 무기 이미지 스프라이트
let weaponSprite: PIXI.Sprite | null = null;

// 현재 로딩 중인 이미지 경로 (중복 로드 방지)
let currentLoadingPath: string | null = null;

// 무기 이미지 로드 및 표시
async function loadWeaponImage(imagePath: string | null) {
    // 같은 이미지면 스킵
    if (currentLoadingPath === imagePath && weaponSprite) {
        return;
    }
    currentLoadingPath = imagePath;

    // 기존 스프라이트의 GSAP 애니메이션 정리 후 제거
    if (weaponSprite) {
        gsap.killTweensOf(weaponSprite);
        gsap.killTweensOf(weaponSprite.scale);
        weaponArea.removeChild(weaponSprite);
        weaponSprite.destroy();
        weaponSprite = null;
    }

    if (!imagePath) {
        return;
    }

    try {
        const texture = await PIXI.Assets.load(imagePath);

        // 로딩 중에 다른 이미지로 변경됐으면 무시
        if (currentLoadingPath !== imagePath) {
            return;
        }

        weaponSprite = new PIXI.Sprite(texture);
        weaponSprite.anchor.set(0.5);
        weaponSprite.position.set(0, -30);
        weaponSprite.rotation = 0; // 회전 초기화
        weaponSprite.alpha = 1; // 투명도 초기화

        // 이미지 크기 조절 (최대 180x180) - 텍스처 원본 크기 기준
        const maxSize = 180;
        const texWidth = texture.orig?.width || texture.width;
        const texHeight = texture.orig?.height || texture.height;
        const targetScale = Math.min(maxSize / texWidth, maxSize / texHeight);
        weaponSprite.scale.set(targetScale);

        weaponArea.addChild(weaponSprite);

        // 레벨 배지와 이름 텍스트를 앞으로
        weaponArea.addChild(levelBadge);
        weaponArea.addChild(levelText);
    } catch (e) {
        console.error(t('weapon_image_load_failed'), imagePath, e);
    }
}

// 무기 이름 (그림자 효과)
const weaponNameText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 26,
    fill: 0xffffff,
    fontWeight: 'bold',
    dropShadow: true,
    dropShadowColor: 0x000000,
    dropShadowDistance: 2,
    dropShadowBlur: 4,
});
weaponNameText.anchor.set(0.5);
weaponNameText.position.set(0, -125);
weaponArea.addChild(weaponNameText);

// 강화 레벨 배지
const levelBadge = new PIXI.Graphics();
levelBadge.beginFill(0x2a2a4a, 0.9);
levelBadge.drawRoundedRect(-50, 60, 100, 50, 12);
levelBadge.endFill();
levelBadge.lineStyle(2, 0x5a5a8a, 0.6);
levelBadge.drawRoundedRect(-50, 60, 100, 50, 12);
weaponArea.addChild(levelBadge);

const levelText = new PIXI.Text('Lv.0', {
    fontFamily: 'Pretendard',
    fontSize: 28,
    fill: 0xffffff,
    fontWeight: 'bold',
});
levelText.anchor.set(0.5);
levelText.position.set(0, 85);
weaponArea.addChild(levelText);

// 확률 표시 (프로그레스 바 스타일)
const rateContainer = new PIXI.Container();
rateContainer.position.set(0, 130);
weaponArea.addChild(rateContainer);

const rateBg = new PIXI.Graphics();
rateBg.beginFill(0x1a1a2a);
rateBg.drawRoundedRect(-90, -10, 180, 20, 10);
rateBg.endFill();
rateContainer.addChild(rateBg);

const rateBar = new PIXI.Graphics();
rateContainer.addChild(rateBar);

const rateText = new PIXI.Text('100%', {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xffffff,
    fontWeight: 'bold',
});
rateText.anchor.set(0.5);
rateText.position.set(0, 0);
rateContainer.addChild(rateText);

function updateRateBar(rate: number) {
    rateBar.clear();
    const width = 176 * rate;
    const color = rate > 0.5 ? 0x2ecc71 : rate > 0.2 ? 0xf39c12 : 0xe74c3c;
    rateBar.beginFill(color, 0.8);
    rateBar.drawRoundedRect(-88, -8, width, 16, 8);
    rateBar.endFill();
}


// 레벨 기반 무기 색상 (텍스트 스타일링용)
function getWeaponColor(level: number): number {
    if (level === 0) return 0x888888; // 맨손 - 회색
    if (level <= 5) return 0xaaaaaa; // 초반 - 은색
    if (level <= 10) return 0x6699ff; // 중반1 - 파란색
    if (level <= 15) return 0x66ff66; // 중반2 - 초록색
    if (level <= 20) return 0xffcc00; // 후반1 - 금색
    if (level <= 25) return 0xff66ff; // 후반2 - 보라색
    if (level <= 30) return 0xff6600; // 전설1 - 주황색
    return 0xff0000; // 전설2 - 빨간색
}

// --- 하단: 버튼 영역 ---
const buttonArea = new PIXI.Container();
buttonArea.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT - 170);
uiContainer.addChild(buttonArea);

// 방지권 사용 버튼 (강화 버튼 위에)
const protectionBtn = new PIXI.Container();
protectionBtn.position.set(0, -55);
protectionBtn.eventMode = 'static';
protectionBtn.cursor = 'pointer';
buttonArea.addChild(protectionBtn);

const protectionBtnBg = new PIXI.Graphics();
protectionBtn.addChild(protectionBtnBg);

const protectionBtnText = new PIXI.Text(t('btn_protection_use'), {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 130,
});
protectionBtnText.anchor.set(0.5);
protectionBtn.addChild(protectionBtnText);

function updateProtectionBtn() {
    protectionBtnBg.clear();
    if (useProtection) {
        // 활성화 상태 - 초록색
        protectionBtnBg.beginFill(0x2ecc71);
        protectionBtnBg.drawRoundedRect(-70, -15, 140, 30, 15);
        protectionBtnBg.endFill();
        protectionBtnText.text = t('btn_protection_active');
        protectionBtnText.style.fill = 0xffffff;
    } else {
        // 비활성화 상태 - 회색 테두리
        protectionBtnBg.lineStyle(2, 0x4ecdc4);
        protectionBtnBg.beginFill(0x1a2a3a, 0.8);
        protectionBtnBg.drawRoundedRect(-70, -15, 140, 30, 15);
        protectionBtnBg.endFill();
        protectionBtnText.text = t('btn_protection_use');
        protectionBtnText.style.fill = 0x4ecdc4;
    }
}
updateProtectionBtn();

protectionBtn.on('pointerdown', () => {
    if (useProtection) {
        // 이미 활성화 상태면 취소하고 방지권 반환
        if (protectionTierUsed >= 0) {
            protectionScrolls[protectionTierUsed] += 1;
            const scroll = PROTECTION_SCROLLS[protectionTierUsed];
            showResult(t('protection_returned', { name: scrollName(scroll) }), 0x888888);
        }
        protectionTierUsed = -1;
        useProtection = false;
        updateProtectionBtn();
        updateUI();
        saveGame();
        haptic('light');
    } else {
        // 팝업 표시
        showProtectionPopup();
    }
});

// 강화 버튼 (메인 - 달궈진 강철)
const enhanceBtn = new PIXI.Container();
enhanceBtn.position.set(0, 0);
enhanceBtn.eventMode = 'static';
enhanceBtn.cursor = 'pointer';
buttonArea.addChild(enhanceBtn);

// 버튼 글로우 효과 (불타는 느낌)
const enhanceBtnGlow = new PIXI.Graphics();
enhanceBtnGlow.beginFill(COLORS.glow, 0.25);
enhanceBtnGlow.drawRoundedRect(-95, -38, 190, 76, 38);
enhanceBtnGlow.endFill();
enhanceBtn.addChild(enhanceBtnGlow);

const enhanceBtnBg = new PIXI.Graphics();
// 버튼 그림자
enhanceBtnBg.beginFill(0x000000, 0.5);
enhanceBtnBg.drawRoundedRect(-85, -28, 170, 64, 32);
enhanceBtnBg.endFill();
// 메인 버튼 (불타는 주황색)
enhanceBtnBg.beginFill(COLORS.primary);
enhanceBtnBg.drawRoundedRect(-85, -32, 170, 64, 32);
enhanceBtnBg.endFill();
// 상단 하이라이트 (입체감)
enhanceBtnBg.beginFill(0xffffff, 0.15);
enhanceBtnBg.drawRoundedRect(-85, -32, 170, 28, { tl: 32, tr: 32, bl: 0, br: 0 } as any);
enhanceBtnBg.endFill();
// 테두리 (밝은 금색)
enhanceBtnBg.lineStyle(3, COLORS.gold, 0.9);
enhanceBtnBg.drawRoundedRect(-85, -32, 170, 64, 32);
enhanceBtn.addChild(enhanceBtnBg);

const enhanceBtnText = new PIXI.Text(t('btn_enhance'), {
    fontFamily: 'Black Han Sans, Pretendard, sans-serif',
    fontSize: 28,
    fill: 0xffffff,
    dropShadow: true,
    dropShadowColor: 0x000000,
    dropShadowDistance: 2,
    dropShadowBlur: 3,
});
enhanceBtnText.anchor.set(0.5);
enhanceBtn.addChild(enhanceBtnText);

// 강화 비용 표시
const enhanceCostBg = new PIXI.Graphics();
enhanceCostBg.beginFill(0x1a1a2a, 0.9);
enhanceCostBg.drawRoundedRect(-50, 38, 100, 28, 14);
enhanceCostBg.endFill();
buttonArea.addChild(enhanceCostBg);

const enhanceCostText = new PIXI.Text('50 G', {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xffd700,
    fontWeight: 'bold',
});
enhanceCostText.anchor.set(0.5);
enhanceCostText.position.set(0, 52);
buttonArea.addChild(enhanceCostText);

// 광고 확률 상승 버튼 (강화 버튼 옆에 작게)
const boostBtn = new PIXI.Container();
boostBtn.position.set(130, 0); // 강화 버튼 오른쪽 (더 떨어뜨림)
boostBtn.eventMode = 'static';
boostBtn.cursor = 'pointer';
boostBtn.alpha = 0.5; // 평소에는 반투명
buttonArea.addChild(boostBtn);

const boostBtnGlow = new PIXI.Graphics(); // 반짝임용 글로우
boostBtn.addChild(boostBtnGlow);

const boostBtnBg = new PIXI.Graphics();
boostBtn.addChild(boostBtnBg);

const boostBtnText = new PIXI.Text(t('btn_boost'), {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    lineHeight: 14,
});
boostBtnText.anchor.set(0.5);
boostBtn.addChild(boostBtnText);

// 유휴 시간 체크용
let lastEnhanceTime = Date.now();
let isBoostAnimating = false;

function updateBoostButton() {
    boostBtnBg.clear();
    boostBtnGlow.clear();

    if (rateBoostActive) {
        // 활성화 - 초록색
        boostBtnBg.beginFill(0x2ecc71);
        boostBtnBg.drawRoundedRect(-30, -25, 60, 50, 10);
        boostBtnBg.endFill();
        boostBtnText.text = t('btn_applying');
        boostBtnText.style.fill = 0xffffff;
        boostBtn.alpha = 1;
    } else if (adBoostRemaining <= 0) {
        // 횟수 소진 - 회색
        boostBtnBg.beginFill(0x555555);
        boostBtnBg.drawRoundedRect(-30, -25, 60, 50, 10);
        boostBtnBg.endFill();
        boostBtnText.text = t('btn_exhausted');
        boostBtnText.style.fill = 0x888888;
        boostBtn.alpha = 0.4;
    } else {
        // 사용 가능 - 보라색
        boostBtnBg.beginFill(0x8e44ad);
        boostBtnBg.drawRoundedRect(-30, -25, 60, 50, 10);
        boostBtnBg.endFill();
        boostBtnBg.lineStyle(1, 0xbb8fce, 0.8);
        boostBtnBg.drawRoundedRect(-30, -25, 60, 50, 10);
        boostBtnText.text = t('btn_boost');
        boostBtnText.style.fill = 0xffffff;
        // 애니메이션 중이 아니면 반투명
        if (!isBoostAnimating) {
            boostBtn.alpha = 0.5;
        }
    }
}
updateBoostButton();

// 4초 유휴 시 반짝임 애니메이션
function startBoostAttention() {
    if (rateBoostActive || isBoostAnimating || adBoostRemaining <= 0) return;

    isBoostAnimating = true;

    // 글로우 효과 그리기
    boostBtnGlow.beginFill(0xffd700, 0.5);
    boostBtnGlow.drawRoundedRect(-35, -30, 70, 60, 12);
    boostBtnGlow.endFill();

    // 반짝임 + 흔들림 애니메이션
    gsap.to(boostBtn, {
        alpha: 1,
        duration: 0.3,
    });

    gsap.to(boostBtn, {
        x: 135,
        duration: 0.1,
        yoyo: true,
        repeat: 5,
        ease: 'power1.inOut',
        onComplete: () => {
            boostBtn.x = 130;
        }
    });

    gsap.to(boostBtnGlow, {
        alpha: 0,
        duration: 1.5,
        onComplete: () => {
            boostBtnGlow.clear();
            // 다시 반투명으로
            if (!rateBoostActive) {
                gsap.to(boostBtn, {
                    alpha: 0.5,
                    duration: 0.5,
                    onComplete: () => {
                        isBoostAnimating = false;
                    }
                });
            } else {
                isBoostAnimating = false;
            }
        }
    });
}

// 강화 버튼 누를 때 시간 리셋
function resetEnhanceTimer() {
    lastEnhanceTime = Date.now();
}

// --- 확률 UP 팝업 ---
const boostPopup = new PIXI.Container();
boostPopup.visible = false;
uiContainer.addChild(boostPopup);

const boostPopupBg = new PIXI.Graphics();
boostPopupBg.beginFill(0x000000, 0.85);
boostPopupBg.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
boostPopupBg.endFill();
boostPopupBg.eventMode = 'static';
boostPopup.addChild(boostPopupBg);

const boostPopupBox = new PIXI.Graphics();
boostPopupBox.beginFill(0x1a2a4a);
boostPopupBox.drawRoundedRect(-150, -140, 300, 280, 20);
boostPopupBox.endFill();
boostPopupBox.lineStyle(2, 0x4a6a9a, 1);
boostPopupBox.drawRoundedRect(-150, -140, 300, 280, 20);
boostPopupBox.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2);
boostPopup.addChild(boostPopupBox);

const boostPopupTitle = new PIXI.Text(t('boost_popup_title'), {
    fontFamily: 'Pretendard',
    fontSize: 22,
    fill: 0xffffff,
    fontWeight: 'bold',
});
boostPopupTitle.anchor.set(0.5);
boostPopupTitle.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 105);
boostPopup.addChild(boostPopupTitle);

const boostPopupDesc = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 16,
    fill: 0xcccccc,
    align: 'center',
});
boostPopupDesc.anchor.set(0.5);
boostPopupDesc.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 65);
boostPopup.addChild(boostPopupDesc);

const boostPopupRate = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 36,
    fill: 0x2ecc71,
    fontWeight: 'bold',
});
boostPopupRate.anchor.set(0.5);
boostPopupRate.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 20);
boostPopup.addChild(boostPopupRate);

// 남은 횟수 표시
const boostRemainingText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: COLORS.gold,
});
boostRemainingText.anchor.set(0.5);
boostRemainingText.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 20);
boostPopup.addChild(boostRemainingText);

// 광고 시청 버튼
const boostConfirmBtn = new PIXI.Container();
boostConfirmBtn.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 65);
boostConfirmBtn.eventMode = 'static';
boostConfirmBtn.cursor = 'pointer';
boostPopup.addChild(boostConfirmBtn);

const boostConfirmBg = new PIXI.Graphics();
boostConfirmBg.beginFill(0x9b59b6);
boostConfirmBg.drawRoundedRect(-100, -22, 200, 44, 22);
boostConfirmBg.endFill();
boostConfirmBtn.addChild(boostConfirmBg);

const boostConfirmText = new PIXI.Text(t('boost_confirm'), {
    fontFamily: 'Pretendard',
    fontSize: 15,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 190,
});
boostConfirmText.anchor.set(0.5);
boostConfirmBtn.addChild(boostConfirmText);

boostConfirmBtn.on('pointerdown', async () => {
    boostPopup.visible = false;
    const adResult = await adService.showRewardedAd();
    if (adResult) {
        adBoostRemaining--; // 횟수 차감
        rateBoostActive = true;
        haptic('success');
        updateBoostButton();
        updateUI();
        showResult(t('boost_applied'), 0x2ecc71);
    }
});

// 닫기 버튼
const boostCloseBtn = new PIXI.Container();
boostCloseBtn.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 115);
boostCloseBtn.eventMode = 'static';
boostCloseBtn.cursor = 'pointer';
boostPopup.addChild(boostCloseBtn);

const boostCloseBg = new PIXI.Graphics();
boostCloseBg.beginFill(0x3a3a5a);
boostCloseBg.drawRoundedRect(-60, -18, 120, 36, 18);
boostCloseBg.endFill();
boostCloseBtn.addChild(boostCloseBg);

const boostCloseText = new PIXI.Text(t('boost_close'), {
    fontFamily: 'Pretendard',
    fontSize: 15,
    fill: 0xaaaaaa,
});
boostCloseText.anchor.set(0.5);
boostCloseBtn.addChild(boostCloseText);

boostCloseBtn.on('pointerdown', () => {
    boostPopup.visible = false;
});

function showBoostPopup() {
    const baseRate = getEnhanceRate(weaponLevel);
    const boostedRate = getBoostedRate(baseRate);

    let multiplier = '';
    if (baseRate <= 0.30) {
        multiplier = t('boost_mult_2x');
    } else if (baseRate <= 0.50) {
        multiplier = t('boost_mult_1_5x');
    } else {
        multiplier = t('boost_mult_1_3x');
    }

    boostPopupDesc.text = t('boost_popup_desc', { rate: (baseRate * 100).toFixed(1), mult: multiplier });
    boostPopupRate.text = `${(boostedRate * 100).toFixed(1)}%`;
    boostRemainingText.text = t('boost_remaining', { current: adBoostRemaining });
    boostPopup.visible = true;
}

boostBtn.on('pointerdown', () => {
    if (rateBoostActive) {
        showResult(t('boost_already_applied'), 0xf39c12);
        return;
    }
    if (adBoostRemaining <= 0) {
        showResult(t('boost_exhausted'), 0xe74c3c);
        return;
    }
    showBoostPopup();
});

// 판매 버튼 (하단 버튼 행: Keep / Sell / Shop)
const sellBtn = new PIXI.Container();
const btnWidth = 85;
const btnSpacing = 20;
// btnRowStartX는 keepBtn 정의 시에 정의됨
sellBtn.position.set(-147.5 + btnWidth + btnSpacing, 95); // 중앙 위치
sellBtn.eventMode = 'static';
sellBtn.cursor = 'pointer';
buttonArea.addChild(sellBtn);

const sellBtnBg = new PIXI.Graphics();
sellBtnBg.beginFill(0x3498db);
sellBtnBg.drawRoundedRect(-42.5, -22, 85, 44, 18);
sellBtnBg.endFill();
sellBtnBg.lineStyle(1, 0x5dade2, 0.6);
sellBtnBg.drawRoundedRect(-42.5, -22, 85, 44, 18);
sellBtn.addChild(sellBtnBg);

const sellBtnText = new PIXI.Text(t('btn_sell'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xffffff,
    fontWeight: 'bold',
});
sellBtnText.anchor.set(0.5);
sellBtn.addChild(sellBtnText);

// 상점 버튼 (하단 버튼 행: Keep / Sell / Shop)
const shopBtn = new PIXI.Container();
shopBtn.position.set(-147.5 + (btnWidth + btnSpacing) * 2, 95); // 우측 위치
shopBtn.eventMode = 'static';
shopBtn.cursor = 'pointer';
buttonArea.addChild(shopBtn);

const shopBtnBg = new PIXI.Graphics();
shopBtnBg.beginFill(0x9b59b6);
shopBtnBg.drawRoundedRect(-42.5, -22, 85, 44, 18);
shopBtnBg.endFill();
shopBtnBg.lineStyle(1, 0xbb8fce, 0.6);
shopBtnBg.drawRoundedRect(-42.5, -22, 85, 44, 18);
shopBtn.addChild(shopBtnBg);

const shopBtnText = new PIXI.Text(t('btn_shop'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xffffff,
    fontWeight: 'bold',
});
shopBtnText.anchor.set(0.5);
shopBtn.addChild(shopBtnText);

// --- 결과 표시 ---
const resultText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 36,
    fill: COLORS.success,
    fontWeight: 'bold',
});
resultText.anchor.set(0.5);
resultText.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 200);
resultText.visible = false;
effectContainer.addChild(resultText);

// --- UI 업데이트 함수 ---
function updateUI() {
    const weapon = getWeaponByLevel(weaponLevel);
    const baseRate = getEnhanceRate(weaponLevel);
    const displayRate = rateBoostActive ? getBoostedRate(baseRate) : baseRate;
    const cost = getEnhanceCost(weaponLevel);
    const sellPrice = getSellPrice(weaponLevel);

    // 텍스트 업데이트
    goldText.text = gold.toLocaleString();
    weaponNameText.text = weaponName(weapon);
    weaponNameText.style.fill = getWeaponColor(weaponLevel);
    levelText.text = `Lv.${weaponLevel}`;

    // 최대 레벨 도달 체크 (올 클리어)
    const isMaxLevel = weaponLevel >= MAX_LEVEL;

    if (isMaxLevel) {
        // 올 클리어 상태
        rateText.text = t('rate_clear');
        rateText.style.fill = 0xffd700; // 골드색
        updateRateBar(1); // 바 꽉 채움
        enhanceBtnText.text = t('btn_enhance_complete');
        enhanceCostText.text = 'MAX';
        enhanceCostText.style.fill = 0xffd700;
        enhanceBtnBg.alpha = 0.5;
        enhanceBtnGlow.alpha = 0.3;
    } else {
        // 일반 상태
        // 확률 바 업데이트 (부스트 시 다르게 표시)
        if (rateBoostActive) {
            rateText.text = t('rate_display_boosted', { rate: (displayRate * 100).toFixed(1) });
            rateText.style.fill = 0x2ecc71; // 초록색
        } else {
            rateText.text = `${(displayRate * 100).toFixed(1)}%`;
            rateText.style.fill = 0xffffff;
        }
        updateRateBar(displayRate);
        enhanceBtnText.text = t('btn_enhance_ready');

        // 무료 강화 활성화 시 0 G 표시
        if (freeEnhanceActive) {
            enhanceCostText.text = t('gold_display_free');
            enhanceCostText.style.fill = 0x9b59b6;
        } else {
            enhanceCostText.text = `${cost.toLocaleString()} G`;
            enhanceCostText.style.fill = COLORS.gold;
        }

        // 버튼 활성화 상태
        const canEnhance = freeEnhanceActive || gold >= cost;
        enhanceBtnBg.alpha = canEnhance ? 1 : 0.5;
        enhanceBtnGlow.alpha = canEnhance ? 1 : 0.3;
    }

    // 판매 버튼 - 2강 이상만 판매 가능
    sellBtnText.text = weaponLevel > 1 ? t('btn_sell_amount', { amount: sellPrice.toLocaleString() }) : t('btn_sell');
    sellBtnBg.alpha = weaponLevel > 1 ? 1 : 0.5;

    // 방지권 버튼 상태 업데이트
    updateProtectionBtn();

    // 무기 이미지 로드
    loadWeaponImage(weapon.image);

    // 부스트 버튼 업데이트 (배율 표시)
    updateBoostButton();

    // 저장
    saveGame();
}

// --- 강화 로직 ---
let lastDestroyedLevel = 0; // 파괴된 무기 레벨 저장

// 무기 교체(판매/장착/보관)로 레벨이 바뀔 때 장전된 방지권을 반환·해제
// (활성 티어가 새 레벨과 안 맞는 채 남으면 저티어 방지권으로 고강 보호되는 우회/헛소모 발생)
function releaseActiveProtection() {
    if (!useProtection) return;
    if (protectionTierUsed >= 0 && protectionTierUsed < PROTECTION_SCROLLS.length) {
        protectionScrolls[protectionTierUsed] += 1;
        showResult(t('protection_returned', { name: scrollName(PROTECTION_SCROLLS[protectionTierUsed]) }), 0x888888);
    }
    useProtection = false;
    protectionTierUsed = -1;
    updateProtectionBtn();
}

function enhance() {
    // 최대 레벨이면 강화 불가
    if (weaponLevel >= MAX_LEVEL) {
        showResult(t('msg_max_level'), COLORS.gold);
        return;
    }

    // 무기 교체 등으로 레벨이 바뀌었으면 제물 보너스 무효 (저레벨 제물의 고레벨 이월 방지)
    if (sacrificeState.forLevel !== weaponLevel) {
        sacrificeState = resetSacrifice(weaponLevel);
        updateSacrificeBonus();
        updateSacrificeBtn();
    }

    const cost = getEnhanceCost(weaponLevel);
    const baseRate = getEnhanceRate(weaponLevel);

    // Sprint 3: 제물 보너스 + 행운부적 보너스 + 부스트 적용 순서
    let rate = rateBoostActive ? getBoostedRate(baseRate) : baseRate;
    rate = applyLuckCharmBonus(rate, buffItems.luckCharmActive);
    const sacBonus = sacrificeBonus(sacrificeState, weaponLevel);
    rate = Math.min(0.99, rate + sacBonus);

    // Sprint 3: 보장강화권 활성 시 성공 강제
    const isGuaranteed = applyGuarantee(buffItems.guaranteeActive);

    // 강화 타이머 리셋
    resetEnhanceTimer();

    // 무료 강화: freeEnhanceActive(광고) 또는 infinityActive
    const isFreeEnhance = freeEnhanceActive || infinityActive;

    if (!isFreeEnhance && gold < cost) {
        showResult(t('msg_gold_short'), COLORS.fail);
        return;
    }

    if (!isFreeEnhance) {
        gold -= cost;
    } else if (freeEnhanceActive && !infinityActive) {
        // 일회성 무료 강화 사용됨 (인피니티 중엔 어차피 무료라 소모하지 않음)
        freeEnhanceActive = false;
        updateAdGoldButton();
    }
    // infinityActive: 골드 차감 없음 (타이머로 관리)

    // 부스트 사용 후 리셋
    const wasBoostActive = rateBoostActive;
    if (rateBoostActive) {
        rateBoostActive = false;
        updateBoostButton();
    }

    // 망치 소리 재생
    playHammerSound();

    // 확률 판정 - 즉시 결과 (보장강화권이면 무조건 성공)
    const roll = Math.random();
    const success = isGuaranteed || roll < rate;

    // 방지권 사용 여부 저장 (강화 후 리셋하기 위해)
    const hadProtection = useProtection;

    if (success) {
        // 성공!
        const prevLevel = weaponLevel;
        weaponLevel++;

        // Sprint 3: 성공 시 제물 보너스 리셋 (새 레벨로) + 표시 갱신 (잔존 +N% 오표시 방지)
        sacrificeState = resetSacrifice(weaponLevel);
        updateSacrificeBonus();
        updateSacrificeBtn();

        // Sprint 3: 버프 아이템 소모 처리
        const buffResult = consumeBuffOnEnhance(buffItems, true, false);
        buffItems = buffResult.newState;
        updateBuffSlots();

        // 성공 효과음
        setTimeout(() => playSuccessSound(), 150);

        if (prevLevel >= 15) {
            setTimeout(() => playCheerSound(), 200);
        }

        if (weaponLevel >= MAX_LEVEL) {
            showCelebration();
        } else {
            if (wasBoostActive) {
                showResult(t('msg_success_boost'), COLORS.success);
            } else if (isGuaranteed) {
                showResult(t('buff_guarantee_consumed'), COLORS.success);
            } else {
                showResult(t('msg_success'), COLORS.success);
            }
            createSuccessEffect();
        }
        haptic('success');
    } else {
        // 실패
        if (hadProtection) {
            // 기존 방지권(스크롤)으로 보호됨
            setTimeout(() => playProtectSound(), 150);
            showResult(t('protection_protected'), 0x4ecdc4);
            haptic('warning');

            // Sprint 3: 버프 소모 (파괴 없으므로 destroyGuard 미소모)
            const buffResult = consumeBuffOnEnhance(buffItems, false, false);
            buffItems = buffResult.newState;
            updateBuffSlots();
        } else {
            // Sprint 3: 파괴방지권 체크
            const hadDestroyGuard = buffItems.destroyGuardActive;

            // 재료 드랍 계산 (파괴)
            const destroyDrop = calcDestroyDrop(weaponLevel);
            const currentDestroyedLevel = weaponLevel;

            if (hadDestroyGuard) {
                // 파괴방지권 발동: 파괴 없음 (하락만)
                const buffResult = consumeBuffOnEnhance(buffItems, false, true);
                buffItems = buffResult.newState;
                updateBuffSlots();
                setTimeout(() => playProtectSound(), 150);
                showResult(t('buff_destroy_guard_consumed'), 0x4ecdc4);
                haptic('warning');
                // 제물 보너스는 유지 (파괴 없으므로 리셋 안함)
                updateUI();
                return;
            }

            // 파괴 처리
            if (destroyDrop) {
                materials = applyDrop(materials, destroyDrop);
            }

            setTimeout(() => playDestroySound(), 150);

            if (weaponLevel >= 15) {
                setTimeout(() => playDisappointSound(), 300);
            }

            lastDestroyedLevel = weaponLevel;
            weaponLevel = 1;
            haptic('error');
            createDestroyEffect();

            // Sprint 3: 파괴 시 제물 보너스 리셋
            sacrificeState = resetSacrifice(1);
            updateSacrificeBonus();

            // Sprint 3: 버프 아이템 소모 처리
            const buffResult2 = consumeBuffOnEnhance(buffItems, false, true);
            buffItems = buffResult2.newState;
            updateBuffSlots();

            // 파괴 = 사이클 종료
            adBoostRemaining = 3;
            adFreeEnhanceRemaining = 3;
            freeEnhanceActive = false;
            rateBoostActive = false;

            if (destroyDrop) {
                setTimeout(() => showDestroyedWithMatPopup(destroyDrop, currentDestroyedLevel), 800);
            }

            // 복구 가능 조건: 파괴 직전 레벨 2 이상 + 남은 횟수 있음
            // adRestoreRemaining은 새 파괴 사이클 시작 시 3으로 리셋
            if (adRestoreRemaining <= 0) {
                // 이전 사이클 횟수 소진 → 새 사이클 시작 시 리셋
                adRestoreRemaining = 3;
            }
            // lastDestroyedLevel >= 2이면 팝업에서 복구 버튼 표시
            saveGame();
            updateAdGoldButton();
            updateBoostButton();
            updateMaterialPanel();
        }
    }

    // 방지권 효과는 1회용 - 성공/실패 상관없이 해제
    if (hadProtection) {
        useProtection = false;
        protectionTierUsed = -1;
        updateProtectionBtn();
    }

    updateUI();
}

// --- 판매 ---
function sellWeapon() {
    if (weaponLevel <= 1) return; // 1강은 판매 불가

    const price = getSellPrice(weaponLevel);

    gold += price;
    releaseActiveProtection();
    weaponLevel = 1;
    sacrificeState = resetSacrifice(1);
    updateSacrificeBonus();
    updateSacrificeBtn();

    showResult(t('sell_received', { amount: price.toLocaleString() }), COLORS.gold);
    haptic('medium');
    updateUI();
}

// --- 상점 (탭 분리: Protection / Buy Weapons) ---
const shopPanel = new PIXI.Container();
shopPanel.visible = false;
uiContainer.addChild(shopPanel);

const shopBg = new PIXI.Graphics();
shopBg.beginFill(0x000000, 0.8);
shopBg.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
shopBg.endFill();
shopBg.eventMode = 'static'; // 배경 클릭 방지
shopPanel.addChild(shopBg);

// 반응형 상점 박스 폭: 화면 너비의 88%, 최소 300, 최대 500
const SHOP_BOX_W = Math.min(500, Math.max(300, Math.floor(SCREEN_WIDTH * 0.88)));
const SHOP_BOX_X = Math.floor((SCREEN_WIDTH - SHOP_BOX_W) / 2);
const SHOP_BOX_TOP = TOP_OFFSET + 30;
// 상점 박스 높이를 고정: 화면 높이 - 상단 오프셋 - 여유
const SHOP_BOX_H = Math.min(600, Math.max(360, SCREEN_HEIGHT - SHOP_BOX_TOP - 80)); // 캡 500→600: 용광로 무기 4행(Lv.10 2재료)이 Close 버튼과 겹치던 문제 해소
// 버튼 내부 좌우 패딩 (버튼 박스 기준)
const SHOP_BTN_HALF = Math.floor(SHOP_BOX_W / 2) - 10; // 버튼 반폭

const shopBox = new PIXI.Graphics();
shopBox.beginFill(COLORS.panel);
shopBox.drawRoundedRect(SHOP_BOX_X, SHOP_BOX_TOP, SHOP_BOX_W, SHOP_BOX_H, 20);
shopBox.endFill();
shopPanel.addChild(shopBox);

const shopTitle = new PIXI.Text(t('shop_title'), {
    fontFamily: 'Pretendard',
    fontSize: 22,
    fill: COLORS.gold,
    fontWeight: 'bold',
});
shopTitle.anchor.set(0.5);
shopTitle.position.set(SCREEN_WIDTH / 2, SHOP_BOX_TOP + 28);
shopPanel.addChild(shopTitle);

// 탭 영역 (Protection / Forge) — 무기 직구매 탭 제거 (Sprint 2)
const TAB_Y = SHOP_BOX_TOP + 55;
const TAB_W = Math.floor(SHOP_BOX_W / 2) - 6;
const TAB_H = 36;

// 탭 배경
const tabProtectionBg = new PIXI.Graphics();
const tabForgeBg = new PIXI.Graphics();

function drawTabs(activeTab: 'protection' | 'forge') {
    tabProtectionBg.clear();
    tabForgeBg.clear();

    // Protection 탭
    tabProtectionBg.beginFill(activeTab === 'protection' ? 0x2a6a4a : 0x1a2a3a);
    tabProtectionBg.drawRoundedRect(SHOP_BOX_X + 4, TAB_Y, TAB_W, TAB_H, 8);
    tabProtectionBg.endFill();

    // Forge 탭
    tabForgeBg.beginFill(activeTab === 'forge' ? 0x6a2a0a : 0x1a2a3a);
    tabForgeBg.drawRoundedRect(SHOP_BOX_X + TAB_W + 8, TAB_Y, TAB_W, TAB_H, 8);
    tabForgeBg.endFill();
}

drawTabs('protection');
shopPanel.addChild(tabProtectionBg);
shopPanel.addChild(tabForgeBg);

// 탭 텍스트
const tabProtectionText = new PIXI.Text(t('tab_protection'), {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: COLORS.text,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: TAB_W - 8,
});
tabProtectionText.anchor.set(0.5);
tabProtectionText.position.set(SHOP_BOX_X + 4 + TAB_W / 2, TAB_Y + TAB_H / 2);
shopPanel.addChild(tabProtectionText);

const tabForgeText = new PIXI.Text(t('tab_forge'), {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: COLORS.text,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: TAB_W - 8,
});
tabForgeText.anchor.set(0.5);
tabForgeText.position.set(SHOP_BOX_X + TAB_W + 8 + TAB_W / 2, TAB_Y + TAB_H / 2);
shopPanel.addChild(tabForgeText);

// 탭 클릭 영역
const tabProtectionHit = new PIXI.Graphics();
tabProtectionHit.beginFill(0xffffff, 0.001);
tabProtectionHit.drawRect(SHOP_BOX_X + 4, TAB_Y, TAB_W, TAB_H);
tabProtectionHit.endFill();
tabProtectionHit.eventMode = 'static';
tabProtectionHit.cursor = 'pointer';
shopPanel.addChild(tabProtectionHit);

const tabForgeHit = new PIXI.Graphics();
tabForgeHit.beginFill(0xffffff, 0.001);
tabForgeHit.drawRect(SHOP_BOX_X + TAB_W + 8, TAB_Y, TAB_W, TAB_H);
tabForgeHit.endFill();
tabForgeHit.eventMode = 'static';
tabForgeHit.cursor = 'pointer';
shopPanel.addChild(tabForgeHit);

// 구분선 (탭 하단)
const shopDivider = new PIXI.Graphics();
shopDivider.lineStyle(1, 0x4a6a8a, 0.8);
shopDivider.moveTo(SHOP_BOX_X + 10, TAB_Y + TAB_H + 4);
shopDivider.lineTo(SHOP_BOX_X + SHOP_BOX_W - 10, TAB_Y + TAB_H + 4);
shopPanel.addChild(shopDivider);

// 콘텐츠 시작 Y (탭 하단 + 여유)
const CONTENT_START_Y = TAB_Y + TAB_H + 16;

// 방지권 탭 컨테이너
const scrollTabContainer = new PIXI.Container();
shopPanel.addChild(scrollTabContainer);

// 용광로 탭 컨테이너 (Sprint 2 — 무기 직구매 탭 대체)
const forgeTabContainer = new PIXI.Container();
forgeTabContainer.visible = false;
shopPanel.addChild(forgeTabContainer);

// 방지권 구매 버튼들 (4종류) — 카드 형태: 이름 + 효과설명 + 가격 + Buy 버튼
const scrollBuyButtons: PIXI.Container[] = [];

const SCROLL_EFFECT_KEYS: Array<'scroll_effect_basic' | 'scroll_effect_mid' | 'scroll_effect_hi' | 'scroll_effect_ultra'> = [
    'scroll_effect_basic', 'scroll_effect_mid', 'scroll_effect_hi', 'scroll_effect_ultra',
];
const SCROLL_CARD_H = 66;
const SCROLL_CARD_GAP = 8;

PROTECTION_SCROLLS.forEach((scroll, index) => {
    const cardY = CONTENT_START_Y + index * (SCROLL_CARD_H + SCROLL_CARD_GAP);
    const card = new PIXI.Container();
    card.position.set(SCREEN_WIDTH / 2, cardY);
    // 'none'은 자식(buyBtn)까지 인터랙션을 차단함 — 카드 자신만 히트테스트 제외
    card.eventMode = 'passive';

    const cardBg = new PIXI.Graphics();
    cardBg.beginFill(0x2a4a6a);
    cardBg.drawRoundedRect(-SHOP_BTN_HALF, 0, SHOP_BTN_HALF * 2, SCROLL_CARD_H, 8);
    cardBg.endFill();
    card.addChild(cardBg);

    // 방지권 이름 (좌상단)
    const nameText = new PIXI.Text(scrollName(scroll), {
        fontFamily: 'Pretendard',
        fontSize: 13,
        fill: 0xffffff,
        fontWeight: 'bold',
    });
    nameText.anchor.set(0, 0);
    nameText.position.set(-SHOP_BTN_HALF + 10, 8);
    const nameMaxW = SHOP_BTN_HALF * 2 - 100;
    if (nameText.width > nameMaxW) nameText.scale.x = nameMaxW / nameText.width;
    card.addChild(nameText);

    // 효과 설명 (이름 아래)
    const effectText = new PIXI.Text(t(SCROLL_EFFECT_KEYS[index]), {
        fontFamily: 'Pretendard',
        fontSize: 10,
        fill: 0x99ccff,
    });
    effectText.anchor.set(0, 0);
    effectText.position.set(-SHOP_BTN_HALF + 10, 26);
    const effectMaxW = SHOP_BTN_HALF * 2 - 100;
    if (effectText.width > effectMaxW) effectText.scale.x = effectMaxW / effectText.width;
    card.addChild(effectText);

    // 가격 표시 (하단 왼쪽)
    const priceText = new PIXI.Text(t('price_display', { amount: scroll.price.toLocaleString() }), {
        fontFamily: 'Pretendard',
        fontSize: 11,
        fill: COLORS.gold,
    });
    priceText.anchor.set(0, 0);
    priceText.position.set(-SHOP_BTN_HALF + 10, 44);
    card.addChild(priceText);

    // 구매 버튼 (우측)
    const buyBtn = new PIXI.Container();
    buyBtn.position.set(SHOP_BTN_HALF - 42, SCROLL_CARD_H / 2);
    buyBtn.eventMode = 'static';
    buyBtn.cursor = 'pointer';
    card.addChild(buyBtn);

    const buyBtnBg = new PIXI.Graphics();
    buyBtnBg.beginFill(0x1a7a3a);
    buyBtnBg.drawRoundedRect(-34, -16, 68, 32, 8);
    buyBtnBg.endFill();
    buyBtn.addChild(buyBtnBg);

    const buyBtnText = new PIXI.Text(t('scroll_buy_btn'), {
        fontFamily: 'Pretendard',
        fontSize: 13,
        fill: 0xffffff,
        fontWeight: 'bold',
    });
    buyBtnText.anchor.set(0.5);
    buyBtn.addChild(buyBtnText);

    buyBtn.on('pointerdown', () => {
        if (gold >= scroll.price) {
            gold -= scroll.price;
            protectionScrolls[index]++;
            haptic('success');
            showResult(t('scroll_purchased', { name: scrollName(scroll) }), COLORS.success);
            saveGame();
            updateUI();
            updateShopButtons();
            showShopTutorial();
        } else {
            showResult(t('msg_gold_short'), COLORS.fail);
        }
    });

    scrollTabContainer.addChild(card);
    scrollBuyButtons.push(buyBtn); // alpha 업데이트를 위해 buyBtn 저장
});

// ──────────────────────────────────────────────────────────────
// 용광로 UI (Sprint 2)
// ──────────────────────────────────────────────────────────────
// 서브탭 (무기 / 방지권 / 승급)
type ForgeSubTab = 'weapon' | 'scroll' | 'upgrade' | 'item';
let forgeActiveSubTab: ForgeSubTab = 'weapon';

const FORGE_SUBTAB_Y = CONTENT_START_Y + 4;
const FORGE_SUBTAB_W = Math.floor(SHOP_BOX_W / 4) - 3;
const FORGE_SUBTAB_H = 28;
const forgeSubs: ForgeSubTab[] = ['weapon', 'scroll', 'upgrade', 'item'];

const forgeSubBgs: PIXI.Graphics[] = [];
const forgeSubTexts: PIXI.Text[] = [];
const forgeSubHits: PIXI.Graphics[] = [];

forgeSubs.forEach((sub, i) => {
    const bg = new PIXI.Graphics();
    forgeTabContainer.addChild(bg);
    forgeSubBgs.push(bg);

    const labelKey = sub === 'weapon' ? 'forge_tab_weapon'
        : sub === 'scroll' ? 'forge_tab_scroll'
        : sub === 'upgrade' ? 'forge_tab_upgrade'
        : 'forge_tab_item';
    const txt = new PIXI.Text(t(labelKey), {
        fontFamily: 'Pretendard',
        fontSize: 10,
        fill: COLORS.text,
        fontWeight: 'bold',
        align: 'center',
        wordWrap: true,
        wordWrapWidth: FORGE_SUBTAB_W - 4,
    });
    txt.anchor.set(0.5);
    const subX = SHOP_BOX_X + 4 + i * (FORGE_SUBTAB_W + 4) + FORGE_SUBTAB_W / 2;
    txt.position.set(subX, FORGE_SUBTAB_Y + FORGE_SUBTAB_H / 2);
    forgeTabContainer.addChild(txt);
    forgeSubTexts.push(txt);

    const hit = new PIXI.Graphics();
    hit.beginFill(0xffffff, 0.001);
    hit.drawRect(SHOP_BOX_X + 4 + i * (FORGE_SUBTAB_W + 4), FORGE_SUBTAB_Y, FORGE_SUBTAB_W, FORGE_SUBTAB_H);
    hit.endFill();
    hit.eventMode = 'static';
    hit.cursor = 'pointer';
    hit.on('pointerdown', () => switchForgeSubTab(sub));
    forgeTabContainer.addChild(hit);
    forgeSubHits.push(hit);
});

function drawForgeSubTabs(active: ForgeSubTab) {
    forgeSubs.forEach((sub, i) => {
        const isActive = sub === active;
        forgeSubBgs[i].clear();
        const color = sub === 'weapon' ? 0x5a1a0a : sub === 'scroll' ? 0x1a3a2a : sub === 'upgrade' ? 0x2a1a5a : 0x1a3a1a;
        forgeSubBgs[i].beginFill(isActive ? color : 0x181818);
        forgeSubBgs[i].drawRoundedRect(
            SHOP_BOX_X + 4 + i * (FORGE_SUBTAB_W + 4),
            FORGE_SUBTAB_Y,
            FORGE_SUBTAB_W,
            FORGE_SUBTAB_H,
            6,
        );
        forgeSubBgs[i].endFill();
    });
}
drawForgeSubTabs('weapon');

// 레시피 스크롤 영역
const forgeRecipeContainer = new PIXI.Container();
forgeRecipeContainer.position.set(0, FORGE_SUBTAB_Y + FORGE_SUBTAB_H + 8);
forgeTabContainer.addChild(forgeRecipeContainer);

// 제작 확인 팝업
const forgeCraftPopup = new PIXI.Container();
forgeCraftPopup.visible = false;
uiContainer.addChild(forgeCraftPopup);

const forgeCraftPopupBg = new PIXI.Graphics();
forgeCraftPopupBg.beginFill(0x000000, 0.85);
forgeCraftPopupBg.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
forgeCraftPopupBg.endFill();
forgeCraftPopupBg.eventMode = 'static';
forgeCraftPopup.addChild(forgeCraftPopupBg);

const forgeCraftBox = new PIXI.Graphics();
forgeCraftBox.beginFill(0x1a1010);
forgeCraftBox.drawRoundedRect(-140, -160, 280, 320, 18);
forgeCraftBox.endFill();
forgeCraftBox.lineStyle(2, 0xd35400, 0.9);
forgeCraftBox.drawRoundedRect(-140, -160, 280, 320, 18);
forgeCraftBox.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2);
forgeCraftPopup.addChild(forgeCraftBox);

const forgeCraftTitle = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 16,
    fill: 0xf39c12,
    fontWeight: 'bold',
});
forgeCraftTitle.anchor.set(0.5);
forgeCraftTitle.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 135);
forgeCraftPopup.addChild(forgeCraftTitle);

const forgeCraftIcon = new PIXI.Sprite(PIXI.Texture.EMPTY);
forgeCraftIcon.width = 60;
forgeCraftIcon.height = 60;
forgeCraftIcon.anchor.set(0.5);
forgeCraftIcon.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 90);
forgeCraftPopup.addChild(forgeCraftIcon);

const forgeCraftCostText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: 0xcccccc,
    align: 'center',
});
forgeCraftCostText.anchor.set(0.5);
forgeCraftCostText.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 30);
forgeCraftPopup.addChild(forgeCraftCostText);

const forgeCraftResultText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0x2ecc71,
    align: 'center',
    fontWeight: 'bold',
});
forgeCraftResultText.anchor.set(0.5);
forgeCraftResultText.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 10);
forgeCraftPopup.addChild(forgeCraftResultText);

const forgeCraftConfirmBtn = new PIXI.Container();
forgeCraftConfirmBtn.position.set(SCREEN_WIDTH / 2 - 75, SCREEN_HEIGHT / 2 + 110);
forgeCraftConfirmBtn.eventMode = 'static';
forgeCraftConfirmBtn.cursor = 'pointer';
forgeCraftPopup.addChild(forgeCraftConfirmBtn);

const forgeCraftConfirmBg = new PIXI.Graphics();
forgeCraftConfirmBg.beginFill(0xd35400);
forgeCraftConfirmBg.drawRoundedRect(-55, -18, 110, 36, 10);
forgeCraftConfirmBg.endFill();
forgeCraftConfirmBtn.addChild(forgeCraftConfirmBg);

const forgeCraftConfirmText = new PIXI.Text(t('forge_craft_btn'), {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 102,
});
forgeCraftConfirmText.anchor.set(0.5);
forgeCraftConfirmBtn.addChild(forgeCraftConfirmText);

const forgeCraftCancelBtn = new PIXI.Container();
forgeCraftCancelBtn.position.set(SCREEN_WIDTH / 2 + 75, SCREEN_HEIGHT / 2 + 110);
forgeCraftCancelBtn.eventMode = 'static';
forgeCraftCancelBtn.cursor = 'pointer';
forgeCraftPopup.addChild(forgeCraftCancelBtn);

const forgeCraftCancelBg = new PIXI.Graphics();
forgeCraftCancelBg.beginFill(0x444455);
forgeCraftCancelBg.drawRoundedRect(-55, -18, 110, 36, 10);
forgeCraftCancelBg.endFill();
forgeCraftCancelBtn.addChild(forgeCraftCancelBg);

const forgeCraftCancelText = new PIXI.Text(t('cancel'), {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: 0xaaaaaa,
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 102,
});
forgeCraftCancelText.anchor.set(0.5);
forgeCraftCancelBtn.addChild(forgeCraftCancelText);

forgeCraftCancelBtn.on('pointerdown', () => { forgeCraftPopup.visible = false; });

// 무기 획득 시 "장착/보관" 선택 팝업
const forgeEquipChoicePopup = new PIXI.Container();
forgeEquipChoicePopup.visible = false;
uiContainer.addChild(forgeEquipChoicePopup);

const forgeEquipChoiceBg = new PIXI.Graphics();
forgeEquipChoiceBg.beginFill(0x000000, 0.85);
forgeEquipChoiceBg.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
forgeEquipChoiceBg.endFill();
forgeEquipChoiceBg.eventMode = 'static';
forgeEquipChoicePopup.addChild(forgeEquipChoiceBg);

const forgeEquipChoiceBox = new PIXI.Graphics();
forgeEquipChoiceBox.beginFill(0x1a1010);
forgeEquipChoiceBox.drawRoundedRect(-140, -100, 280, 200, 18);
forgeEquipChoiceBox.endFill();
forgeEquipChoiceBox.lineStyle(2, 0xd35400, 0.9);
forgeEquipChoiceBox.drawRoundedRect(-140, -100, 280, 200, 18);
forgeEquipChoiceBox.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2);
forgeEquipChoicePopup.addChild(forgeEquipChoiceBox);

const forgeEquipChoiceTitle = new PIXI.Text(t('forge_weapon_to_equip'), {
    fontFamily: 'Pretendard',
    fontSize: 15,
    fill: 0xf39c12,
    fontWeight: 'bold',
});
forgeEquipChoiceTitle.anchor.set(0.5);
forgeEquipChoiceTitle.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 75);
forgeEquipChoicePopup.addChild(forgeEquipChoiceTitle);

const forgeEquipChoiceEquipBtn = new PIXI.Container();
forgeEquipChoiceEquipBtn.position.set(SCREEN_WIDTH / 2 - 75, SCREEN_HEIGHT / 2 + 30);
forgeEquipChoiceEquipBtn.eventMode = 'static';
forgeEquipChoiceEquipBtn.cursor = 'pointer';
forgeEquipChoicePopup.addChild(forgeEquipChoiceEquipBtn);

const forgeEquipChoiceEquipBg = new PIXI.Graphics();
forgeEquipChoiceEquipBg.beginFill(0x1a6a3a);
forgeEquipChoiceEquipBg.drawRoundedRect(-55, -18, 110, 36, 10);
forgeEquipChoiceEquipBg.endFill();
forgeEquipChoiceEquipBtn.addChild(forgeEquipChoiceEquipBg);

const forgeEquipChoiceEquipText = new PIXI.Text(t('forge_weapon_equip'), {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 102,
});
forgeEquipChoiceEquipText.anchor.set(0.5);
forgeEquipChoiceEquipBtn.addChild(forgeEquipChoiceEquipText);

const forgeEquipChoiceKeepBtn = new PIXI.Container();
forgeEquipChoiceKeepBtn.position.set(SCREEN_WIDTH / 2 + 75, SCREEN_HEIGHT / 2 + 30);
forgeEquipChoiceKeepBtn.eventMode = 'static';
forgeEquipChoiceKeepBtn.cursor = 'pointer';
forgeEquipChoicePopup.addChild(forgeEquipChoiceKeepBtn);

const forgeEquipChoiceKeepBg = new PIXI.Graphics();
forgeEquipChoiceKeepBg.beginFill(0x2a4a6a);
forgeEquipChoiceKeepBg.drawRoundedRect(-55, -18, 110, 36, 10);
forgeEquipChoiceKeepBg.endFill();
forgeEquipChoiceKeepBtn.addChild(forgeEquipChoiceKeepBg);

const forgeEquipChoiceKeepText = new PIXI.Text(t('forge_weapon_to_keep'), {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: 0xffffff,
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 102,
});
forgeEquipChoiceKeepText.anchor.set(0.5);
forgeEquipChoiceKeepBtn.addChild(forgeEquipChoiceKeepText);

// 보관 → 장착 콜백 (팝업 열 때 주입)
let _forgePendingWeaponLevel = 0;

function openForgeEquipChoice(level: number) {
    _forgePendingWeaponLevel = level;
    // 재료는 이미 소모·저장된 상태 — 팝업 중 앱 종료 시 무기 증발 방지용 영속화
    storage.set('swordEnhance_pendingForge_v1', level);
    forgeEquipChoicePopup.visible = true;
}

function clearForgePending() {
    _forgePendingWeaponLevel = 0;
    storage.set('swordEnhance_pendingForge_v1', 0);
}

forgeEquipChoiceEquipBtn.on('pointerdown', () => {
    if (_forgePendingWeaponLevel <= 0) return;
    // 기존 장착 무기 보존: 보관함으로 스왑, 가득이면 판매가 환급 (무경고 소실 방지)
    if (weaponLevel > 1) {
        const res = keepWeapon(keepInv, weaponLevel);
        if (res.success) {
            keepInv = res.state;
            updateKeepBtn();
            updateKeepPanelBtnCount();
        } else {
            const price = getSellPrice(weaponLevel);
            gold += price;
            showResult(t('sell_received', { amount: price.toLocaleString() }), COLORS.gold);
        }
    }
    releaseActiveProtection();
    weaponLevel = _forgePendingWeaponLevel;
    sacrificeState = resetSacrifice(weaponLevel);
    updateSacrificeBonus();
    updateSacrificeBtn();
    clearForgePending();
    forgeEquipChoicePopup.visible = false;
    updateUI();
    saveGame();
    haptic('success');
});

forgeEquipChoiceKeepBtn.on('pointerdown', () => {
    if (_forgePendingWeaponLevel <= 0) return;
    const res = keepWeapon(keepInv, _forgePendingWeaponLevel);
    if (res.success) {
        keepInv = res.state;
        showResult(t('keep_success', { name: weaponName(getWeaponByLevel(_forgePendingWeaponLevel)) }), 0x4ecdc4);
        updateKeepBtn();
        updateKeepPanelBtnCount();
        clearForgePending();
        forgeEquipChoicePopup.visible = false;
        saveGame();
        haptic('success');
    } else {
        // 보관함 가득 — 기존 무기를 덮어쓰는 강제 장착 대신 팝업 유지 (유저가 장착을 직접 선택하도록)
        showResult(t('keep_full'), 0xe74c3c);
        haptic('warning');
    }
});

// 미수령 제작 무기 복원 — 장착/보관 선택 팝업 중 앱이 종료되면 재료만 소모되고 무기가 증발했음
{
    const pendingForge = storage.get('swordEnhance_pendingForge_v1', 0);
    if (typeof pendingForge === 'number' && pendingForge >= 1 && pendingForge <= MAX_LEVEL) {
        openForgeEquipChoice(pendingForge);
    }
}

// 재료명 조회 유틸
function matName(key: keyof Materials): string {
    return t(`mat_${key}` as Parameters<typeof t>[0]);
}

// 레시피 → 결과 텍스트
function forgeResultLabel(recipe: import('./forge.js').ForgeRecipe): string {
    const r = recipe.result;
    if (r.type === 'weapon') {
        return `${weaponName(getWeaponByLevel(r.level))} (Lv.${r.level})`;
    }
    if (r.type === 'scroll') {
        const scroll = PROTECTION_SCROLLS[r.tier];
        return `${scrollName(scroll)} x${r.count}`;
    }
    if (r.type === 'buff_item') {
        const nameMap: Record<string, string> = {
            luck_charm: t('buff_item_luck_charm'),
            guarantee: t('buff_item_guarantee'),
            destroy_guard: t('buff_item_destroy_guard'),
        };
        return `${nameMap[r.item] ?? r.item} x${r.count}`;
    }
    // material_upgrade
    return `${matName(r.from)} x10 -> ${matName(r.to)} x${r.gain}`;
}

// 레시피 → 비용 텍스트
function forgeCostLabel(recipe: import('./forge.js').ForgeRecipe): string {
    const c = recipe.cost;
    const parts: string[] = [];
    if (c.iron_dust) parts.push(`${matName('iron_dust')} x${c.iron_dust}`);
    if (c.refining_core) parts.push(`${matName('refining_core')} x${c.refining_core}`);
    if (c.lava_essence) parts.push(`${matName('lava_essence')} x${c.lava_essence}`);
    if (c.abyss_crystal) parts.push(`${matName('abyss_crystal')} x${c.abyss_crystal}`);
    return parts.join('\n');
}

// 용광로 레시피 스크롤 시작 인덱스 — 작은 화면에서 마지막 레시피가 잘리거나 Close 버튼에 가려지던 문제 해결
let forgeRowStart = 0;

// 용광로 레시피 행 빌드
function buildForgeRecipeRows(category: ForgeCategory) {
    forgeRecipeContainer.removeChildren();
    const recipes = FORGE_RECIPES.filter(r => r.category === category);
    const areaMaxH = SHOP_BOX_H - (FORGE_SUBTAB_Y - SHOP_BOX_TOP) - FORGE_SUBTAB_H - 60;

    forgeRowStart = Math.min(Math.max(0, forgeRowStart), Math.max(0, recipes.length - 1));
    let forgeOverflowed = false;

    let yOffset = 0;
    recipes.forEach((recipe, recipeIdx) => {
        if (recipeIdx < forgeRowStart) return;
        const crafted = canCraft(recipe, materials);
        const missing = crafted ? [] : getMissingMaterials(recipe, materials);
        const costCount = Object.values(recipe.cost).filter(v => v && v > 0).length;
        const rowH = Math.max(72, (1 + costCount + missing.length) * 15 + 32);

        const row = new PIXI.Container();
        row.position.set(SCREEN_WIDTH / 2, yOffset + rowH / 2);

        const rowBg = new PIXI.Graphics();
        rowBg.beginFill(crafted ? 0x1a1a10 : 0x0e0e0e, 0.9);
        rowBg.drawRoundedRect(-SHOP_BTN_HALF, -rowH / 2 + 4, SHOP_BTN_HALF * 2, rowH - 8, 8);
        rowBg.endFill();
        if (crafted) {
            rowBg.lineStyle(1, 0xd35400, 0.5);
            rowBg.drawRoundedRect(-SHOP_BTN_HALF, -rowH / 2 + 4, SHOP_BTN_HALF * 2, rowH - 8, 8);
        }
        row.addChild(rowBg);

        // 아이콘 (행 세로 중앙)
        const icon = new PIXI.Sprite(PIXI.Texture.EMPTY);
        icon.width = 40;
        icon.height = 40;
        icon.anchor.set(0.5);
        icon.position.set(-SHOP_BTN_HALF + 26, 0);
        icon.alpha = crafted ? 1 : 0.4;
        row.addChild(icon);
        PIXI.Texture.fromURL(recipe.icon).then(tex => { icon.texture = tex; }).catch(() => {});

        // 텍스트 영역 시작 Y: 상단에서 내려오며 순차 배치
        const textX = -SHOP_BTN_HALF + 54;
        const lineH = 15;
        const totalLines = 1 + costCount + missing.length;
        const startY = -(totalLines * lineH) / 2 + lineH / 2;

        // 레시피 이름
        const nameText = new PIXI.Text(t(recipe.nameKey), {
            fontFamily: 'Pretendard',
            fontSize: 12,
            fill: crafted ? 0xffffff : 0x666666,
            fontWeight: 'bold',
        });
        nameText.anchor.set(0, 0.5);
        nameText.position.set(textX, startY);
        const nameMaxW = SHOP_BTN_HALF * 2 - 54 - 68; // 아이콘 + 버튼 영역 제외
        if (nameText.width > nameMaxW) {
            nameText.scale.x = nameMaxW / nameText.width;
        }
        row.addChild(nameText);

        // 재료 비용 줄 (각 재료 한 줄씩)
        const costEntries: Array<{key: keyof import('./materials.js').Materials; amount: number}> = [];
        if (recipe.cost.iron_dust) costEntries.push({ key: 'iron_dust', amount: recipe.cost.iron_dust });
        if (recipe.cost.refining_core) costEntries.push({ key: 'refining_core', amount: recipe.cost.refining_core });
        if (recipe.cost.lava_essence) costEntries.push({ key: 'lava_essence', amount: recipe.cost.lava_essence });
        if (recipe.cost.abyss_crystal) costEntries.push({ key: 'abyss_crystal', amount: recipe.cost.abyss_crystal });

        costEntries.forEach((entry, ci) => {
            const costLineText = new PIXI.Text(`${matName(entry.key)} x${entry.amount}`, {
                fontFamily: 'Pretendard',
                fontSize: 10,
                fill: crafted ? 0xf39c12 : 0x555555,
            });
            costLineText.anchor.set(0, 0.5);
            costLineText.position.set(textX, startY + lineH * (1 + ci));
            row.addChild(costLineText);
        });

        // 부족 재료 표시 (비활성 이유) — 비용 줄 아래에 배치
        if (missing.length > 0) {
            missing.forEach((m, mi) => {
                const missingLine = new PIXI.Text(
                    `${matName(m.key)}: ${m.have}/${m.need}`,
                    { fontFamily: 'Pretendard', fontSize: 9, fill: 0xe74c3c },
                );
                missingLine.anchor.set(0, 0.5);
                missingLine.position.set(textX, startY + lineH * (1 + costCount + mi));
                row.addChild(missingLine);
            });
        }

        // 제작 버튼 (세로 중앙)
        const craftBtn = new PIXI.Container();
        craftBtn.position.set(SHOP_BTN_HALF - 38, 0);
        craftBtn.eventMode = crafted ? 'static' : 'none';
        craftBtn.cursor = 'pointer';
        craftBtn.alpha = crafted ? 1 : 0.3;
        row.addChild(craftBtn);

        const craftBtnBg = new PIXI.Graphics();
        craftBtnBg.beginFill(crafted ? 0xd35400 : 0x444444);
        craftBtnBg.drawRoundedRect(-30, -14, 60, 28, 7);
        craftBtnBg.endFill();
        craftBtn.addChild(craftBtnBg);

        const craftBtnText = new PIXI.Text(t('forge_craft_btn'), {
            fontFamily: 'Pretendard',
            fontSize: 11,
            fill: 0xffffff,
            fontWeight: 'bold',
        });
        craftBtnText.anchor.set(0.5);
        craftBtn.addChild(craftBtnText);

        craftBtn.on('pointerdown', () => {
            // 확인 팝업 열기
            forgeCraftTitle.text = t(recipe.nameKey);
            PIXI.Texture.fromURL(recipe.icon).then(tex => { forgeCraftIcon.texture = tex; }).catch(() => {});
            forgeCraftCostText.text = `${t('forge_cost_label')}\n${forgeCostLabel(recipe)}`;
            forgeCraftResultText.text = `${t('forge_result_label')} ${forgeResultLabel(recipe)}`;

            // 확인 버튼 이벤트 재연결
            forgeCraftConfirmBtn.removeAllListeners();
            forgeCraftConfirmBtn.on('pointerdown', () => {
                forgeCraftPopup.visible = false;
                if (!canCraft(recipe, materials)) {
                    showResult(t('forge_not_enough_mat'), COLORS.fail);
                    return;
                }
                materials = deductCost(materials, recipe.cost);

                const r = recipe.result;
                if (r.type === 'weapon') {
                    // 무기 획득 → 장착/보관 선택
                    showResult(t('forge_craft_success', { name: t(recipe.nameKey) }), 0xf39c12);
                    haptic('success');
                    openForgeEquipChoice(r.level);
                } else if (r.type === 'scroll') {
                    protectionScrolls[r.tier] += r.count;
                    const scroll = PROTECTION_SCROLLS[r.tier];
                    showResult(t('forge_craft_success', { name: scrollName(scroll) }), COLORS.success);
                    haptic('success');
                } else if (r.type === 'material_upgrade') {
                    // material_upgrade
                    materials = { ...materials, [r.to]: materials[r.to] + r.gain };
                    showResult(t('forge_craft_success', { name: matName(r.to) }), 0xa855f7);
                    haptic('success');
                } else if (r.type === 'buff_item') {
                    // Sprint 3: 버프 아이템 획득
                    buffItems = addBuffItem(buffItems, r.item, r.count);
                    updateBuffSlots();
                    const nameMap: Record<string, string> = {
                        luck_charm: t('buff_item_luck_charm'),
                        guarantee: t('buff_item_guarantee'),
                        destroy_guard: t('buff_item_destroy_guard'),
                    };
                    showResult(t('forge_craft_success', { name: nameMap[r.item] ?? r.item }), 0x27ae60);
                    haptic('success');
                }

                saveGame();
                updateMaterialPanel();
                buildForgeRecipeRows(forgeActiveSubTab);
            });

            forgeCraftPopup.visible = true;
        });

        // 행 높이 제한 (패널 박스 넘침 방지) — 아랫변까지 검사해 Close 버튼과의 겹침 차단
        if (yOffset + rowH <= areaMaxH) {
            forgeRecipeContainer.addChild(row);
            yOffset += rowH + 6; // 다음 행 시작 위치 (행간 6px)
        } else {
            forgeOverflowed = true;
        }
    });

    // 넘친 레시피 접근용 스크롤 버튼 갱신
    forgeNavUpBtn.visible = forgeRowStart > 0;
    forgeNavDownBtn.visible = forgeOverflowed;
}

// 레시피 스크롤 버튼 (Close 버튼 행 좌우 빈 공간 — 어떤 레시피 행과도 겹치지 않음)
function makeForgeNavBtn(dir: -1 | 1): PIXI.Container {
    const btn = new PIXI.Container();
    btn.position.set(SCREEN_WIDTH / 2 + dir * (SHOP_BTN_HALF - 26), CLOSE_BTN_Y_FORGE);
    btn.eventMode = 'static';
    btn.cursor = 'pointer';
    btn.visible = false;
    const bg = new PIXI.Graphics();
    bg.beginFill(0x3a3a2a);
    bg.drawRoundedRect(-18, -16, 36, 32, 8);
    bg.endFill();
    bg.lineStyle(1, 0xd35400, 0.5);
    bg.drawRoundedRect(-18, -16, 36, 32, 8);
    btn.addChild(bg);
    const arrow = new PIXI.Text(dir < 0 ? '^' : 'v', {
        fontFamily: 'Pretendard', fontSize: 15, fill: 0xf39c12, fontWeight: 'bold',
    });
    arrow.anchor.set(0.5);
    btn.addChild(arrow);
    btn.on('pointerdown', () => {
        forgeRowStart += dir;
        buildForgeRecipeRows(forgeActiveSubTab);
        haptic('light');
    });
    forgeTabContainer.addChild(btn);
    return btn;
}
const CLOSE_BTN_Y_FORGE = SHOP_BOX_TOP + SHOP_BOX_H - 28; // CLOSE_BTN_Y와 동일 (선언 순서상 별도 정의)
const forgeNavUpBtn = makeForgeNavBtn(-1);
const forgeNavDownBtn = makeForgeNavBtn(1);

function switchForgeSubTab(sub: ForgeSubTab) {
    forgeActiveSubTab = sub;
    forgeRowStart = 0; // 서브탭 전환 시 스크롤 리셋
    drawForgeSubTabs(sub);
    buildForgeRecipeRows(sub);
}

// 탭 전환 로직
function switchShopTab(tab: 'protection' | 'forge') {
    drawTabs(tab);
    scrollTabContainer.visible = tab === 'protection';
    forgeTabContainer.visible = tab === 'forge';
    if (tab === 'forge') {
        buildForgeRecipeRows(forgeActiveSubTab);
    }
}

tabProtectionHit.on('pointerdown', () => switchShopTab('protection'));
tabForgeHit.on('pointerdown', () => switchShopTab('forge'));

// 상점 버튼 상태 업데이트
function updateShopButtons() {
    // 방지권 버튼 상태
    PROTECTION_SCROLLS.forEach((scroll, index) => {
        scrollBuyButtons[index].alpha = gold >= scroll.price ? 1 : 0.5;
    });
    // 용광로 탭이 열려있으면 레시피 행 갱신
    if (forgeTabContainer.visible) {
        buildForgeRecipeRows(forgeActiveSubTab);
    }
}

// 닫기 버튼 — shopBox 하단 고정 (SCREEN_HEIGHT 기준이 아닌 SHOP_BOX 하단 기준)
const CLOSE_BTN_Y = SHOP_BOX_TOP + SHOP_BOX_H - 28;
const closeShopBtn = new PIXI.Container();
closeShopBtn.position.set(SCREEN_WIDTH / 2, CLOSE_BTN_Y);
closeShopBtn.eventMode = 'static';
closeShopBtn.cursor = 'pointer';
shopPanel.addChild(closeShopBtn);

const closeShopBg = new PIXI.Graphics();
closeShopBg.beginFill(0x555566);
closeShopBg.drawRoundedRect(-80, -20, 160, 40, 10);
closeShopBg.endFill();
closeShopBtn.addChild(closeShopBg);

const closeShopText = new PIXI.Text(t('close_shop'), {
    fontFamily: 'Pretendard',
    fontSize: 15,
    fill: COLORS.text,
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 152,
});
closeShopText.anchor.set(0.5);
closeShopBtn.addChild(closeShopText);

closeShopBtn.on('pointerdown', () => {
    shopPanel.visible = false;
    switchShopTab('protection'); // 닫을 때 탭 초기화
});

// --- 상점 튜토리얼 팝업 ---
const shopTutorialPopup = new PIXI.Container();
shopTutorialPopup.visible = false;
uiContainer.addChild(shopTutorialPopup);

const tutorialBg = new PIXI.Graphics();
tutorialBg.beginFill(0x000000, 0.85);
tutorialBg.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
tutorialBg.endFill();
tutorialBg.eventMode = 'static';
shopTutorialPopup.addChild(tutorialBg);

const tutorialBox = new PIXI.Graphics();
tutorialBox.beginFill(0x1a2a4a);
tutorialBox.drawRoundedRect(-140, -100, 280, 200, 20);
tutorialBox.endFill();
tutorialBox.lineStyle(2, 0x4ecdc4, 1);
tutorialBox.drawRoundedRect(-140, -100, 280, 200, 20);
tutorialBox.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2);
shopTutorialPopup.addChild(tutorialBox);

const tutorialIcon = new PIXI.Text('📦', { fontSize: 40 });
tutorialIcon.anchor.set(0.5);
tutorialIcon.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 60);
shopTutorialPopup.addChild(tutorialIcon);

const tutorialTitle = new PIXI.Text(t('buy_complete_title'), {
    fontFamily: 'Pretendard',
    fontSize: 18,
    fill: 0x4ecdc4,
    fontWeight: 'bold',
});
tutorialTitle.anchor.set(0.5);
tutorialTitle.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 15);
shopTutorialPopup.addChild(tutorialTitle);

const tutorialDesc = new PIXI.Text(t('buy_complete_desc'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xaaaaaa,
    align: 'center',
});
tutorialDesc.anchor.set(0.5);
tutorialDesc.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 30);
shopTutorialPopup.addChild(tutorialDesc);

const tutorialOkBtn = new PIXI.Container();
tutorialOkBtn.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 75);
tutorialOkBtn.eventMode = 'static';
tutorialOkBtn.cursor = 'pointer';
shopTutorialPopup.addChild(tutorialOkBtn);

const tutorialOkBg = new PIXI.Graphics();
tutorialOkBg.beginFill(0x4ecdc4);
tutorialOkBg.drawRoundedRect(-60, -18, 120, 36, 18);
tutorialOkBg.endFill();
tutorialOkBtn.addChild(tutorialOkBg);

const tutorialOkText = new PIXI.Text(t('buy_complete_ok'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0x1a2a4a,
    fontWeight: 'bold',
});
tutorialOkText.anchor.set(0.5);
tutorialOkBtn.addChild(tutorialOkText);

tutorialOkBtn.on('pointerdown', () => {
    shopTutorialPopup.visible = false;
});

function showShopTutorial() {
    if (shopTutorialShown) return;
    shopTutorialShown = true;
    saveGame();
    shopTutorialPopup.visible = true;
}

function openShop() {
    shopPanel.visible = true;
    updateShopButtons();
}

// --- 광고 복구 팝업 ---
const adRestorePanel = new PIXI.Container();
adRestorePanel.visible = false;
uiContainer.addChild(adRestorePanel);

const adRestoreBg = new PIXI.Graphics();
adRestoreBg.beginFill(0x000000, 0.9);
adRestoreBg.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
adRestoreBg.endFill();
adRestoreBg.eventMode = 'static';
adRestorePanel.addChild(adRestoreBg);

// 팝업 박스 (중앙 정렬을 위한 컨테이너)
const popupCenterY = SCREEN_HEIGHT / 2;
const boxWidth = 300;
const boxHeight = 320;

const adRestoreBox = new PIXI.Graphics();
adRestoreBox.beginFill(0x1a2a4a);
adRestoreBox.drawRoundedRect(-boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight, 24);
adRestoreBox.endFill();
// 테두리
adRestoreBox.lineStyle(2, 0x3a5a8a, 1);
adRestoreBox.drawRoundedRect(-boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight, 24);
adRestoreBox.position.set(SCREEN_WIDTH / 2, popupCenterY);
adRestorePanel.addChild(adRestoreBox);

// 파괴 아이콘
const destroyIcon = new PIXI.Text('💔', { fontSize: 50 });
destroyIcon.anchor.set(0.5);
destroyIcon.position.set(SCREEN_WIDTH / 2, popupCenterY - 115);
adRestorePanel.addChild(destroyIcon);

const adRestoreTitle = new PIXI.Text(t('destroy_title'), {
    fontFamily: 'Pretendard',
    fontSize: 22,
    fill: 0xff6b6b,
    fontWeight: 'bold',
});
adRestoreTitle.anchor.set(0.5);
adRestoreTitle.position.set(SCREEN_WIDTH / 2, popupCenterY - 60);
adRestorePanel.addChild(adRestoreTitle);

const adRestoreWeaponText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 20,
    fill: COLORS.text,
    fontWeight: 'bold',
});
adRestoreWeaponText.anchor.set(0.5);
adRestoreWeaponText.position.set(SCREEN_WIDTH / 2, popupCenterY - 25);
adRestorePanel.addChild(adRestoreWeaponText);

// 구분선
const divider = new PIXI.Graphics();
divider.beginFill(0x3a5a8a);
divider.drawRect(-100, 0, 200, 1);
divider.endFill();
divider.position.set(SCREEN_WIDTH / 2, popupCenterY + 5);
adRestorePanel.addChild(divider);

// 남은 횟수 표시
const adRestoreCountText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 15,
    fill: COLORS.gold,
});
adRestoreCountText.anchor.set(0.5);
adRestoreCountText.position.set(SCREEN_WIDTH / 2, popupCenterY + 30);
adRestorePanel.addChild(adRestoreCountText);

// 광고 시청 버튼
const watchAdBtn = new PIXI.Container();
watchAdBtn.position.set(SCREEN_WIDTH / 2, popupCenterY + 75);
watchAdBtn.eventMode = 'static';
watchAdBtn.cursor = 'pointer';
adRestorePanel.addChild(watchAdBtn);

const watchAdBg = new PIXI.Graphics();
watchAdBg.beginFill(0x2ecc71);
watchAdBg.drawRoundedRect(-120, -25, 240, 50, 25);
watchAdBg.endFill();
watchAdBtn.addChild(watchAdBg);

const watchAdText = new PIXI.Text(t('destroy_ad_restore'), {
    fontFamily: 'Pretendard',
    fontSize: 15,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 230,
});
watchAdText.anchor.set(0.5);
watchAdBtn.addChild(watchAdText);

watchAdBtn.on('pointerdown', async () => {
    adRestorePanel.visible = false;

    // 광고 시청 (플레이스홀더)
    const adResult = await adService.showRewardedAd();

    if (adResult) {
        // 광고 시청 완료 - 무기 복구
        adRestoreRemaining--; // 횟수 차감
        weaponLevel = lastDestroyedLevel;
        const weapon = getWeaponByLevel(weaponLevel);
        showResult(t('weapon_restored', { name: weaponName(weapon) }), COLORS.success);
        haptic('success');
        updateUI();
    } else {
        showResult(t('ad_watch_fail'), COLORS.fail);
    }
});

// 포기 버튼
const giveUpBtn = new PIXI.Container();
giveUpBtn.position.set(SCREEN_WIDTH / 2, popupCenterY + 135);
giveUpBtn.eventMode = 'static';
giveUpBtn.cursor = 'pointer';
adRestorePanel.addChild(giveUpBtn);

// 히트 영역 (투명 배경)
const giveUpHitArea = new PIXI.Graphics();
giveUpHitArea.beginFill(0x000000, 0.001); // 거의 투명
giveUpHitArea.drawRoundedRect(-100, -20, 200, 40, 8);
giveUpHitArea.endFill();
giveUpBtn.addChild(giveUpHitArea);

const giveUpText = new PIXI.Text(t('destroy_give_up'), {
    fontFamily: 'Pretendard',
    fontSize: 15,
    fill: 0x999999,
});
giveUpText.anchor.set(0.5);
giveUpBtn.addChild(giveUpText);

giveUpBtn.on('pointerdown', () => {
    adRestorePanel.visible = false;
    lastDestroyedLevel = 0;
    adRestoreRemaining = 3; // 처음부터 시작하면 기회 리셋
    adBoostRemaining = 3; // 확률 UP 기회도 리셋
    adFreeEnhanceRemaining = 3; // 무료 강화 기회도 리셋
    freeEnhanceActive = false;
    updateAdGoldButton();
});

// Deprecated: showAdRestorePopup — 통합 파괴 팝업(showDestroyedWithMatPopup)에서 처리됨
// function showAdRestorePopup() {
//     const weapon = getWeaponByLevel(lastDestroyedLevel);
//     adRestoreWeaponText.text = weaponName(weapon);
//     adRestoreWeaponText.style.fill = getWeaponColor(lastDestroyedLevel);
//     adRestoreCountText.text = t('destroy_restore_count', { current: adRestoreRemaining, max: 3 });
//     adRestorePanel.visible = true;
// }

// --- 결과 표시 함수 ---
function showResult(text: string, color: number) {
    // 이전 애니메이션 취소
    gsap.killTweensOf(resultText);

    resultText.text = text;
    resultText.style.fill = color;
    resultText.visible = true;
    resultText.alpha = 1;
    resultText.y = SCREEN_HEIGHT / 2 - 200;

    gsap.to(resultText, {
        y: resultText.y - 30,
        alpha: 0,
        duration: 1.5,
        ease: 'power2.out',
        onComplete: () => {
            resultText.visible = false;
        }
    });
}

// --- 이펙트 ---
function createSuccessEffect() {
    for (let i = 0; i < 20; i++) {
        const particle = new PIXI.Graphics();
        particle.beginFill(COLORS.gold);
        particle.drawCircle(0, 0, Math.random() * 5 + 2);
        particle.endFill();
        particle.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 50);
        effectContainer.addChild(particle);

        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * 100 + 50;

        gsap.to(particle, {
            x: particle.x + Math.cos(angle) * distance,
            y: particle.y + Math.sin(angle) * distance,
            alpha: 0,
            duration: 0.8,
            ease: 'power2.out',
            onComplete: () => {
                effectContainer.removeChild(particle);
            }
        });
    }
}

// 파괴 연출 (드라마틱하게)
function createDestroyEffect() {
    const centerX = SCREEN_WIDTH / 2;
    const centerY = SCREEN_HEIGHT / 2 - 50;

    // 1. 화면 플래시
    const flash = new PIXI.Graphics();
    flash.beginFill(0xff0000, 0.6);
    flash.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
    flash.endFill();
    effectContainer.addChild(flash);
    gsap.to(flash, {
        alpha: 0,
        duration: 0.3,
        onComplete: () => { effectContainer.removeChild(flash); }
    });

    // 2. 강한 화면 흔들기
    gsap.to(gameContainer, {
        x: 15,
        duration: 0.03,
        yoyo: true,
        repeat: 10,
        onComplete: () => {
            gameContainer.x = 0;
        }
    });

    // 3. 무기 폭발 효과 - 현재 스프라이트 캡처
    const currentSprite = weaponSprite;
    if (currentSprite) {
        const originalScale = currentSprite.scale.x;
        gsap.to(currentSprite.scale, {
            x: originalScale * 1.5,
            y: originalScale * 1.5,
            duration: 0.1,
            onComplete: () => {
                // 캡처한 스프라이트가 아직 유효하면 복원
                if (currentSprite && currentSprite.parent) {
                    currentSprite.scale.set(originalScale);
                }
            }
        });
    }

    // 4. 검 파편 효과 (많이, 다양하게)
    for (let i = 0; i < 25; i++) {
        const particle = new PIXI.Graphics();
        const color = Math.random() > 0.5 ? 0xff4444 : 0x888888;
        particle.beginFill(color);

        // 다양한 파편 모양
        if (Math.random() > 0.5) {
            particle.drawRect(-6, -3, 12, 6);
        } else {
            particle.moveTo(0, -8);
            particle.lineTo(5, 4);
            particle.lineTo(-5, 4);
            particle.closePath();
        }
        particle.endFill();
        particle.position.set(centerX, centerY);
        particle.rotation = Math.random() * Math.PI * 2;
        effectContainer.addChild(particle);

        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * 150 + 80;
        const duration = Math.random() * 0.5 + 0.8;

        gsap.to(particle, {
            x: centerX + Math.cos(angle) * distance,
            y: centerY + Math.sin(angle) * distance + 150, // 중력 느낌
            rotation: particle.rotation + Math.random() * 6 - 3,
            alpha: 0,
            duration: duration,
            ease: 'power2.out',
            onComplete: () => {
                effectContainer.removeChild(particle);
            }
        });
    }

    // 5. 충격파 효과
    const shockwave = new PIXI.Graphics();
    shockwave.lineStyle(4, 0xff0000, 0.8);
    shockwave.drawCircle(0, 0, 10);
    shockwave.position.set(centerX, centerY);
    effectContainer.addChild(shockwave);

    gsap.to(shockwave.scale, {
        x: 8,
        y: 8,
        duration: 0.5,
        ease: 'power2.out',
    });
    gsap.to(shockwave, {
        alpha: 0,
        duration: 0.5,
        ease: 'power2.out',
        onComplete: () => { effectContainer.removeChild(shockwave); }
    });

    // 6. "파괴" 텍스트 효과
    const destroyText = new PIXI.Text(t('msg_destroyed'), {
        fontFamily: 'Pretendard',
        fontSize: 48,
        fill: 0xff0000,
        fontWeight: 'bold',
        dropShadow: true,
        dropShadowColor: 0x000000,
        dropShadowDistance: 3,
    });
    destroyText.anchor.set(0.5);
    destroyText.position.set(centerX, centerY - 30);
    destroyText.scale.set(0.5);
    effectContainer.addChild(destroyText);

    gsap.to(destroyText.scale, {
        x: 1.2,
        y: 1.2,
        duration: 0.2,
        ease: 'back.out',
    });
    gsap.to(destroyText, {
        y: centerY - 80,
        alpha: 0,
        duration: 1.2,
        delay: 0.3,
        ease: 'power2.out',
        onComplete: () => { effectContainer.removeChild(destroyText); }
    });
}

// 최고 레벨 달성 축하 이펙트
function showCelebration() {
    const centerX = SCREEN_WIDTH / 2;
    const centerY = SCREEN_HEIGHT / 2;

    // 1. 화면 금색 플래시
    const flash = new PIXI.Graphics();
    flash.beginFill(0xffd700, 0.5);
    flash.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
    flash.endFill();
    effectContainer.addChild(flash);
    gsap.to(flash, {
        alpha: 0,
        duration: 0.5,
        onComplete: () => { effectContainer.removeChild(flash); }
    });

    // 2. 폭죽 효과 (여러 번 터짐)
    const fireworkColors = [0xff0000, 0x00ff00, 0x0088ff, 0xffff00, 0xff00ff, 0x00ffff, 0xffd700];

    function createFirework(x: number, y: number, delay: number) {
        setTimeout(() => {
            const color = fireworkColors[Math.floor(Math.random() * fireworkColors.length)];

            // 폭죽 파티클
            for (let i = 0; i < 30; i++) {
                const particle = new PIXI.Graphics();
                particle.beginFill(color);
                particle.drawCircle(0, 0, Math.random() * 4 + 2);
                particle.endFill();
                particle.position.set(x, y);
                effectContainer.addChild(particle);

                const angle = (Math.PI * 2 / 30) * i + Math.random() * 0.3;
                const distance = Math.random() * 80 + 60;

                gsap.to(particle, {
                    x: x + Math.cos(angle) * distance,
                    y: y + Math.sin(angle) * distance + 50,
                    alpha: 0,
                    duration: 1 + Math.random() * 0.5,
                    ease: 'power2.out',
                    onComplete: () => { effectContainer.removeChild(particle); }
                });
            }

            // 폭죽 중심 광원
            const glow = new PIXI.Graphics();
            glow.beginFill(0xffffff, 0.8);
            glow.drawCircle(0, 0, 15);
            glow.endFill();
            glow.position.set(x, y);
            effectContainer.addChild(glow);
            gsap.to(glow, {
                alpha: 0,
                duration: 0.3,
                onComplete: () => { effectContainer.removeChild(glow); }
            });

            haptic('light');
        }, delay);
    }

    // 여러 위치에서 폭죽 터뜨리기
    createFirework(centerX, centerY - 100, 0);
    createFirework(centerX - 100, centerY - 50, 200);
    createFirework(centerX + 100, centerY - 50, 400);
    createFirework(centerX - 50, centerY - 150, 600);
    createFirework(centerX + 50, centerY - 150, 800);
    createFirework(centerX, centerY - 80, 1000);

    // 3. 축하 텍스트
    const congratsText = new PIXI.Text(t('congrats_text'), {
        fontFamily: 'Pretendard',
        fontSize: 36,
        fill: 0xffd700,
        fontWeight: 'bold',
        dropShadow: true,
        dropShadowColor: 0x000000,
        dropShadowDistance: 3,
        dropShadowBlur: 5,
    });
    congratsText.anchor.set(0.5);
    congratsText.position.set(centerX, centerY - 180);
    congratsText.scale.set(0);
    effectContainer.addChild(congratsText);

    gsap.to(congratsText.scale, {
        x: 1.2,
        y: 1.2,
        duration: 0.5,
        ease: 'back.out',
    });

    const subText = new PIXI.Text(t('completion_title'), {
        fontFamily: 'Pretendard',
        fontSize: 20,
        fill: 0xffffff,
        fontWeight: 'bold',
        dropShadow: true,
        dropShadowColor: 0x000000,
        dropShadowDistance: 2,
    });
    subText.anchor.set(0.5);
    subText.position.set(centerX, centerY - 130);
    subText.alpha = 0;
    effectContainer.addChild(subText);

    gsap.to(subText, {
        alpha: 1,
        duration: 0.5,
        delay: 0.5,
    });

    // 4. 텍스트 서서히 사라짐
    gsap.to(congratsText, {
        alpha: 0,
        y: centerY - 220,
        duration: 1,
        delay: 3,
        ease: 'power2.out',
        onComplete: () => { effectContainer.removeChild(congratsText); }
    });
    gsap.to(subText, {
        alpha: 0,
        y: centerY - 170,
        duration: 1,
        delay: 3,
        ease: 'power2.out',
        onComplete: () => { effectContainer.removeChild(subText); }
    });

    // 5. 금색 파티클 지속 효과
    for (let wave = 0; wave < 3; wave++) {
        setTimeout(() => {
            for (let i = 0; i < 15; i++) {
                const particle = new PIXI.Graphics();
                particle.beginFill(0xffd700);
                particle.drawCircle(0, 0, 5);
                particle.endFill();
                particle.position.set(
                    Math.random() * SCREEN_WIDTH,
                    SCREEN_HEIGHT + 20
                );
                effectContainer.addChild(particle);

                gsap.to(particle, {
                    y: -50,
                    x: particle.x + (Math.random() - 0.5) * 100,
                    rotation: Math.random() * Math.PI * 4,
                    alpha: 0,
                    duration: 2 + Math.random(),
                    ease: 'power1.out',
                    onComplete: () => { effectContainer.removeChild(particle); }
                });
            }
        }, wave * 500);
    }

    // 6. 2초 후 완료 팝업 표시
    setTimeout(() => {
        showCompletionPopup();
    }, 2000);
}

// --- 완료 팝업 (이름 등록 + 리더보드) ---
const completionPopup = new PIXI.Container();
completionPopup.visible = false;
uiContainer.addChild(completionPopup);

const completionBg = new PIXI.Graphics();
completionBg.beginFill(0x000000, 0.9);
completionBg.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
completionBg.endFill();
completionBg.eventMode = 'static';
completionPopup.addChild(completionBg);

const completionBox = new PIXI.Graphics();
completionBox.beginFill(0x1a2a4a);
completionBox.drawRoundedRect(-160, -200, 320, 400, 20);
completionBox.endFill();
completionBox.lineStyle(3, 0xffd700);
completionBox.drawRoundedRect(-160, -200, 320, 400, 20);
completionBox.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2);
completionPopup.addChild(completionBox);

const completionTitle = new PIXI.Text(t('completion_all_done'), {
    fontFamily: 'Pretendard',
    fontSize: 22,
    fill: 0xffd700,
    fontWeight: 'bold',
});
completionTitle.anchor.set(0.5);
completionTitle.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 160);
completionPopup.addChild(completionTitle);

const completionRankText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 16,
    fill: 0xffffff,
    align: 'center',
});
completionRankText.anchor.set(0.5);
completionRankText.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 120);
completionPopup.addChild(completionRankText);

// 리더보드 표시 영역
const leaderboardContainer = new PIXI.Container();
leaderboardContainer.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 50);
completionPopup.addChild(leaderboardContainer);

const leaderboardTitle = new PIXI.Text(t('leaderboard_title'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xaaaaaa,
});
leaderboardTitle.anchor.set(0.5);
leaderboardTitle.position.set(0, -30);
leaderboardContainer.addChild(leaderboardTitle);

const leaderboardText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: 0xffffff,
    align: 'center',
    lineHeight: 22,
});
leaderboardText.anchor.set(0.5, 0);
leaderboardText.position.set(0, -5);
leaderboardContainer.addChild(leaderboardText);

// 이름 등록 버튼
const registerBtn = new PIXI.Container();
registerBtn.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 120);
registerBtn.eventMode = 'static';
registerBtn.cursor = 'pointer';
completionPopup.addChild(registerBtn);

const registerBtnBg = new PIXI.Graphics();
registerBtnBg.beginFill(0xffd700);
registerBtnBg.drawRoundedRect(-100, -22, 200, 44, 22);
registerBtnBg.endFill();
registerBtn.addChild(registerBtnBg);

const registerBtnText = new PIXI.Text(t('register_name'), {
    fontFamily: 'Pretendard',
    fontSize: 16,
    fill: 0x000000,
    fontWeight: 'bold',
});
registerBtnText.anchor.set(0.5);
registerBtn.addChild(registerBtnText);

registerBtn.on('pointerdown', () => {
    const name = prompt(t('prompt_register_name'));
    if (name && name.trim()) {
        const rank = addToLeaderboard(name.trim());
        completionRankText.text = t('completion_rank_msg', { rank });
        updateLeaderboardDisplay();
        registerBtn.visible = false;
    }
});

// 닫기 버튼
const completionCloseBtn = new PIXI.Container();
completionCloseBtn.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 170);
completionCloseBtn.eventMode = 'static';
completionCloseBtn.cursor = 'pointer';
completionPopup.addChild(completionCloseBtn);

const completionCloseBtnBg = new PIXI.Graphics();
completionCloseBtnBg.beginFill(0x555555);
completionCloseBtnBg.drawRoundedRect(-60, -18, 120, 36, 18);
completionCloseBtnBg.endFill();
completionCloseBtn.addChild(completionCloseBtnBg);

const completionCloseBtnText = new PIXI.Text(t('completion_close'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xffffff,
});
completionCloseBtnText.anchor.set(0.5);
completionCloseBtn.addChild(completionCloseBtnText);

completionCloseBtn.on('pointerdown', () => {
    completionPopup.visible = false;
});

function updateLeaderboardDisplay() {
    const leaderboard = getLeaderboard();
    const top10 = leaderboard.slice(-10).reverse(); // 최근 10명

    if (top10.length === 0) {
        leaderboardText.text = t('no_completion');
    } else {
        leaderboardText.text = top10.map((entry) =>
            t('leaderboard_entry', { rank: entry.rank, name: entry.name })
        ).join('\n');
    }
}

function showCompletionPopup() {
    const leaderboard = getLeaderboard();
    const totalCompletions = leaderboard.length;

    if (totalCompletions > 0) {
        completionRankText.text = t('completion_total_msg', { total: totalCompletions });
    } else {
        completionRankText.text = t('be_first');
    }

    registerBtn.visible = true;
    updateLeaderboardDisplay();
    completionPopup.visible = true;
}

// --- 버튼 이벤트 ---
enhanceBtn.on('pointerdown', enhance);
sellBtn.on('pointerdown', sellWeapon);
shopBtn.on('pointerdown', openShop);

// --- 초기화 ---
updateUI();

// --- 게임 루프 ---
let time = 0;
let lastBoostAttentionTime = 0;
let lastGoldAdAttentionTime = 0;
app.ticker.add((delta) => {
    time += delta * 0.01;

    // 배경 파티클 (불티가 위로 상승)
    bgParticles.forEach((particle, i) => {
        particle.y -= delta * 0.4 * (Math.random() + 0.5); // 위로 이동
        particle.x += Math.sin(time * 2 + i) * 0.3; // 좌우로 살짝 흔들림
        particle.alpha = 0.4 + Math.sin(time * 3 + i) * 0.3; // 반짝임
        // 화면 위로 벗어나면 아래로 리셋
        if (particle.y < -10) {
            particle.y = SCREEN_HEIGHT + 10;
            particle.x = Math.random() * SCREEN_WIDTH;
        }
    });

    // 강화 버튼 글로우 애니메이션
    if (gold >= getEnhanceCost(weaponLevel)) {
        enhanceBtnGlow.alpha = 0.3 + Math.sin(time * 3) * 0.15;
    }

    // 무기 미세 흔들림 (고강화 시)
    if (weaponLevel >= 20 && weaponSprite) {
        weaponSprite.rotation = Math.sin(time * 10) * 0.02;
    }

    // 4초 이상 강화 안 누르면 확률 UP 버튼 반짝임 (횟수 남아있을 때만)
    const now = Date.now();
    if (!rateBoostActive && !isBoostAnimating && adBoostRemaining > 0 && now - lastEnhanceTime > 4000 && now - lastBoostAttentionTime > 8000) {
        startBoostAttention();
        lastBoostAttentionTime = now;
    }

    // 골드 부족하면 무료 강화 버튼 반짝임
    const currentEnhanceCost = getEnhanceCost(weaponLevel);
    if (gold < currentEnhanceCost && adFreeEnhanceRemaining > 0 && !isGoldAdAnimating && now - lastGoldAdAttentionTime > 6000) {
        startGoldAdAttention();
        lastGoldAdAttentionTime = now;
    }
});

// --- 타이틀 화면 ---
const titleScreen = new PIXI.Container();
app.stage.addChild(titleScreen);

// 타이틀 배경 (검은색)
const titleBg = new PIXI.Graphics();
titleBg.beginFill(0x000000);
titleBg.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
titleBg.endFill();
titleScreen.addChild(titleBg);

// 타이틀 이미지 로드
async function loadTitleScreen() {
    try {
        const titleTexture = await PIXI.Assets.load('./title.png');
        const titleImage = new PIXI.Sprite(titleTexture);
        titleImage.anchor.set(0.5);
        titleImage.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2);

        // 화면에 맞게 스케일 조절 (cover 방식)
        const scaleX = SCREEN_WIDTH / titleTexture.width;
        const scaleY = SCREEN_HEIGHT / titleTexture.height;
        const scale = Math.max(scaleX, scaleY);
        titleImage.scale.set(scale);

        titleScreen.addChildAt(titleImage, 1);
    } catch (e) {
        console.error(t('title_load_failed'), e);
    }
}
loadTitleScreen();

// 게임 타이틀
const gameTitle = new PIXI.Text(t('game_title'), {
    fontFamily: 'Pretendard',
    fontSize: 42,
    fill: 0xffffff,
    fontWeight: 'bold',
    dropShadow: true,
    dropShadowColor: 0x000000,
    dropShadowDistance: 3,
    dropShadowBlur: 5,
});
gameTitle.anchor.set(0.5);
gameTitle.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT * 0.15);
titleScreen.addChild(gameTitle);

// 터치하여 시작 텍스트
const startText = new PIXI.Text(t('tap_to_start'), {
    fontFamily: 'Pretendard',
    fontSize: 22,
    fill: 0xffffff,
    fontWeight: 'bold',
    dropShadow: true,
    dropShadowColor: 0x000000,
    dropShadowDistance: 2,
    dropShadowBlur: 3,
});
startText.anchor.set(0.5);
startText.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT * 0.85);
titleScreen.addChild(startText);

// 텍스트 깜빡임 애니메이션
gsap.to(startText, {
    alpha: 0.3,
    duration: 0.8,
    repeat: -1,
    yoyo: true,
    ease: 'power1.inOut',
});

// 타이틀 화면 숨기기 함수
function hideTitleScreen() {
    gsap.to(titleScreen, {
        alpha: 0,
        duration: 0.5,
        ease: 'power2.out',
        onComplete: () => {
            titleScreen.visible = false;
            titleScreen.eventMode = 'none';
        }
    });
    haptic('light');
}

// 타이틀 화면 보이기 함수
function showTitleScreen() {
    titleScreen.visible = true;
    titleScreen.alpha = 1;
    titleScreen.eventMode = 'static';
    startText.alpha = 1;
    gsap.to(startText, {
        alpha: 0.3,
        duration: 0.8,
        repeat: -1,
        yoyo: true,
        ease: 'power1.inOut',
    });
}

// 터치하면 게임 시작
titleScreen.eventMode = 'static';
titleScreen.cursor = 'pointer';
titleScreen.on('pointerdown', hideTitleScreen);

// --- 데모 모드 (?demo=1) ---
// 스토어 스크린샷 촬영용. 타이틀 자동 스킵 + 골드 충전 + 자동 강화 루프
if (new URLSearchParams(location.search).get('demo') === '1') {
    setTimeout(() => {
        hideTitleScreen();
        gold = 999999999;
        goldText.text = gold.toLocaleString();

        setInterval(() => {
            if (gold < 1000000) {
                gold = 999999999;
                goldText.text = gold.toLocaleString();
            }
            // 파괴 복구 팝업이 떠있으면 먼저 닫는다 (처음부터 다시 시작)
            if (adRestorePanel.visible) {
                adRestorePanel.visible = false;
                lastDestroyedLevel = 0;
                adRestoreRemaining = 3;
                adBoostRemaining = 3;
                adFreeEnhanceRemaining = 3;
                freeEnhanceActive = false;
                return;
            }
            enhance();
        }, 1400);
    }, 600);
}

// ============================================================
// SPRINT 1: 재료 드랍 UI + 킵/무기 인벤토리 UI
// ============================================================

// --- 재료 패널 버튼 (상단 골드 아이콘 우측) ---
const matPanelBtn = new PIXI.Container();
matPanelBtn.position.set(TOPR_MAT_X, TOP_OFFSET + 20);
matPanelBtn.eventMode = 'static';
matPanelBtn.cursor = 'pointer';
uiContainer.addChild(matPanelBtn);

const matPanelBtnBg = new PIXI.Graphics();
matPanelBtnBg.beginFill(0x1a1030, 0.8);
matPanelBtnBg.drawRoundedRect(0, 0, 70, 50, 10);
matPanelBtnBg.endFill();
matPanelBtnBg.lineStyle(1, 0x9b59b6, 0.5);
matPanelBtnBg.drawRoundedRect(0, 0, 70, 50, 10);
matPanelBtn.addChild(matPanelBtnBg);

// 재료 아이콘 (mat_iron_dust.png 대표)
const matBtnIcon = new PIXI.Sprite(PIXI.Texture.EMPTY);
matBtnIcon.width = 28;
matBtnIcon.height = 28;
matBtnIcon.position.set(8, 11);
matPanelBtn.addChild(matBtnIcon);

PIXI.Texture.fromURL('./items/mat_iron_dust.png').then((tex) => {
    matBtnIcon.texture = tex;
}).catch(() => { /* 아이콘 없으면 무시 */ });

const matBtnLabel = new PIXI.Text(t('mat_panel_title'), {
    fontFamily: 'Pretendard',
    fontSize: 10,
    fill: 0xcc99ff,
    fontWeight: 'bold',
});
matBtnLabel.anchor.set(0.5);
matBtnLabel.position.set(45, 12);
matPanelBtn.addChild(matBtnLabel);

const matBtnTotal = new PIXI.Text('0', {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: 0xffffff,
    fontWeight: 'bold',
});
matBtnTotal.anchor.set(0.5);
matBtnTotal.position.set(45, 32);
matPanelBtn.addChild(matBtnTotal);

// --- 재료 패널 (팝업) ---
const matPanel = new PIXI.Container();
matPanel.visible = false;
uiContainer.addChild(matPanel);

const matPanelOverlay = new PIXI.Graphics();
matPanelOverlay.beginFill(0x000000, 0.75);
matPanelOverlay.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
matPanelOverlay.endFill();
matPanelOverlay.eventMode = 'static';
matPanel.addChild(matPanelOverlay);

const matPanelBox = new PIXI.Graphics();
matPanelBox.beginFill(0x1a1030);
matPanelBox.drawRoundedRect(-150, -220, 300, 440, 18);
matPanelBox.endFill();
matPanelBox.lineStyle(2, 0x9b59b6, 0.9);
matPanelBox.drawRoundedRect(-150, -220, 300, 440, 18);
matPanelBox.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2);
matPanel.addChild(matPanelBox);

const matPanelTitle = new PIXI.Text(t('mat_panel_title'), {
    fontFamily: 'Pretendard',
    fontSize: 20,
    fill: 0xcc99ff,
    fontWeight: 'bold',
});
matPanelTitle.anchor.set(0.5);
matPanelTitle.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 195);
matPanel.addChild(matPanelTitle);

// 재료 4종 행 컨테이너 (아이콘 + 이름 + 개수)
const matRows: PIXI.Container[] = [];
const matCountTexts: PIXI.Text[] = [];

MATERIAL_DEFS.forEach((def, idx) => {
    const row = new PIXI.Container();
    row.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 130 + idx * 75);
    matPanel.addChild(row);
    matRows.push(row);

    // 행 배경
    const rowBg = new PIXI.Graphics();
    rowBg.beginFill(0x2a1050, 0.7);
    rowBg.drawRoundedRect(-130, -28, 260, 56, 10);
    rowBg.endFill();
    row.addChild(rowBg);

    // 아이콘
    const icon = new PIXI.Sprite(PIXI.Texture.EMPTY);
    icon.width = 40;
    icon.height = 40;
    icon.anchor.set(0.5);
    icon.position.set(-100, 0);
    row.addChild(icon);
    PIXI.Texture.fromURL(def.icon).then((tex) => {
        icon.texture = tex;
    }).catch(() => { /* 아이콘 없으면 무시 */ });

    // 재료명
    const nameText = new PIXI.Text(t(`mat_${def.key}` as Parameters<typeof t>[0]), {
        fontFamily: 'Pretendard',
        fontSize: 13,
        fill: 0xeeeeee,
        fontWeight: 'bold',
    });
    nameText.anchor.set(0, 0.5);
    nameText.position.set(-70, -8);
    row.addChild(nameText);

    // 레벨 범위 표시
    const rangeText = new PIXI.Text(`Lv.${def.levelMin}-${def.levelMax}`, {
        fontFamily: 'Pretendard',
        fontSize: 10,
        fill: 0x888888,
    });
    rangeText.anchor.set(0, 0.5);
    rangeText.position.set(-70, 12);
    row.addChild(rangeText);

    // 개수
    const countText = new PIXI.Text('0', {
        fontFamily: 'Pretendard',
        fontSize: 20,
        fill: 0xffd700,
        fontWeight: 'bold',
    });
    countText.anchor.set(1, 0.5);
    countText.position.set(120, 0);
    row.addChild(countText);
    matCountTexts.push(countText);
});

// 닫기 버튼
const matPanelCloseBtn = new PIXI.Container();
matPanelCloseBtn.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 170);
matPanelCloseBtn.eventMode = 'static';
matPanelCloseBtn.cursor = 'pointer';
matPanel.addChild(matPanelCloseBtn);

const matPanelCloseBg = new PIXI.Graphics();
matPanelCloseBg.beginFill(0x444466);
matPanelCloseBg.drawRoundedRect(-60, -20, 120, 40, 10);
matPanelCloseBg.endFill();
matPanelCloseBtn.addChild(matPanelCloseBg);

const matPanelCloseText = new PIXI.Text(t('close'), {
    fontFamily: 'Pretendard',
    fontSize: 15,
    fill: 0xaaaaaa,
});
matPanelCloseText.anchor.set(0.5);
matPanelCloseBtn.addChild(matPanelCloseText);

matPanelCloseBtn.on('pointerdown', () => { matPanel.visible = false; });
matPanelOverlay.on('pointerdown', () => { matPanel.visible = false; });
matPanelBtn.on('pointerdown', () => {
    updateMaterialPanel();
    matPanel.visible = true;
});

function updateMaterialPanel() {
    const keys: (keyof Materials)[] = ['iron_dust', 'refining_core', 'lava_essence', 'abyss_crystal'];
    keys.forEach((key, idx) => {
        matCountTexts[idx].text = String(materials[key]);
    });
    const total = materials.iron_dust + materials.refining_core + materials.lava_essence + materials.abyss_crystal;
    matBtnTotal.text = String(total);
}
updateMaterialPanel();

// --- 재료 드랍 팝업 (강화 결과 연출 위에 표시) ---
// ============================================================
// 통합 파괴 팝업 (Weapon Destroyed + Material Drop)
// ============================================================

const destroyedPopup = new PIXI.Container();
destroyedPopup.visible = false;
uiContainer.addChild(destroyedPopup);

// 반투명 검은색 오버레이
const destroyedOverlay = new PIXI.Graphics();
destroyedOverlay.beginFill(0x000000, 0.7);
destroyedOverlay.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
destroyedOverlay.endFill();
destroyedOverlay.eventMode = 'static';
destroyedPopup.addChild(destroyedOverlay);

// 팝업 카드 (중앙)
const POPUP_W = 280;
const POPUP_H = 464; // 콘텐츠(아이콘 -188 ~ 닫기 +189)를 충분한 여백으로 감싸도록 확대 (이전 380은 여백 1~2px라 테두리 겹침)
const POPUP_X = SCREEN_WIDTH / 2;
const POPUP_Y = SCREEN_HEIGHT / 2;

const destroyedCard = new PIXI.Graphics();
destroyedCard.beginFill(COLORS.panel);
destroyedCard.drawRoundedRect(-POPUP_W / 2, -POPUP_H / 2, POPUP_W, POPUP_H, 16);
destroyedCard.endFill();
destroyedCard.lineStyle(2, COLORS.glow, 0.6);
destroyedCard.drawRoundedRect(-POPUP_W / 2, -POPUP_H / 2, POPUP_W, POPUP_H, 16);
destroyedCard.position.set(POPUP_X, POPUP_Y);
destroyedPopup.addChild(destroyedCard);

// 상단: 파괴 아이콘 + 제목
const destroyedIconCircle = new PIXI.Graphics();
destroyedIconCircle.beginFill(COLORS.fail, 0.2);
destroyedIconCircle.drawCircle(0, 0, 28);
destroyedIconCircle.endFill();
destroyedIconCircle.lineStyle(2, COLORS.fail, 0.7);
destroyedIconCircle.drawCircle(0, 0, 28);
destroyedIconCircle.position.set(POPUP_X, POPUP_Y - 160);
destroyedPopup.addChild(destroyedIconCircle);

// 깨진 검 아이콘 (간단한 X 모양)
const brokenSwordX1 = new PIXI.Graphics();
brokenSwordX1.lineStyle(3, COLORS.fail, 0.9);
brokenSwordX1.moveTo(-8, -8);
brokenSwordX1.lineTo(8, 8);
brokenSwordX1.position.set(POPUP_X, POPUP_Y - 160);
destroyedPopup.addChild(brokenSwordX1);

const brokenSwordX2 = new PIXI.Graphics();
brokenSwordX2.lineStyle(3, COLORS.fail, 0.9);
brokenSwordX2.moveTo(8, -8);
brokenSwordX2.lineTo(-8, 8);
brokenSwordX2.position.set(POPUP_X, POPUP_Y - 160);
destroyedPopup.addChild(brokenSwordX2);

// 제목: "Weapon Destroyed"
const destroyedTitle = new PIXI.Text(t('msg_destroyed'), {
    fontFamily: 'Pretendard',
    fontSize: 22,
    fill: COLORS.fail,
    fontWeight: 'bold',
});
destroyedTitle.anchor.set(0.5);
destroyedTitle.position.set(POPUP_X, POPUP_Y - 120);
destroyedPopup.addChild(destroyedTitle);

// 무기명
const destroyedWeaponName = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 16,
    fill: COLORS.text,
    fontWeight: 'bold',
});
destroyedWeaponName.anchor.set(0.5);
destroyedWeaponName.position.set(POPUP_X, POPUP_Y - 85);
destroyedPopup.addChild(destroyedWeaponName);

// 구분선
const destroyedDivider = new PIXI.Graphics();
destroyedDivider.lineStyle(1, COLORS.textDim, 0.3);
destroyedDivider.moveTo(POPUP_X - 110, POPUP_Y - 60);
destroyedDivider.lineTo(POPUP_X + 110, POPUP_Y - 60);
destroyedPopup.addChild(destroyedDivider);

// 재료 섹션 제목
const matSectionTitle = new PIXI.Text(t('mat_drop_title'), {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: COLORS.gold,
    fontWeight: 'bold',
});
matSectionTitle.anchor.set(0, 0.5);
matSectionTitle.position.set(POPUP_X - 100, POPUP_Y - 35);
destroyedPopup.addChild(matSectionTitle);

// 재료 아이템 표시 (아이콘 + 텍스트)
const destroyedMatIcon = new PIXI.Sprite(PIXI.Texture.EMPTY);
destroyedMatIcon.width = 28;
destroyedMatIcon.height = 28;
destroyedMatIcon.anchor.set(0.5);
destroyedMatIcon.position.set(POPUP_X - 105, POPUP_Y);
destroyedPopup.addChild(destroyedMatIcon);

const destroyedMatText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: COLORS.gold,
    fontWeight: 'bold',
});
destroyedMatText.anchor.set(0, 0.5);
destroyedMatText.position.set(POPUP_X - 65, POPUP_Y);
destroyedPopup.addChild(destroyedMatText);

// 버튼 영역
const BUTTON_Y_START = POPUP_Y + 45;
const BUTTON_H = 48;
const BUTTON_MARGIN = 12;

// 버튼 1: Watch Ad to Restore
const restoreAdBtn = new PIXI.Container();
restoreAdBtn.eventMode = 'static';
restoreAdBtn.cursor = 'pointer';
restoreAdBtn.position.set(POPUP_X, BUTTON_Y_START);
destroyedPopup.addChild(restoreAdBtn);

const restoreAdBg = new PIXI.Graphics();
restoreAdBg.beginFill(COLORS.fail);
restoreAdBg.drawRoundedRect(-120, -BUTTON_H / 2, 240, BUTTON_H, 10);
restoreAdBg.endFill();
restoreAdBtn.addChild(restoreAdBg);

const restoreAdText = new PIXI.Text(t('destroy_restore_ad'), {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 230,
});
restoreAdText.anchor.set(0.5);
restoreAdBtn.addChild(restoreAdText);

// 버튼 2: Watch Ad x2
const matDoubleBtn = new PIXI.Container();
matDoubleBtn.eventMode = 'static';
matDoubleBtn.cursor = 'pointer';
matDoubleBtn.position.set(POPUP_X, BUTTON_Y_START + BUTTON_H + BUTTON_MARGIN);
destroyedPopup.addChild(matDoubleBtn);

const matDoubleBg = new PIXI.Graphics();
matDoubleBg.beginFill(COLORS.accent);
matDoubleBg.drawRoundedRect(-120, -BUTTON_H / 2, 240, BUTTON_H, 10);
matDoubleBg.endFill();
matDoubleBtn.addChild(matDoubleBg);

const matDoubleText = new PIXI.Text(t('mat_double_ad'), {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 230,
});
matDoubleText.anchor.set(0.5);
matDoubleBtn.addChild(matDoubleText);

// 버튼 3: Close
const destroyedCloseBtn = new PIXI.Container();
destroyedCloseBtn.eventMode = 'static';
destroyedCloseBtn.cursor = 'pointer';
destroyedCloseBtn.position.set(POPUP_X, BUTTON_Y_START + (BUTTON_H + BUTTON_MARGIN) * 2);
destroyedPopup.addChild(destroyedCloseBtn);

const destroyedCloseBg = new PIXI.Graphics();
destroyedCloseBg.beginFill(0x555555);
destroyedCloseBg.drawRoundedRect(-120, -BUTTON_H / 2, 240, BUTTON_H, 10);
destroyedCloseBg.endFill();
destroyedCloseBtn.addChild(destroyedCloseBg);

const destroyedCloseText = new PIXI.Text(t('close'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: COLORS.text,
    fontWeight: 'bold',
});
destroyedCloseText.anchor.set(0.5);
destroyedCloseBtn.addChild(destroyedCloseText);

// 현재 팝업 상태
let _currentDrop: DropResult | null = null;
let _matDoubleUsed = false;

let destroyedHideTimer: gsap.core.Tween | null = null;

function showDestroyedWithMatPopup(drop: DropResult, destroyedLevel: number) {
    _currentDrop = drop;
    _matDoubleUsed = false;

    const def = MATERIAL_DEFS.find((d) => d.key === drop.key);
    if (!def) return;

    // 무기명 업데이트 (파괴 직전 레벨 사용, 전역 weaponLevel과 혼동 없음)
    const weapon = getWeaponByLevel(destroyedLevel);
    destroyedWeaponName.text = weaponName(weapon);

    // 재료 아이콘 + 텍스트
    destroyedMatText.text = t('mat_drop_notice', {
        amount: drop.amount,
        name: t(`mat_${drop.key}` as Parameters<typeof t>[0]),
    });

    PIXI.Texture.fromURL(def.icon).then((tex) => {
        destroyedMatIcon.texture = tex;
    }).catch(() => { /* 아이콘 로드 실패 */ });

    // 버튼 상태 리셋
    matDoubleText.text = t('mat_double_ad');
    matDoubleBtn.alpha = 1;
    matDoubleBtn.eventMode = 'static';

    // 복구 버튼 상태: 레벨 2 이상 + 남은 횟수 있으면 표시
    const canRestore = destroyedLevel >= 2 && adRestoreRemaining > 0;
    if (canRestore) {
        restoreAdText.text = t('destroy_restore_ad', { current: adRestoreRemaining, max: 3 });
        restoreAdBtn.alpha = 1;
        restoreAdBtn.eventMode = 'static';
        restoreAdBtn.visible = true;
    } else {
        restoreAdBtn.visible = false;
    }

    // 팝업 표시
    destroyedPopup.visible = true;
    destroyedPopup.alpha = 0;
    gsap.to(destroyedPopup, { alpha: 1, duration: 0.3 });

    // 8초 후 자동 숨김 — 이전 파괴의 타이머가 남아 있으면 새 팝업을 조기에 닫아버리므로 반드시 kill
    destroyedHideTimer?.kill();
    destroyedHideTimer = gsap.delayedCall(8, () => {
        destroyedHideTimer = null;
        if (destroyedPopup.visible) {
            gsap.to(destroyedPopup, {
                alpha: 0,
                duration: 0.3,
                onComplete: () => { destroyedPopup.visible = false; },
            });
        }
    });
}

// 버튼 이벤트
restoreAdBtn.on('pointerdown', async () => {
    // 레벨 2 이상 + 남은 횟수 있을 때만 (showDestroyedWithMatPopup에서 이미 visible 제어)
    if (lastDestroyedLevel < 2 || adRestoreRemaining <= 0) return;
    restoreAdBtn.alpha = 0.5;
    restoreAdBtn.eventMode = 'none';

    const ok = await adService.showRewardedAd();
    if (ok) {
        adRestoreRemaining--;
        weaponLevel = lastDestroyedLevel;
        showResult(t('destroy_restored', { level: lastDestroyedLevel }), COLORS.success);
        haptic('success');
        updateUI();
        saveGame();
        destroyedPopup.visible = false;
    } else {
        restoreAdBtn.alpha = 1;
        restoreAdBtn.eventMode = 'static';
    }
});

matDoubleBtn.on('pointerdown', async () => {
    if (_matDoubleUsed || !_currentDrop) return;
    matDoubleBtn.alpha = 0.5;
    matDoubleBtn.eventMode = 'none';

    const ok = await adService.showRewardedAd();
    if (ok && _currentDrop) {
        _matDoubleUsed = true;
        materials = applyDrop(materials, _currentDrop, 1);
        matDoubleText.text = t('mat_doubled');
        destroyedMatText.text = t('mat_drop_notice', {
            amount: _currentDrop.amount * 2,
            name: t(`mat_${_currentDrop.key}` as Parameters<typeof t>[0]),
        });
        updateMaterialPanel();
        saveGame();
        haptic('success');
    } else {
        matDoubleBtn.alpha = 1;
        matDoubleBtn.eventMode = 'static';
    }
});

destroyedCloseBtn.on('pointerdown', () => {
    destroyedHideTimer?.kill();
    destroyedHideTimer = null;
    gsap.to(destroyedPopup, {
        alpha: 0,
        duration: 0.2,
        onComplete: () => { destroyedPopup.visible = false; },
    });
});

destroyedOverlay.on('pointerdown', () => {
    // 오버레이 클릭으로도 닫을 수 있게
    destroyedHideTimer?.kill();
    destroyedHideTimer = null;
    gsap.to(destroyedPopup, {
        alpha: 0,
        duration: 0.2,
        onComplete: () => { destroyedPopup.visible = false; },
    });
});

// ============================================================
// SPRINT 1: 킵 버튼 + 킵 인벤토리 패널
// ============================================================

// 킵 버튼 (하단 버튼 행에 추가: Keep / Sell / Shop 한 줄 배치)
// buttonArea 내에 배치
const keepBtn = new PIXI.Container();
keepBtn.eventMode = 'static';
keepBtn.cursor = 'pointer';
buttonArea.addChild(keepBtn);

const keepBtnBg = new PIXI.Graphics();
keepBtn.addChild(keepBtnBg);

const keepBtnText = new PIXI.Text(t('keep_btn'), {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: 0xffffff,
    fontWeight: 'bold',
});
keepBtnText.anchor.set(0.5);
keepBtn.addChild(keepBtnText);

function updateKeepBtn() {
    keepBtnBg.clear();
    if (!canKeep(keepInv) ) {
        keepBtnBg.beginFill(0x555555);
        keepBtnBg.drawRoundedRect(-38, -22, 76, 44, 12);
        keepBtnBg.endFill();
        keepBtn.alpha = 0.5;
    } else {
        keepBtnBg.beginFill(0x27608a);
        keepBtnBg.drawRoundedRect(-38, -22, 76, 44, 12);
        keepBtnBg.endFill();
        keepBtnBg.lineStyle(1, 0x4ecdc4, 0.7);
        keepBtnBg.drawRoundedRect(-38, -22, 76, 44, 12);
        keepBtn.alpha = 1;
    }
}

// 킵 버튼 위치: 하단 버튼 행 (Keep / Sell / Shop) 한 줄 배치
// 375px 모바일 기준 3개 버튼 균등 배치
// 각 버튼: 너비 ~85px, 간격 20px
// 총 295px, 345px 사용 너비 내 중앙 정렬
const btnRowStartX = -147.5; // (345/2 - 295/2)
keepBtn.position.set(btnRowStartX, 95); // y는 Sell/Shop과 동일
updateKeepBtn();

keepBtn.on('pointerdown', () => {
    if (weaponLevel <= 1) {
        showResult(t('keep_lv1_blocked'), 0xe74c3c);
        return;
    }
    if (!canKeep(keepInv)) {
        showResult(t('keep_full'), 0xe74c3c);
        return;
    }
    const result = keepWeapon(keepInv, weaponLevel);
    if (result.success) {
        keepInv = result.state;
        const wName = weaponName(getWeaponByLevel(weaponLevel));
        releaseActiveProtection();
        weaponLevel = 1;
        sacrificeState = resetSacrifice(1);
        updateSacrificeBonus();
        updateSacrificeBtn();
        showResult(t('keep_success', { name: wName }), 0x4ecdc4);
        haptic('success');
        updateKeepBtn();
        updateKeepPanelBtnCount();
        updateUI();
        saveGame();
    }
});

// --- 킵 인벤토리 패널 ---
const keepPanel = new PIXI.Container();
keepPanel.visible = false;
uiContainer.addChild(keepPanel);

const keepPanelOverlay = new PIXI.Graphics();
keepPanelOverlay.beginFill(0x000000, 0.78);
keepPanelOverlay.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
keepPanelOverlay.endFill();
keepPanelOverlay.eventMode = 'static';
keepPanel.addChild(keepPanelOverlay);

const keepPanelBox = new PIXI.Graphics();
keepPanelBox.beginFill(0x0f1a2a);
keepPanelBox.drawRoundedRect(-150, -250, 300, 500, 18);
keepPanelBox.endFill();
keepPanelBox.lineStyle(2, 0x4ecdc4, 0.8);
keepPanelBox.drawRoundedRect(-150, -250, 300, 500, 18);
keepPanelBox.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2);
keepPanel.addChild(keepPanelBox);

const keepPanelTitle = new PIXI.Text(t('keep_panel_title'), {
    fontFamily: 'Pretendard',
    fontSize: 20,
    fill: 0x4ecdc4,
    fontWeight: 'bold',
});
keepPanelTitle.anchor.set(0.5);
keepPanelTitle.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 228);
keepPanel.addChild(keepPanelTitle);

// 슬롯 정보 + 확장 버튼
const keepSlotInfoText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: 0xaaaaaa,
});
keepSlotInfoText.anchor.set(0.5);
keepSlotInfoText.position.set(SCREEN_WIDTH / 2 - 40, SCREEN_HEIGHT / 2 - 200);
keepPanel.addChild(keepSlotInfoText);

const keepExpandBtn = new PIXI.Container();
keepExpandBtn.position.set(SCREEN_WIDTH / 2 + 70, SCREEN_HEIGHT / 2 - 200);
keepExpandBtn.eventMode = 'static';
keepExpandBtn.cursor = 'pointer';
keepPanel.addChild(keepExpandBtn);

const keepExpandBg = new PIXI.Graphics();
keepExpandBtn.addChild(keepExpandBg);

const keepExpandText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 11,
    fill: 0xffffff,
    fontWeight: 'bold',
});
keepExpandText.anchor.set(0.5);
keepExpandBtn.addChild(keepExpandText);

// 무기 목록
const keepItemsContainer = new PIXI.Container();
keepItemsContainer.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 165);
keepPanel.addChild(keepItemsContainer);

const keepEmptyText = new PIXI.Text(t('keep_empty'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0x888888,
});
keepEmptyText.anchor.set(0.5);
keepEmptyText.position.set(0, 80);
keepItemsContainer.addChild(keepEmptyText);

// 닫기 버튼
const keepPanelCloseBtn = new PIXI.Container();
keepPanelCloseBtn.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 210);
keepPanelCloseBtn.eventMode = 'static';
keepPanelCloseBtn.cursor = 'pointer';
keepPanel.addChild(keepPanelCloseBtn);

const keepPanelCloseBg = new PIXI.Graphics();
keepPanelCloseBg.beginFill(0x333355);
keepPanelCloseBg.drawRoundedRect(-60, -20, 120, 40, 10);
keepPanelCloseBg.endFill();
keepPanelCloseBtn.addChild(keepPanelCloseBg);

const keepPanelCloseText = new PIXI.Text(t('close'), {
    fontFamily: 'Pretendard',
    fontSize: 15,
    fill: 0xaaaaaa,
});
keepPanelCloseText.anchor.set(0.5);
keepPanelCloseBtn.addChild(keepPanelCloseText);

keepPanelCloseBtn.on('pointerdown', () => { keepPanel.visible = false; });
keepPanelOverlay.on('pointerdown', () => { keepPanel.visible = false; });

// 킵 슬롯 페이지네이션 — 3행(슬롯 5+)부터 닫기 버튼/화면 밖으로 넘쳐 조작 불가였던 문제 해결
const KEEP_PAGE_SIZE = 4; // 2열 x 2행
let keepPage = 0;

function makeKeepPageBtn(dir: -1 | 1): PIXI.Container {
    const btn = new PIXI.Container();
    btn.position.set(SCREEN_WIDTH / 2 + dir * 110, SCREEN_HEIGHT / 2 + 210);
    btn.eventMode = 'static';
    btn.cursor = 'pointer';
    btn.visible = false;
    const bg = new PIXI.Graphics();
    bg.beginFill(0x2a3a55);
    bg.drawRoundedRect(-20, -18, 40, 36, 8);
    bg.endFill();
    btn.addChild(bg);
    const arrow = new PIXI.Text(dir < 0 ? '<' : '>', {
        fontFamily: 'Pretendard', fontSize: 16, fill: 0xffffff, fontWeight: 'bold',
    });
    arrow.anchor.set(0.5);
    btn.addChild(arrow);
    btn.on('pointerdown', () => {
        keepPage += dir;
        updateKeepPanel();
        haptic('light');
    });
    keepPanel.addChild(btn);
    return btn;
}
const keepPagePrevBtn = makeKeepPageBtn(-1);
const keepPageNextBtn = makeKeepPageBtn(1);

const keepPageText = new PIXI.Text('', {
    fontFamily: 'Pretendard', fontSize: 11, fill: 0x8899aa,
});
keepPageText.anchor.set(0.5);
keepPageText.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 178);
keepPanel.addChild(keepPageText);

function updateKeepPanel() {
    updateKeepPanelBtnCount();
    // 슬롯 정보
    const cost = nextSlotCost(keepInv);
    keepSlotInfoText.text = t('keep_slot_count', {
        used: keepInv.weapons.length,
        total: keepInv.slots,
    });

    // 확장 버튼
    keepExpandBg.clear();
    if (cost === null) {
        keepExpandBg.beginFill(0x444444);
        keepExpandBg.drawRoundedRect(-35, -14, 70, 28, 6);
        keepExpandBg.endFill();
        keepExpandText.text = t('keep_slot_max');
        keepExpandText.style.fill = 0x777777;
        keepExpandBtn.alpha = 0.5;
        keepExpandBtn.eventMode = 'none';
    } else {
        keepExpandBg.beginFill(0x2a6a4a);
        keepExpandBg.drawRoundedRect(-40, -14, 80, 28, 6);
        keepExpandBg.endFill();
        keepExpandText.text = t('keep_slot_cost', { amount: cost.toLocaleString() });
        keepExpandText.style.fill = 0xffd700;
        keepExpandBtn.alpha = 1;
        keepExpandBtn.eventMode = 'static';
    }

    // 무기 목록 갱신
    keepItemsContainer.removeChildren();
    if (keepInv.weapons.length === 0) {
        keepEmptyText.visible = true;
        keepItemsContainer.addChild(keepEmptyText);
        keepPagePrevBtn.visible = false;
        keepPageNextBtn.visible = false;
        keepPageText.visible = false;
        return;
    }
    keepEmptyText.visible = false;

    // 카드 레이아웃: 2열 그리드
    // 패널 박스 폭 300px, keepItemsContainer 원점은 박스 중앙
    const CARD_W = 124;
    const CARD_H = 148;
    const CARD_GAP = 8;
    const COLS = 2;
    const gridW = COLS * CARD_W + (COLS - 1) * CARD_GAP; // 256
    const gridStartX = -gridW / 2; // 좌측 시작

    // 빈 슬롯 포함한 전체 슬롯 렌더 — 페이지 단위 (4칸 초과분은 페이지 버튼으로 접근)
    const totalSlots = Math.max(keepInv.slots, keepInv.weapons.length);
    const totalPages = Math.max(1, Math.ceil(totalSlots / KEEP_PAGE_SIZE));
    keepPage = Math.min(Math.max(0, keepPage), totalPages - 1);
    keepPagePrevBtn.visible = keepPage > 0;
    keepPageNextBtn.visible = keepPage < totalPages - 1;
    keepPageText.visible = totalPages > 1;
    keepPageText.text = `${keepPage + 1} / ${totalPages}`;

    const pageStart = keepPage * KEEP_PAGE_SIZE;
    const pageEnd = Math.min(totalSlots, pageStart + KEEP_PAGE_SIZE);
    for (let slotIdx = pageStart; slotIdx < pageEnd; slotIdx++) {
        const localIdx = slotIdx - pageStart;
        const col = localIdx % COLS;
        const row2 = Math.floor(localIdx / COLS);
        const cardX = gridStartX + col * (CARD_W + CARD_GAP);
        const cardY = row2 * (CARD_H + CARD_GAP);

        const card = new PIXI.Container();
        card.position.set(cardX, cardY);
        keepItemsContainer.addChild(card);

        const cardBg = new PIXI.Graphics();
        const kw = keepInv.weapons[slotIdx];

        if (!kw) {
            // 빈 슬롯
            cardBg.lineStyle(1, 0x334455, 0.8);
            cardBg.beginFill(0x0a1520, 0.6);
            cardBg.drawRoundedRect(0, 0, CARD_W, CARD_H, 8);
            cardBg.endFill();
            card.addChild(cardBg);

            const emptyLabel = new PIXI.Text(t('keep_slot_empty'), {
                fontFamily: 'Pretendard',
                fontSize: 11,
                fill: 0x334466,
            });
            emptyLabel.anchor.set(0.5);
            emptyLabel.position.set(CARD_W / 2, CARD_H / 2);
            card.addChild(emptyLabel);
            continue;
        }

        // 무기 카드
        const weapon = getWeaponByLevel(kw.level);
        cardBg.beginFill(0x152535, 0.9);
        cardBg.lineStyle(1, getWeaponColor(kw.level), 0.6);
        cardBg.drawRoundedRect(0, 0, CARD_W, CARD_H, 8);
        cardBg.endFill();
        card.addChild(cardBg);

        // 무기 이미지
        const weaponImg = new PIXI.Sprite(PIXI.Texture.EMPTY);
        weaponImg.width = 60;
        weaponImg.height = 60;
        weaponImg.anchor.set(0.5);
        weaponImg.position.set(CARD_W / 2, 36);
        card.addChild(weaponImg);
        if (weapon.image) {
            PIXI.Texture.fromURL(weapon.image).then(tex => { weaponImg.texture = tex; }).catch(() => {});
        }

        // 레벨 라벨
        const lvText = new PIXI.Text(`Lv.${kw.level}`, {
            fontFamily: 'Pretendard',
            fontSize: 13,
            fill: getWeaponColor(kw.level),
            fontWeight: 'bold',
        });
        lvText.anchor.set(0.5);
        lvText.position.set(CARD_W / 2, 70);
        card.addChild(lvText);

        // 무기 이름
        const wNameText = new PIXI.Text(weaponName(weapon), {
            fontFamily: 'Pretendard',
            fontSize: 9,
            fill: 0xcccccc,
        });
        wNameText.anchor.set(0.5);
        wNameText.position.set(CARD_W / 2, 84);
        if (wNameText.width > CARD_W - 8) wNameText.scale.x = (CARD_W - 8) / wNameText.width;
        card.addChild(wNameText);

        // 판매가
        const priceText = new PIXI.Text(`${getSellPrice(kw.level).toLocaleString()}G`, {
            fontFamily: 'Pretendard',
            fontSize: 9,
            fill: 0x888888,
        });
        priceText.anchor.set(0.5);
        priceText.position.set(CARD_W / 2, 96);
        card.addChild(priceText);

        // 장착 버튼 (하단 좌)
        const equipBtn = new PIXI.Container();
        equipBtn.position.set(CARD_W / 2 - 32, CARD_H - 22);
        equipBtn.eventMode = 'static';
        equipBtn.cursor = 'pointer';
        const equipBg = new PIXI.Graphics();
        equipBg.beginFill(0x1a6a3a);
        equipBg.drawRoundedRect(-28, -14, 56, 28, 6);
        equipBg.endFill();
        const equipTxt = new PIXI.Text(t('keep_card_equip'), {
            fontFamily: 'Pretendard', fontSize: 10, fill: 0xffffff, fontWeight: 'bold',
        });
        equipTxt.anchor.set(0.5);
        equipBtn.addChild(equipBg);
        equipBtn.addChild(equipTxt);
        card.addChild(equipBtn);

        const capturedIdx = slotIdx;
        equipBtn.on('pointerdown', () => {
            const res = retrieveWeapon(keepInv, capturedIdx, weaponLevel);
            if (res) {
                keepInv = res.state;
                releaseActiveProtection();
                weaponLevel = res.retrievedLevel;
                sacrificeState = resetSacrifice(weaponLevel);
                updateSacrificeBonus();
                updateSacrificeBtn();
                showResult(t('equip_msg', { name: weaponName(getWeaponByLevel(weaponLevel)) }), 0x4ecdc4);
                haptic('success');
                updateKeepPanel();
                updateKeepBtn();
                updateUI();
                saveGame();
            }
        });

        // 판매 버튼 (하단 우)
        const sellBtn = new PIXI.Container();
        sellBtn.position.set(CARD_W / 2 + 32, CARD_H - 22);
        sellBtn.eventMode = 'static';
        sellBtn.cursor = 'pointer';
        const sellBg = new PIXI.Graphics();
        sellBg.beginFill(0x6a3010);
        sellBg.drawRoundedRect(-24, -14, 48, 28, 6);
        sellBg.endFill();
        const sellTxt = new PIXI.Text(t('keep_card_sell'), {
            fontFamily: 'Pretendard', fontSize: 10, fill: 0xffffff, fontWeight: 'bold',
        });
        sellTxt.anchor.set(0.5);
        sellBtn.addChild(sellBg);
        sellBtn.addChild(sellTxt);
        card.addChild(sellBtn);

        sellBtn.on('pointerdown', () => {
            const price = getSellPrice(kw.level);
            gold += price;
            keepInv = sellKeptWeapon(keepInv, capturedIdx);
            showResult(t('keep_sell_received', { amount: price.toLocaleString() }), COLORS.gold);
            haptic('medium');
            updateKeepPanel();
            updateKeepBtn();
            updateUI();
            saveGame();
        });
    }
}

keepExpandBtn.on('pointerdown', () => {
    const cost = nextSlotCost(keepInv);
    if (cost === null) return;
    if (gold < cost) {
        showResult(t('msg_gold_short'), COLORS.fail);
        return;
    }
    const res = expandSlot(keepInv);
    if (res.success) {
        gold -= cost;
        keepInv = res.state;
        updateKeepPanel();
        updateUI();
        saveGame();
        haptic('success');
    }
});

// 킵 패널 버튼 (상단 패널 우측 settingsBtn 왼쪽)
const keepPanelBtn = new PIXI.Container();
keepPanelBtn.position.set(TOPR_KEEP_X, TOP_OFFSET + 20);
keepPanelBtn.eventMode = 'static';
keepPanelBtn.cursor = 'pointer';
uiContainer.addChild(keepPanelBtn);

// 배지 배경 (재료 배지와 동일 스타일, 청록 테마)
const keepPanelBtnBg = new PIXI.Graphics();
keepPanelBtnBg.beginFill(0x0e2a2a, 0.8);
keepPanelBtnBg.drawRoundedRect(0, 0, 70, 50, 10);
keepPanelBtnBg.endFill();
keepPanelBtnBg.lineStyle(1, 0x4ecdc4, 0.5);
keepPanelBtnBg.drawRoundedRect(0, 0, 70, 50, 10);
keepPanelBtn.addChild(keepPanelBtnBg);

const keepPanelBtnLabel = new PIXI.Text(t('keep_btn'), {
    fontFamily: 'Pretendard',
    fontSize: 11,
    fill: 0x4ecdc4,
    fontWeight: 'bold',
});
keepPanelBtnLabel.anchor.set(0.5);
keepPanelBtnLabel.position.set(35, 14);
keepPanelBtn.addChild(keepPanelBtnLabel);

// 보관 슬롯 수 (n/m)
const keepPanelBtnCount = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: 0xffffff,
    fontWeight: 'bold',
});
keepPanelBtnCount.anchor.set(0.5);
keepPanelBtnCount.position.set(35, 33);
keepPanelBtn.addChild(keepPanelBtnCount);
function updateKeepPanelBtnCount() {
    keepPanelBtnCount.text = `${keepInv.weapons.length}/${keepInv.slots}`;
}
updateKeepPanelBtnCount();

keepPanelBtn.on('pointerdown', () => {
    updateKeepPanel();
    keepPanel.visible = true;
});

// ─────────────────────────────────────────────────────
// Sprint 3: 제물 시스템 UI
// ─────────────────────────────────────────────────────

// 제물 보너스 텍스트 (강화 영역 위, rate 바 아래)
const sacrificeBonusText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: 0xf39c12,
    fontWeight: 'bold',
});
sacrificeBonusText.anchor.set(0.5);
// weaponArea.y = SCREEN_HEIGHT/2 - 40 = 400, rateContainer.y = 130 → 절대 y ≈ 400+130+20 = 550
// 프레임 바로 아래 (무기 프레임 하단 y = 400+155 = 555)
sacrificeBonusText.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 110);
uiContainer.addChild(sacrificeBonusText);

function updateSacrificeBonus() {
    const bonus = sacrificeBonus(sacrificeState, weaponLevel);
    if (bonus > 0) {
        const pct = (bonus * 100).toFixed(0);
        const cap = sacrificeCap(sacrificeState);
        sacrificeBonusText.text = t('sacrifice_bonus_label', { pct }) + ' ' + t('sacrifice_cap_label', { count: sacrificeState.count, cap });
        sacrificeBonusText.visible = true;
        sacrificeBtnCountText.text = `+${(bonus * 100).toFixed(0)}%`;
    } else {
        sacrificeBonusText.visible = false;
        sacrificeBtnCountText.text = '';
    }
}

// 제물 버튼 (강화 버튼 왼쪽, boostBtn 반대편)
const sacrificeBtn = new PIXI.Container();
sacrificeBtn.position.set(-130, 0); // buttonArea 기준
sacrificeBtn.eventMode = 'static';
sacrificeBtn.cursor = 'pointer';
sacrificeBtn.alpha = 0.7;
buttonArea.addChild(sacrificeBtn);

const sacrificeBtnBg = new PIXI.Graphics();
sacrificeBtn.addChild(sacrificeBtnBg);

const sacrificeBtnLabel = new PIXI.Text(t('sacrifice_btn'), {
    fontFamily: 'Pretendard',
    fontSize: 11,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    lineHeight: 12,
    wordWrap: true,
    wordWrapWidth: 54,
});
sacrificeBtnLabel.anchor.set(0.5, 0.5);
sacrificeBtnLabel.position.set(0, -6);
sacrificeBtn.addChild(sacrificeBtnLabel);

const sacrificeBtnCountText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 11,
    fill: 0xf39c12,
    fontWeight: 'bold',
    align: 'center',
});
sacrificeBtnCountText.anchor.set(0.5, 0);
sacrificeBtnCountText.position.set(0, 12);
sacrificeBtn.addChild(sacrificeBtnCountText);

function updateSacrificeBtn() {
    sacrificeBtnBg.clear();
    const avail = sacrificeTargetIndices(keepInv, weaponLevel);
    const atCap = sacrificeState.count >= sacrificeCap(sacrificeState);
    if (!atCap && avail.length > 0) {
        // 제물 사용 가능 — 밝은 크림슨 강조
        sacrificeBtnBg.beginFill(0x8b2020);
        sacrificeBtnBg.drawRoundedRect(-30, -25, 60, 50, 10);
        sacrificeBtnBg.endFill();
        sacrificeBtnBg.lineStyle(2, 0xe05050, 1);
        sacrificeBtnBg.drawRoundedRect(-30, -25, 60, 50, 10);
        sacrificeBtn.alpha = 1;
    } else {
        // 제물 없거나 한도 초과 — 어두운 크림슨 (항상 색 있게, 회색 금지)
        sacrificeBtnBg.beginFill(0x5a1212);
        sacrificeBtnBg.drawRoundedRect(-30, -25, 60, 50, 10);
        sacrificeBtnBg.endFill();
        sacrificeBtnBg.lineStyle(1, 0x8b2020, 0.7);
        sacrificeBtnBg.drawRoundedRect(-30, -25, 60, 50, 10);
        sacrificeBtn.alpha = 0.8;
    }
}
updateSacrificeBtn();

// 제물 패널
const sacrificePanel = new PIXI.Container();
sacrificePanel.visible = false;
uiContainer.addChild(sacrificePanel);

const sacrificePanelBg = new PIXI.Graphics();
sacrificePanelBg.beginFill(0x000000, 0.85);
sacrificePanelBg.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
sacrificePanelBg.endFill();
sacrificePanelBg.eventMode = 'static';
sacrificePanel.addChild(sacrificePanelBg);

const SBOX_W = Math.min(440, Math.max(300, Math.floor(SCREEN_WIDTH * 0.88)));
const SBOX_X = Math.floor((SCREEN_WIDTH - SBOX_W) / 2);
const SBOX_TOP = TOP_OFFSET + 40;
const SBOX_H = Math.min(480, Math.max(300, SCREEN_HEIGHT - SBOX_TOP - 100));

const sacrificeBox = new PIXI.Graphics();
sacrificeBox.beginFill(0x1a1a00, 0.95);
sacrificeBox.drawRoundedRect(SBOX_X, SBOX_TOP, SBOX_W, SBOX_H, 16);
sacrificeBox.endFill();
sacrificeBox.lineStyle(2, 0xf39c12, 0.7);
sacrificeBox.drawRoundedRect(SBOX_X, SBOX_TOP, SBOX_W, SBOX_H, 16);
sacrificePanel.addChild(sacrificeBox);

const sacrificePanelTitle = new PIXI.Text(t('sacrifice_panel_title'), {
    fontFamily: 'Pretendard',
    fontSize: 18,
    fill: 0xf39c12,
    fontWeight: 'bold',
});
sacrificePanelTitle.anchor.set(0.5);
sacrificePanelTitle.position.set(SCREEN_WIDTH / 2, SBOX_TOP + 26);
sacrificePanel.addChild(sacrificePanelTitle);

const sacrificePanelDesc = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: 0xcccccc,
    align: 'center',
});
sacrificePanelDesc.anchor.set(0.5);
sacrificePanelDesc.position.set(SCREEN_WIDTH / 2, SBOX_TOP + 52);
sacrificePanel.addChild(sacrificePanelDesc);

const sacrificeCurrentBonus = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xf4d03f,
    fontWeight: 'bold',
    align: 'center',
});
sacrificeCurrentBonus.anchor.set(0.5);
sacrificeCurrentBonus.position.set(SCREEN_WIDTH / 2, SBOX_TOP + 74);
sacrificePanel.addChild(sacrificeCurrentBonus);

// 제물 리스트 컨테이너
const sacrificeListContainer = new PIXI.Container();
sacrificeListContainer.position.set(SBOX_X + 12, SBOX_TOP + 98);
sacrificePanel.addChild(sacrificeListContainer);

// 광고로 추가 슬롯
const sacrificeAdBtn = new PIXI.Container();
sacrificeAdBtn.position.set(SCREEN_WIDTH / 2, SBOX_TOP + SBOX_H - 64);
sacrificeAdBtn.eventMode = 'static';
sacrificeAdBtn.cursor = 'pointer';
sacrificePanel.addChild(sacrificeAdBtn);

const sacrificeAdBtnBg = new PIXI.Graphics();
sacrificeAdBtnBg.beginFill(0x8e44ad);
sacrificeAdBtnBg.drawRoundedRect(-110, -18, 220, 36, 18);
sacrificeAdBtnBg.endFill();
sacrificeAdBtn.addChild(sacrificeAdBtnBg);

const sacrificeAdBtnText = new PIXI.Text(t('sacrifice_ad_slots'), {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 212,
});
sacrificeAdBtnText.anchor.set(0.5);
sacrificeAdBtn.addChild(sacrificeAdBtnText);

// 닫기 버튼
const sacrificeCloseBtn = new PIXI.Container();
sacrificeCloseBtn.position.set(SCREEN_WIDTH / 2, SBOX_TOP + SBOX_H - 24);
sacrificeCloseBtn.eventMode = 'static';
sacrificeCloseBtn.cursor = 'pointer';
sacrificePanel.addChild(sacrificeCloseBtn);

const sacrificeCloseBg = new PIXI.Graphics();
sacrificeCloseBg.beginFill(0x444444);
sacrificeCloseBg.drawRoundedRect(-60, -14, 120, 28, 14);
sacrificeCloseBg.endFill();
sacrificeCloseBtn.addChild(sacrificeCloseBg);

const sacrificeCloseText = new PIXI.Text(t('close'), {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: 0xaaaaaa,
});
sacrificeCloseText.anchor.set(0.5);
sacrificeCloseBtn.addChild(sacrificeCloseText);

sacrificeCloseBtn.on('pointerdown', () => { sacrificePanel.visible = false; });
sacrificePanelBg.on('pointerdown', () => { sacrificePanel.visible = false; });

function updateSacrificePanel() {
    const targetLevel = weaponLevel - 1;
    const tierPct = Math.round(sacrificeBonusPer(weaponLevel) * 100);
    sacrificePanelDesc.text = t('sacrifice_panel_desc', { level: targetLevel, tierPct });
    const bonus = sacrificeBonus(sacrificeState, weaponLevel);
    const cap = sacrificeCap(sacrificeState);
    sacrificeCurrentBonus.text = t('sacrifice_bonus_label', { pct: (bonus * 100).toFixed(0) }) + '  ' + t('sacrifice_cap_label', { count: sacrificeState.count, cap });

    // 광고 버튼 상태 (제물 수가 절대 상한이면 소진)
    if (sacrificeState.count >= SACRIFICE_ABSOLUTE_CAP) {
        sacrificeAdBtnBg.clear();
        sacrificeAdBtnBg.beginFill(0x444444);
        sacrificeAdBtnBg.drawRoundedRect(-110, -18, 220, 36, 18);
        sacrificeAdBtnBg.endFill();
        sacrificeAdBtnText.text = t('sacrifice_ad_exhausted');
        sacrificeAdBtn.alpha = 0.5;
    } else {
        sacrificeAdBtnBg.clear();
        sacrificeAdBtnBg.beginFill(0x8e44ad);
        sacrificeAdBtnBg.drawRoundedRect(-110, -18, 220, 36, 18);
        sacrificeAdBtnBg.endFill();
        sacrificeAdBtnText.text = t('sacrifice_ad_slots', { pct: tierPct * 3 });
        sacrificeAdBtn.alpha = 1;
    }

    // 리스트 갱신
    sacrificeListContainer.removeChildren();
    const indices = sacrificeTargetIndices(keepInv, weaponLevel);
    const listW = SBOX_W - 24;
    const itemH = 46;
    const maxVisible = Math.floor((SBOX_H - 120) / itemH);

    if (indices.length === 0) {
        const emptyTxt = new PIXI.Text(t('sacrifice_empty', { level: targetLevel }), {
            fontFamily: 'Pretendard',
            fontSize: 13,
            fill: 0x888888,
            align: 'center',
        });
        emptyTxt.anchor.set(0.5);
        emptyTxt.position.set(listW / 2, 30);
        sacrificeListContainer.addChild(emptyTxt);
        return;
    }

    const atCap = sacrificeState.count >= cap;
    indices.slice(0, maxVisible).forEach((keepIdx, i) => {
        const kw = keepInv.weapons[keepIdx];
        const row = new PIXI.Container();
        row.position.set(0, i * itemH);

        const rowBg = new PIXI.Graphics();
        rowBg.beginFill(0x2a2000, 0.8);
        rowBg.drawRoundedRect(0, 0, listW, itemH - 4, 8);
        rowBg.endFill();
        row.addChild(rowBg);

        const weaponEntry = getWeaponByLevel(kw.level);
        const rowTxt = new PIXI.Text(`Lv.${kw.level} ${t(weaponEntry.nameKey)}`, {
            fontFamily: 'Pretendard',
            fontSize: 13,
            fill: 0xf0d060,
            fontWeight: 'bold',
        });
        rowTxt.anchor.set(0, 0.5);
        rowTxt.position.set(12, itemH / 2 - 2);
        row.addChild(rowTxt);

        const offerBtn = new PIXI.Container();
        offerBtn.position.set(listW - 54, itemH / 2 - 2);
        offerBtn.eventMode = atCap ? 'none' : 'static';
        offerBtn.cursor = 'pointer';

        const offerBg = new PIXI.Graphics();
        offerBg.beginFill(atCap ? 0x444444 : 0xb7950b);
        offerBg.drawRoundedRect(-40, -14, 80, 28, 14);
        offerBg.endFill();
        offerBtn.addChild(offerBg);

        const offerTxt = new PIXI.Text(t('sacrifice_offer_btn'), {
            fontFamily: 'Pretendard',
            fontSize: 12,
            fill: 0xffffff,
            fontWeight: 'bold',
        });
        offerTxt.anchor.set(0.5);
        offerBtn.addChild(offerTxt);
        row.addChild(offerBtn);

        const capturedIdx = keepIdx;
        offerBtn.on('pointerdown', () => {
            const result = addSacrifice(sacrificeState, keepInv, capturedIdx);
            if (!result) {
                showResult(t('sacrifice_cap_reached'), COLORS.fail);
                return;
            }
            sacrificeState = result.sacrificeState;
            keepInv = result.keepInv;
            const addedTierPct = Math.round(sacrificeBonusPer(weaponLevel) * 100);
            showResult(t('sacrifice_added', { tierPct: addedTierPct }), 0xf39c12);
            haptic('medium');
            updateSacrificePanel();
            updateSacrificeBonus();
            updateSacrificeBtn();
            updateKeepPanelBtnCount();
            updateUI();
            saveGame();
        });
        sacrificeListContainer.addChild(row);
    });
}

sacrificeAdBtn.on('pointerdown', async () => {
    // applyAdExtra가 null을 반환할 조건은 광고 시청 전에 걸러냄 (광고만 보고 보상 없는 상황 방지)
    if (sacrificeState.adExtra >= SACRIFICE_AD_EXTRA || sacrificeState.count >= SACRIFICE_ABSOLUTE_CAP) {
        showResult(t('sacrifice_ad_exhausted'), COLORS.fail);
        return;
    }
    const adResult = await adService.showRewardedAd();
    if (adResult) {
        const newState = applyAdExtra(sacrificeState);
        if (newState) {
            sacrificeState = newState;
            showResult(t('sacrifice_ad_granted'), 0xf39c12);
            haptic('success');
            updateSacrificePanel();
            updateSacrificeBonus();
            updateSacrificeBtn();
            saveGame();
        }
    }
});

sacrificeBtn.on('pointerdown', () => {
    // 항상 패널을 연다 (Lv.1 등에서도 상태/안내를 보여줌 — 무반응 방지)
    updateSacrificePanel();
    sacrificePanel.visible = true;
    haptic('light');
});

// ─────────────────────────────────────────────────────
// Sprint 3: 인피니티 모드 UI
// ─────────────────────────────────────────────────────

// 인피니티 버튼 (보라/노랑 그라디언트 느낌, 강화버튼 위 좌측)
const infinityBtn = new PIXI.Container();
// protectionBtn 위에 배치 (protectionBtn.y = -55, 추가 -40)
infinityBtn.position.set(0, -95);
infinityBtn.eventMode = 'static';
infinityBtn.cursor = 'pointer';
buttonArea.addChild(infinityBtn);

const infinityBtnBg = new PIXI.Graphics();
infinityBtn.addChild(infinityBtnBg);

const infinityBtnText = new PIXI.Text(t('infinity_btn'), {
    fontFamily: 'Pretendard',
    fontSize: 12,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 132,
});
infinityBtnText.anchor.set(0.5);
infinityBtn.addChild(infinityBtnText);

function getInfinityCooldownRemaining(): number {
    if (infinityLastUsedTs === 0) return 0;
    const elapsed = Date.now() - infinityLastUsedTs;
    return Math.max(0, INFINITY_COOLDOWN_MS - elapsed);
}

function updateInfinityBtn() {
    infinityBtnBg.clear();
    if (infinityActive) {
        const remaining = Math.ceil((infinityEndTime - Date.now()) / 1000);
        infinityBtnBg.beginFill(0x1abc9c);
        infinityBtnBg.drawRoundedRect(-70, -14, 140, 28, 14);
        infinityBtnBg.endFill();
        infinityBtnText.text = t('infinity_active', { sec: Math.max(0, remaining) });
        infinityBtnText.style.fill = 0xffffff;
        infinityBtn.alpha = 1;
    } else {
        const coolRemaining = getInfinityCooldownRemaining();
        if (coolRemaining > 0) {
            const sec = Math.ceil(coolRemaining / 1000);
            const mm = Math.floor(sec / 60);
            const ss = sec % 60;
            const label = mm > 0 ? `${mm}:${String(ss).padStart(2, '0')}` : `${sec}s`;
            infinityBtnBg.beginFill(0x444444);
            infinityBtnBg.drawRoundedRect(-70, -14, 140, 28, 14);
            infinityBtnBg.endFill();
            infinityBtnText.text = t('infinity_cooldown', { sec: label });
            infinityBtnText.style.fill = 0x888888;
            infinityBtn.alpha = 0.4;
        } else {
            infinityBtnBg.beginFill(0x1a237e);
            infinityBtnBg.drawRoundedRect(-70, -14, 140, 28, 14);
            infinityBtnBg.endFill();
            infinityBtnBg.lineStyle(1, 0x7c4dff, 0.8);
            infinityBtnBg.drawRoundedRect(-70, -14, 140, 28, 14);
            infinityBtnText.text = t('infinity_btn');
            infinityBtnText.style.fill = 0xc5cae9;
            infinityBtn.alpha = 0.8;
        }
    }
}
updateInfinityBtn();

// 인피니티 팝업 — boost 팝업과 동일한 카드 레이아웃
const infinityPopup = new PIXI.Container();
infinityPopup.visible = false;
uiContainer.addChild(infinityPopup);

const infinityPopupBg = new PIXI.Graphics();
infinityPopupBg.beginFill(0x000000, 0.85);
infinityPopupBg.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
infinityPopupBg.endFill();
infinityPopupBg.eventMode = 'static';
infinityPopup.addChild(infinityPopupBg);

// 카드 박스 (boost와 동일: -150,-140 / 300x280)
const infinityPopupBox = new PIXI.Graphics();
infinityPopupBox.beginFill(0x1a2a4a);
infinityPopupBox.drawRoundedRect(-150, -140, 300, 280, 20);
infinityPopupBox.endFill();
infinityPopupBox.lineStyle(2, 0x4a6a9a, 1);
infinityPopupBox.drawRoundedRect(-150, -140, 300, 280, 20);
infinityPopupBox.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2);
infinityPopup.addChild(infinityPopupBox);

// 제목 (boost: fontSize 22, fill white, y = center-105)
const infinityPopupTitle = new PIXI.Text(t('infinity_popup_title'), {
    fontFamily: 'Pretendard',
    fontSize: 22,
    fill: 0xffffff,
    fontWeight: 'bold',
});
infinityPopupTitle.anchor.set(0.5);
infinityPopupTitle.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 105);
infinityPopup.addChild(infinityPopupTitle);

// 설명 (boost: fontSize 16, fill 0xcccccc, y = center-65)
const infinityPopupDesc = new PIXI.Text(t('infinity_popup_desc'), {
    fontFamily: 'Pretendard',
    fontSize: 14,
    fill: 0xcccccc,
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 270,
});
infinityPopupDesc.anchor.set(0.5);
infinityPopupDesc.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 55);
infinityPopup.addChild(infinityPopupDesc);

// 쿨타임/상태 표시 (boost의 boostPopupRate 자리 — 크고 강조)
const infinityStatusText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 28,
    fill: 0x7c4dff,
    fontWeight: 'bold',
});
infinityStatusText.anchor.set(0.5);
infinityStatusText.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 5);
infinityPopup.addChild(infinityStatusText);

// 쿨타임 안내 소문자 (boost의 boostRemainingText 자리)
const infinityCooldownLabel = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: COLORS.gold,
});
infinityCooldownLabel.anchor.set(0.5);
infinityCooldownLabel.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 30);
infinityPopup.addChild(infinityCooldownLabel);

// 광고 보고 시작 버튼 (boost와 동일: 보라 0x9b59b6, y = center+65)
const infinityConfirmBtn = new PIXI.Container();
infinityConfirmBtn.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 65);
infinityConfirmBtn.eventMode = 'static';
infinityConfirmBtn.cursor = 'pointer';
infinityPopup.addChild(infinityConfirmBtn);

const infinityConfirmBg = new PIXI.Graphics();
infinityConfirmBg.beginFill(0x9b59b6);
infinityConfirmBg.drawRoundedRect(-100, -22, 200, 44, 22);
infinityConfirmBg.endFill();
infinityConfirmBtn.addChild(infinityConfirmBg);

const infinityConfirmText = new PIXI.Text(t('infinity_popup_confirm'), {
    fontFamily: 'Pretendard',
    fontSize: 15,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 190,
});
infinityConfirmText.anchor.set(0.5);
infinityConfirmBtn.addChild(infinityConfirmText);

// 닫기 버튼 (boost와 동일: 0x3a3a5a, y = center+115)
const infinityClosePopupBtn = new PIXI.Container();
infinityClosePopupBtn.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 115);
infinityClosePopupBtn.eventMode = 'static';
infinityClosePopupBtn.cursor = 'pointer';
infinityPopup.addChild(infinityClosePopupBtn);

const infinityClosePopupBg = new PIXI.Graphics();
infinityClosePopupBg.beginFill(0x3a3a5a);
infinityClosePopupBg.drawRoundedRect(-60, -18, 120, 36, 18);
infinityClosePopupBg.endFill();
infinityClosePopupBtn.addChild(infinityClosePopupBg);

const infinityClosePopupText = new PIXI.Text(t('infinity_popup_close'), {
    fontFamily: 'Pretendard',
    fontSize: 15,
    fill: 0xaaaaaa,
});
infinityClosePopupText.anchor.set(0.5);
infinityClosePopupBtn.addChild(infinityClosePopupText);

// 팝업 열 때 상태 갱신
function showInfinityPopup() {
    const coolRemaining = getInfinityCooldownRemaining();
    if (coolRemaining > 0) {
        const sec = Math.ceil(coolRemaining / 1000);
        const mm = Math.floor(sec / 60);
        const ss = sec % 60;
        const label = mm > 0 ? `${mm}:${String(ss).padStart(2, '0')}` : `${sec}s`;
        infinityStatusText.text = label;
        infinityStatusText.style.fill = 0x888888;
        infinityCooldownLabel.text = t('infinity_cooldown', { sec: label });
        infinityConfirmBtn.alpha = 0.4;
        infinityConfirmBtn.eventMode = 'none';
    } else {
        infinityStatusText.text = `${INFINITY_DURATION_MS / 1000}s FREE`;
        infinityStatusText.style.fill = 0x7c4dff;
        infinityCooldownLabel.text = '';
        infinityConfirmBtn.alpha = 1;
        infinityConfirmBtn.eventMode = 'static';
    }
    infinityPopup.visible = true;
}

infinityClosePopupBtn.on('pointerdown', () => { infinityPopup.visible = false; });
infinityPopupBg.on('pointerdown', () => { infinityPopup.visible = false; });

infinityConfirmBtn.on('pointerdown', async () => {
    if (getInfinityCooldownRemaining() > 0) return;
    infinityPopup.visible = false;
    const adResult = await adService.showRewardedAd();
    if (adResult) {
        infinityActive = true;
        infinityEndTime = Date.now() + INFINITY_DURATION_MS;
        infinityLastUsedTs = Date.now();
        haptic('success');
        showResult(t('infinity_started'), 0x7c4dff);
        updateInfinityBtn();
        updateUI();
        saveGame();
        // 타이머: INFINITY_DURATION_MS 후 종료
        setTimeout(() => {
            infinityActive = false;
            infinityEndTime = 0;
            showResult(t('infinity_ended'), 0x9999cc);
            updateInfinityBtn();
            updateUI();
            saveGame();
        }, INFINITY_DURATION_MS);
    }
});

infinityBtn.on('pointerdown', () => {
    if (infinityActive) return; // 이미 활성
    showInfinityPopup();
});

// 인피니티 타이머 표시 (500ms마다 업데이트 — 활성 카운트다운 + 쿨타임 카운트다운)
let _infinityCooldownWasActive = false;
setInterval(() => {
    if (infinityActive) {
        if (Date.now() >= infinityEndTime) {
            infinityActive = false;
            infinityEndTime = 0;
        }
        updateInfinityBtn();
        updateUI();
    } else if (getInfinityCooldownRemaining() > 0) {
        _infinityCooldownWasActive = true;
        updateInfinityBtn();
    } else if (_infinityCooldownWasActive) {
        // 쿨다운이 0에 도달한 직후 한 번 더 갱신 — 안 하면 "Ready in 1s" 회색 표시로 영구 고착
        _infinityCooldownWasActive = false;
        updateInfinityBtn();
    }
}, 500);

// ─────────────────────────────────────────────────────
// Sprint 3: 버프 아이템 슬롯 UI
// ─────────────────────────────────────────────────────

// 버프 슬롯: weaponArea 아래 (무기 프레임 하단 ~ 강화버튼 사이)
// weaponArea.y = SCREEN_HEIGHT/2 - 40 = 880/2 - 40 = 400
// weaponArea 기준: 프레임 하단 = 155 (weaponFrame 아래 끝), sacrificeBonusText.y = 158
// 버프슬롯은 weaponArea 기준 y = 175 (프레임 아래 여유)
const BUFF_SLOT_ITEMS: Array<{ key: BuffItemKey; icon: string; nameKey: string }> = [
    { key: 'luck_charm', icon: './items/item_luck_charm.png', nameKey: 'buff_item_luck_charm' },
    { key: 'guarantee', icon: './items/item_guarantee_ticket.png', nameKey: 'buff_item_guarantee' },
    { key: 'destroy_guard', icon: './items/item_destroy_guard.png', nameKey: 'buff_item_destroy_guard' },
];
const BUFF_SLOT_W = 52;
const BUFF_SLOT_GAP = 8;
const buffSlotTotalW = BUFF_SLOT_ITEMS.length * (BUFF_SLOT_W + BUFF_SLOT_GAP) - BUFF_SLOT_GAP;
const BUFF_SLOT_START_X = -Math.floor(buffSlotTotalW / 2);

const buffSlotsContainer = new PIXI.Container();
// 무기 프레임 하단(weaponArea.y + 155) 바로 아래 + 여유 5px
// weaponArea.y = SCREEN_HEIGHT/2 - 40
const BUFF_Y_ABS = Math.floor(SCREEN_HEIGHT / 2) - 40 + 160;
buffSlotsContainer.position.set(SCREEN_WIDTH / 2, BUFF_Y_ABS);
uiContainer.addChild(buffSlotsContainer);

// 각 슬롯 데이터
const buffSlotBgs: PIXI.Graphics[] = [];
const buffSlotCounts: PIXI.Text[] = [];
const buffSlotActiveMarks: PIXI.Graphics[] = [];
const buffSlotSprites: (PIXI.Sprite | null)[] = [null, null, null];

BUFF_SLOT_ITEMS.forEach((item, i) => {
    const slotX = BUFF_SLOT_START_X + i * (BUFF_SLOT_W + BUFF_SLOT_GAP);

    const slotBg = new PIXI.Graphics();
    slotBg.beginFill(0x1a1a2e, 0.85);
    slotBg.drawRoundedRect(0, 0, BUFF_SLOT_W, BUFF_SLOT_W, 10);
    slotBg.endFill();
    slotBg.position.set(slotX, 0);
    buffSlotsContainer.addChild(slotBg);
    buffSlotBgs.push(slotBg);

    // 아이콘 이미지 로드
    PIXI.Assets.load(item.icon).then((texture: PIXI.Texture) => {
        const sprite = new PIXI.Sprite(texture);
        sprite.anchor.set(0.5);
        sprite.position.set(slotX + BUFF_SLOT_W / 2, BUFF_SLOT_W / 2);
        const scale = Math.min((BUFF_SLOT_W - 12) / texture.width, (BUFF_SLOT_W - 12) / texture.height);
        sprite.scale.set(scale);
        buffSlotsContainer.addChild(sprite);
        buffSlotSprites[i] = sprite;
        updateBuffSlots();
    }).catch(() => {
        // 이미지 로드 실패 시 텍스트 대체
        const fallback = new PIXI.Text(item.key.slice(0, 2).toUpperCase(), {
            fontFamily: 'Pretendard',
            fontSize: 12,
            fill: 0x888888,
        });
        fallback.anchor.set(0.5);
        fallback.position.set(slotX + BUFF_SLOT_W / 2, BUFF_SLOT_W / 2);
        buffSlotsContainer.addChild(fallback);
    });

    // 개수 텍스트
    const countText = new PIXI.Text('0', {
        fontFamily: 'Pretendard',
        fontSize: 11,
        fill: 0xffffff,
        fontWeight: 'bold',
    });
    countText.anchor.set(1, 1);
    countText.position.set(slotX + BUFF_SLOT_W - 3, BUFF_SLOT_W - 2);
    buffSlotsContainer.addChild(countText);
    buffSlotCounts.push(countText);

    // 활성 마크
    const activeMark = new PIXI.Graphics();
    activeMark.lineStyle(2, 0x2ecc71, 1);
    activeMark.drawRoundedRect(0, 0, BUFF_SLOT_W, BUFF_SLOT_W, 10);
    activeMark.position.set(slotX, 0);
    activeMark.visible = false;
    buffSlotsContainer.addChild(activeMark);
    buffSlotActiveMarks.push(activeMark);

    // 클릭 이벤트
    const hitArea = new PIXI.Graphics();
    hitArea.beginFill(0xffffff, 0.001);
    hitArea.drawRect(0, 0, BUFF_SLOT_W, BUFF_SLOT_W);
    hitArea.endFill();
    hitArea.position.set(slotX, 0);
    hitArea.eventMode = 'static';
    hitArea.cursor = 'pointer';
    buffSlotsContainer.addChild(hitArea);

    const capturedKey = item.key;
    hitArea.on('pointerdown', () => {
        showBuffItemPopup(capturedKey);
    });
});

// ─────────────────────────────────────────────────────
// 버프 아이템 설명 팝업
// ─────────────────────────────────────────────────────
const buffItemPopup = new PIXI.Container();
buffItemPopup.visible = false;
uiContainer.addChild(buffItemPopup);

const buffItemPopupBg = new PIXI.Graphics();
buffItemPopupBg.beginFill(0x000000, 0.85);
buffItemPopupBg.drawRect(0, 0, SCREEN_WIDTH, SCREEN_HEIGHT);
buffItemPopupBg.endFill();
buffItemPopupBg.eventMode = 'static';
buffItemPopup.addChild(buffItemPopupBg);

// 카드 박스 (boost/infinity와 동일 스타일, 높이 약간 큼 300px)
const buffItemPopupBox = new PIXI.Graphics();
buffItemPopupBox.beginFill(0x1a2a4a);
buffItemPopupBox.drawRoundedRect(-150, -155, 300, 310, 20);
buffItemPopupBox.endFill();
buffItemPopupBox.lineStyle(2, 0x4a6a9a, 1);
buffItemPopupBox.drawRoundedRect(-150, -155, 300, 310, 20);
buffItemPopupBox.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2);
buffItemPopup.addChild(buffItemPopupBox);

const buffItemPopupTitle = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 22,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
});
buffItemPopupTitle.anchor.set(0.5);
buffItemPopupTitle.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 120);
buffItemPopup.addChild(buffItemPopupTitle);

const buffItemPopupEffect = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 15,
    fill: 0xaaddff,
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 260,
});
buffItemPopupEffect.anchor.set(0.5);
buffItemPopupEffect.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 65);
buffItemPopup.addChild(buffItemPopupEffect);

const buffItemPopupCount = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 18,
    fill: COLORS.gold,
    fontWeight: 'bold',
});
buffItemPopupCount.anchor.set(0.5);
buffItemPopupCount.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 - 10);
buffItemPopup.addChild(buffItemPopupCount);

const buffItemPopupActiveLabel = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 13,
    fill: 0x2ecc71,
    fontWeight: 'bold',
});
buffItemPopupActiveLabel.anchor.set(0.5);
buffItemPopupActiveLabel.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 20);
buffItemPopup.addChild(buffItemPopupActiveLabel);

// 사용 버튼
const buffItemUseBtn = new PIXI.Container();
buffItemUseBtn.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 60);
buffItemUseBtn.eventMode = 'static';
buffItemUseBtn.cursor = 'pointer';
buffItemPopup.addChild(buffItemUseBtn);

const buffItemUseBtnBg = new PIXI.Graphics();
buffItemUseBtnBg.beginFill(0x27ae60);
buffItemUseBtnBg.drawRoundedRect(-100, -22, 200, 44, 22);
buffItemUseBtnBg.endFill();
buffItemUseBtn.addChild(buffItemUseBtnBg);

const buffItemUseBtnText = new PIXI.Text('', {
    fontFamily: 'Pretendard',
    fontSize: 15,
    fill: 0xffffff,
    fontWeight: 'bold',
    align: 'center',
    wordWrap: true,
    wordWrapWidth: 190,
});
buffItemUseBtnText.anchor.set(0.5);
buffItemUseBtn.addChild(buffItemUseBtnText);

// 닫기 버튼
const buffItemCloseBtn = new PIXI.Container();
buffItemCloseBtn.position.set(SCREEN_WIDTH / 2, SCREEN_HEIGHT / 2 + 115);
buffItemCloseBtn.eventMode = 'static';
buffItemCloseBtn.cursor = 'pointer';
buffItemPopup.addChild(buffItemCloseBtn);

const buffItemCloseBtnBg = new PIXI.Graphics();
buffItemCloseBtnBg.beginFill(0x3a3a5a);
buffItemCloseBtnBg.drawRoundedRect(-60, -18, 120, 36, 18);
buffItemCloseBtnBg.endFill();
buffItemCloseBtn.addChild(buffItemCloseBtnBg);

const buffItemCloseBtnText = new PIXI.Text(t('close'), {
    fontFamily: 'Pretendard',
    fontSize: 15,
    fill: 0xaaaaaa,
});
buffItemCloseBtnText.anchor.set(0.5);
buffItemCloseBtn.addChild(buffItemCloseBtnText);

buffItemCloseBtn.on('pointerdown', () => {
    buffItemPopup.visible = false;
});

let _buffPopupCurrentKey: BuffItemKey | null = null;

function showBuffItemPopup(key: BuffItemKey) {
    _buffPopupCurrentKey = key;
    const nameMap: Record<BuffItemKey, string> = {
        luck_charm: t('buff_item_luck_charm'),
        guarantee: t('buff_item_guarantee'),
        destroy_guard: t('buff_item_destroy_guard'),
    };
    const effectMap: Record<BuffItemKey, string> = {
        luck_charm: t('buff_popup_luck_charm_effect'),
        guarantee: t('buff_popup_guarantee_effect'),
        destroy_guard: t('buff_popup_destroy_guard_effect'),
    };
    const isActiveMap: Record<BuffItemKey, boolean> = {
        luck_charm: buffItems.luckCharmActive,
        guarantee: buffItems.guaranteeActive,
        destroy_guard: buffItems.destroyGuardActive,
    };
    const countMap: Record<BuffItemKey, number> = {
        luck_charm: buffItems.luck_charm,
        guarantee: buffItems.guarantee,
        destroy_guard: buffItems.destroy_guard,
    };

    const count = countMap[key];
    const isActive = isActiveMap[key];

    buffItemPopupTitle.text = nameMap[key];
    buffItemPopupEffect.text = effectMap[key];
    buffItemPopupCount.text = t('buff_popup_count', { count: String(count) });

    if (isActive) {
        buffItemPopupActiveLabel.text = t('buff_item_active') + ' !';
        buffItemPopupActiveLabel.visible = true;
    } else {
        buffItemPopupActiveLabel.visible = false;
    }

    if (count > 0 && !isActive) {
        // 사용 가능
        buffItemUseBtnBg.clear();
        buffItemUseBtnBg.beginFill(0x27ae60);
        buffItemUseBtnBg.drawRoundedRect(-100, -22, 200, 44, 22);
        buffItemUseBtnBg.endFill();
        buffItemUseBtnText.text = t('buff_popup_use_btn');
        buffItemUseBtnText.style.fill = 0xffffff;
        buffItemUseBtn.eventMode = 'static';
        buffItemUseBtn.cursor = 'pointer';
        buffItemUseBtn.visible = true;
    } else if (count <= 0) {
        // 보유 없음
        buffItemPopupCount.text = t('buff_popup_no_stock');
        buffItemPopupCount.style.fill = 0x888888;
        buffItemUseBtnBg.clear();
        buffItemUseBtnBg.beginFill(0x333344);
        buffItemUseBtnBg.drawRoundedRect(-100, -22, 200, 44, 22);
        buffItemUseBtnBg.endFill();
        buffItemUseBtnText.text = t('buff_item_use');
        buffItemUseBtnText.style.fill = 0x666666;
        buffItemUseBtn.eventMode = 'none';
        buffItemUseBtn.cursor = 'default';
        buffItemUseBtn.visible = true;
    } else {
        // 이미 활성화됨 — 사용 버튼 숨김
        buffItemUseBtn.visible = false;
    }
    // count color 복원 (보유 있을 때)
    if (count > 0) {
        buffItemPopupCount.style.fill = COLORS.gold;
    }

    buffItemPopup.visible = true;
}

buffItemUseBtn.on('pointerdown', () => {
    if (_buffPopupCurrentKey) {
        buffItemPopup.visible = false;
        handleBuffItemUse(_buffPopupCurrentKey);
    }
});

function handleBuffItemUse(key: BuffItemKey) {
    let newState: BuffItemsState | null = null;
    let msgKey: string = '';
    let msgColor = 0x2ecc71;

    if (key === 'luck_charm') {
        if (buffItems.luckCharmActive) {
            showResult(t('buff_item_already_active'), COLORS.fail);
            return;
        }
        newState = useLuckCharm(buffItems);
        msgKey = 'buff_luck_charm_activated';
        msgColor = 0xf4d03f;
    } else if (key === 'guarantee') {
        if (buffItems.guaranteeActive) {
            showResult(t('buff_item_already_active'), COLORS.fail);
            return;
        }
        newState = useGuarantee(buffItems);
        msgKey = 'buff_guarantee_activated';
        msgColor = 0x2ecc71;
    } else if (key === 'destroy_guard') {
        if (buffItems.destroyGuardActive) {
            showResult(t('buff_item_already_active'), COLORS.fail);
            return;
        }
        newState = useDestroyGuard(buffItems);
        msgKey = 'buff_destroy_guard_activated';
        msgColor = 0x4ecdc4;
    }

    if (!newState) {
        const nameMap: Record<BuffItemKey, string> = {
            luck_charm: t('buff_item_luck_charm'),
            guarantee: t('buff_item_guarantee'),
            destroy_guard: t('buff_item_destroy_guard'),
        };
        showResult(t('buff_item_none', { name: nameMap[key] }), COLORS.fail);
        return;
    }

    buffItems = newState;
    if (msgKey) showResult(t(msgKey as Parameters<typeof t>[0]), msgColor);
    haptic('medium');
    updateBuffSlots();
    updateUI();
    saveGame();
}

function updateBuffSlots() {
    const stateMap = [
        { count: buffItems.luck_charm, active: buffItems.luckCharmActive },
        { count: buffItems.guarantee, active: buffItems.guaranteeActive },
        { count: buffItems.destroy_guard, active: buffItems.destroyGuardActive },
    ];
    stateMap.forEach((s, i) => {
        const bg = buffSlotBgs[i];
        bg.clear();
        bg.lineStyle(0);
        if (s.count > 0) {
            bg.beginFill(0x1e2a4a, 0.9);
        } else {
            bg.beginFill(0x111111, 0.6);
        }
        bg.drawRoundedRect(0, 0, BUFF_SLOT_W, BUFF_SLOT_W, 10);
        bg.endFill();

        buffSlotCounts[i].text = s.count > 0 ? `${s.count}` : '0';
        buffSlotCounts[i].style.fill = s.count > 0 ? 0xffffff : 0x555555;

        buffSlotActiveMarks[i].visible = s.active;

        // 스프라이트 투명도
        const sprite = buffSlotSprites[i];
        if (sprite) {
            sprite.alpha = s.count > 0 ? 1 : 0.3;
        }
    });
}
updateBuffSlots();

// ─────────────────────────────────────────────────────
// Sprint 3: 용광로 아이템 탭 등록
// (forge UI는 기존 forgePanel 내 탭에 'item' 카테고리 추가)
// ─────────────────────────────────────────────────────
// 기존 forgeSubTabs 배열에 'item' 추가는 forge.ts ForgeCategory에서 처리됨.
// forgePanel의 탭 목록과 renderForgeTab 함수가 카테고리별로 레시피를 필터링하므로
// 새 레시피가 자동으로 나타남. 단, 탭 버튼 라벨 추가가 필요하다.
// ─────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────
// z-order 정리: 모든 팝업/패널을 최상단으로 올린다.
// (버프 아이템 슬롯/상단 배지/제물 보너스 텍스트 등 메인 상시 UI가
//  나중에 addChild 되어 팝업 위로 새던 문제 해결 — 팝업이 항상 덮도록.)
// ─────────────────────────────────────────────────────
[
    goldAdPopup, menuPopup, resetConfirmPopup, protectionPopup, boostPopup,
    shopPanel, forgeCraftPopup, forgeEquipChoicePopup, shopTutorialPopup,
    adRestorePanel, completionPopup, matPanel, destroyedPopup, keepPanel,
    sacrificePanel, infinityPopup, buffItemPopup,
].forEach((p) => uiContainer.setChildIndex(p, uiContainer.children.length - 1));

// Game Ready
console.log(t('game_started'));
